import React from 'react';
import { Alert, Button, Card, Checkbox, Input, InputNumber, Modal, Pagination, Select, Switch, Table, Tabs, Tag, Tooltip, message } from 'antd';
import { CopyOutlined, SwapLeftOutlined } from '@ant-design/icons';
import request from '../../../../utils/request';
import { confirmInventoryUnitWeightDimensions, getInventoryUnitDetail, getInventoryUnits } from '../../../../services/inventory';
import { isLocalSpeechSupported, LocalSpeechLang, speakTextLocal } from '../../../../utils/localSpeech';
import StorageLabelPreview from '../../../admin-staff/storage/labels/components/StorageLabelPreview';
import { storageContainerAdapter } from '../../../admin-staff/storage/labels/adapters/containerAdapter';
import { storageLabelService } from '../../../admin-staff/storage/labels/services/storageLabelService';
import { handleLabelError } from '../../../admin-staff/storage/labels/utils/labelUtils';
import type { StorageContainerLabelData } from '../../../admin-staff/storage/labels/types/storageLabel';

type Props = {
  step: any;
  data: any;
  metrics: Record<string, any>;
  instructionDetail?: any;
  onChange: (key: string, val: any) => void;
  onSubmit: () => Promise<void> | void;
  instructionCodeFromParent?: string;
  canOperate?: boolean;
  stageNo?: number | null;
};

const SortAndCountStepRenderer: React.FC<Props> = ({
  step,
  data,
  metrics,
  instructionDetail,
  onChange,
  instructionCodeFromParent,
  canOperate,
}) => {
  const [activeInfoTab, setActiveInfoTab] = React.useState<string>('db-location');
  const [attachmentsLoading, setAttachmentsLoading] = React.useState<boolean>(false);
  const [backendAttachments, setBackendAttachments] = React.useState<any[]>([]);
  const [baselineLoading, setBaselineLoading] = React.useState<boolean>(false);
  const [baselineRows, setBaselineRows] = React.useState<any[]>([]);
  const [attachmentsPage, setAttachmentsPage] = React.useState<number>(1);
  const [attachmentsPageSize, setAttachmentsPageSize] = React.useState<number>(10);
  const [groupListPreviewLoading, setGroupListPreviewLoading] = React.useState<boolean>(false);
  const [groupListPreviewError, setGroupListPreviewError] = React.useState<string>('');
  const [groupListPreviewCols, setGroupListPreviewCols] = React.useState<any[]>([]);
  const [groupListPreviewRows, setGroupListPreviewRows] = React.useState<any[]>([]);
  const [groupListSearchKeyword, setGroupListSearchKeyword] = React.useState<string>('');
  const [groupListTablePage, setGroupListTablePage] = React.useState<number>(1);
  const [groupListTablePageSize, setGroupListTablePageSize] = React.useState<number>(10);
  const [sortInputValue, setSortInputValue] = React.useState<string>('');
  const [sortInputQty, setSortInputQty] = React.useState<number>(1);
  const [sortDbRows, setSortDbRows] = React.useState<Array<{
    id: string;
    location_no: number;
    group_code: string;
    sort_id: number;
    sorting_mark: string;
    preset_group_no: string;
    inventory_unit_id?: number;
    target_inventory_unit_id?: number;
    inventory_unit_code?: string;
    target_code?: string;
  }>>([]);
  const [operationRows, setOperationRows] = React.useState<Array<{
    id: string;
    seq: number;
    location_no: number | string;
    group_code: string;
    sort_id: number | string;
    qty: number;
    sorting_mark: string;
    preset_group_no: string;
    time_text: string;
  }>>([]);
  const [boxedQtyByLocationSort, setBoxedQtyByLocationSort] = React.useState<Record<string, number>>({});
  const [currentInputMark, setCurrentInputMark] = React.useState<string>('');
  const [currentSortIdDisplay, setCurrentSortIdDisplay] = React.useState<string>('-');
  const [currentSortQtyDisplay, setCurrentSortQtyDisplay] = React.useState<number>(1);
  const [currentLocationDisplay, setCurrentLocationDisplay] = React.useState<string>('-');
  const [sortDbMarkFilter, setSortDbMarkFilter] = React.useState<string>('');
  const [sortDbLocationFilter, setSortDbLocationFilter] = React.useState<string>('');
  const [sortDbPage, setSortDbPage] = React.useState<number>(1);
  const [sortDbPageSize, setSortDbPageSize] = React.useState<number>(10);
  const [operationPage, setOperationPage] = React.useState<number>(1);
  const [operationPageSize, setOperationPageSize] = React.useState<number>(10);
  const [flowGuideOpen, setFlowGuideOpen] = React.useState<boolean>(false);
  const [editQtyModalOpen, setEditQtyModalOpen] = React.useState<boolean>(false);
  const [editQtyRowId, setEditQtyRowId] = React.useState<string>('');
  const [editQtyValue, setEditQtyValue] = React.useState<number>(1);
  const [tempBoxModalOpen, setTempBoxModalOpen] = React.useState<boolean>(false);
  const [tempBoxLocationNo, setTempBoxLocationNo] = React.useState<string>('');
  const [tempBoxSelectionMap, setTempBoxSelectionMap] = React.useState<Record<string, { checked: boolean; qty: number }>>({});
  const [tempBoxPage, setTempBoxPage] = React.useState<number>(1);
  const [tempBoxPageSize, setTempBoxPageSize] = React.useState<number>(10);
  const [tempBoxCreateContainerOpen, setTempBoxCreateContainerOpen] = React.useState<boolean>(false);
  const [tempBoxAddExistingOpen, setTempBoxAddExistingOpen] = React.useState<boolean>(false);
  const [tempBoxAddExistingLoading, setTempBoxAddExistingLoading] = React.useState<boolean>(false);
  const [tempBoxAddExistingSubmitting, setTempBoxAddExistingSubmitting] = React.useState<boolean>(false);
  const [tempBoxAddExistingContainers, setTempBoxAddExistingContainers] = React.useState<any[]>([]);
  const [tempBoxAddExistingSelectedContainerId, setTempBoxAddExistingSelectedContainerId] = React.useState<number | null>(null);
  const [tempBoxAddExistingSearch, setTempBoxAddExistingSearch] = React.useState<string>('');
  const [tempBoxContainerTypeChoices, setTempBoxContainerTypeChoices] = React.useState<Array<{ value: string; label: string }>>([]);
  const [tempBoxContainerTypeLoading, setTempBoxContainerTypeLoading] = React.useState<boolean>(false);
  const [tempBoxContainerType, setTempBoxContainerType] = React.useState<string>('');
  const [tempBoxContainerRemarks, setTempBoxContainerRemarks] = React.useState<string>('');
  const [tempBoxCreateSubmitting, setTempBoxCreateSubmitting] = React.useState<boolean>(false);
  const [tempBoxManualMatchOpen, setTempBoxManualMatchOpen] = React.useState<boolean>(false);
  const [tempBoxManualMatchRows, setTempBoxManualMatchRows] = React.useState<any[]>([]);
  const [tempBoxManualMatchSelection, setTempBoxManualMatchSelection] = React.useState<Record<string, number | null>>({});
  const [tempBoxPendingPayload, setTempBoxPendingPayload] = React.useState<any | null>(null);
  const [tempBoxPendingAction, setTempBoxPendingAction] = React.useState<'create' | 'add-existing'>('create');
  const [need2WeightConfirmOpen, setNeed2WeightConfirmOpen] = React.useState<boolean>(false);
  const [need2UnconfirmedUnits, setNeed2UnconfirmedUnits] = React.useState<any[]>([]);
  const [need2CurrentUnitIdx, setNeed2CurrentUnitIdx] = React.useState<number>(0);
  const [need2WeightForm, setNeed2WeightForm] = React.useState<{
    actual_weight: number;
    actual_length: number;
    actual_width: number;
    actual_height: number;
    logistics_package: boolean;
  }>({
    actual_weight: 0,
    actual_length: 0,
    actual_width: 0,
    actual_height: 0,
    logistics_package: false,
  });
  const [need2WeightConfirmSubmitting, setNeed2WeightConfirmSubmitting] = React.useState<boolean>(false);
  const [need2PendingAction, setNeed2PendingAction] = React.useState<'create' | 'add-existing' | ''>('');
  const [need2PendingPayload, setNeed2PendingPayload] = React.useState<any | null>(null);
  const [need2PendingContainerId, setNeed2PendingContainerId] = React.useState<number>(0);
  const [need2PrecheckOpen, setNeed2PrecheckOpen] = React.useState<boolean>(false);
  const [need2PrecheckMisses, setNeed2PrecheckMisses] = React.useState<any[]>([]);
  const [need2PrecheckUnconfirmedUnits, setNeed2PrecheckUnconfirmedUnits] = React.useState<any[]>([]);
  const [need2PrecheckMatchedCount, setNeed2PrecheckMatchedCount] = React.useState<number>(0);
  const [need2PrecheckDetailText, setNeed2PrecheckDetailText] = React.useState<string>('');
  const [need2PrecheckAction, setNeed2PrecheckAction] = React.useState<'create' | 'add-existing' | ''>('');
  const [need2PrecheckPayload, setNeed2PrecheckPayload] = React.useState<any | null>(null);
  const [need2PrecheckContainerId, setNeed2PrecheckContainerId] = React.useState<number>(0);
  const [need2ResolvedMatches, setNeed2ResolvedMatches] = React.useState<Record<string, number>>({});
  const [need2MissingCreateOpen, setNeed2MissingCreateOpen] = React.useState<boolean>(false);
  const [need2MissingCreateRows, setNeed2MissingCreateRows] = React.useState<any[]>([]);
  const [need2MissingCreateIdx, setNeed2MissingCreateIdx] = React.useState<number>(0);
  const [need2MissingCreateLoading, setNeed2MissingCreateLoading] = React.useState<boolean>(false);
  const [need2MissingCreateSubmitting, setNeed2MissingCreateSubmitting] = React.useState<boolean>(false);
  const [need2MissingCreateContext, setNeed2MissingCreateContext] = React.useState<any | null>(null);
  const [need2MissingCreateForm, setNeed2MissingCreateForm] = React.useState<{
    weight_g: number;
    length_cm: number;
    width_cm: number;
    height_cm: number;
    logistics_package: boolean;
  }>({
    weight_g: 0,
    length_cm: 0,
    width_cm: 0,
    height_cm: 0,
    logistics_package: false,
  });
  const [need1BiuMatchPickerOpen, setNeed1BiuMatchPickerOpen] = React.useState<boolean>(false);
  const [need1BiuMatchPickerCandidates, setNeed1BiuMatchPickerCandidates] = React.useState<any[]>([]);
  const [need1BiuMatchPickerSelectedCode, setNeed1BiuMatchPickerSelectedCode] = React.useState<string>('');
  const [need1BiuMatchPendingScanText, setNeed1BiuMatchPendingScanText] = React.useState<string>('');
  const [need1BiuMatchPendingQty, setNeed1BiuMatchPendingQty] = React.useState<number>(1);
  const [generatedDraftContainersLoading, setGeneratedDraftContainersLoading] = React.useState<boolean>(false);
  const [generatedDraftContainers, setGeneratedDraftContainers] = React.useState<any[]>([]);
  const [generatedDraftContainersPage, setGeneratedDraftContainersPage] = React.useState<number>(1);
  const [generatedDraftContainersPageSize, setGeneratedDraftContainersPageSize] = React.useState<number>(10);
  const [labelPreviewVisible, setLabelPreviewVisible] = React.useState<boolean>(false);
  const [labelPreviewData, setLabelPreviewData] = React.useState<StorageContainerLabelData | undefined>(undefined);
  const [operationTargetUnitContentCache, setOperationTargetUnitContentCache] = React.useState<Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>>>({});
  const [operationTargetUnitSortingGroupCache, setOperationTargetUnitSortingGroupCache] = React.useState<Record<string, { code: string; name: string }>>({});
  const [statsMarkFilter, setStatsMarkFilter] = React.useState<string>('');
  const [statsMarkPage, setStatsMarkPage] = React.useState<number>(1);
  const [statsMarkPageSize, setStatsMarkPageSize] = React.useState<number>(10);
  const [statsLocationPage, setStatsLocationPage] = React.useState<number>(1);
  const [statsLocationPageSize, setStatsLocationPageSize] = React.useState<number>(10);
  const adaptiveLayoutHeight = React.useMemo(() => {
    const maxPageSize = Math.max(
      Number(sortDbPageSize || 10),
      Number(operationPageSize || 10),
      Number(statsMarkPageSize || 10),
      Number(statsLocationPageSize || 10),
      Number(attachmentsPageSize || 10),
    );
    const baseHeight = 760;
    const extra = Math.max(0, maxPageSize - 10) * 16;
    return Math.min(1600, baseHeight + extra);
  }, [sortDbPageSize, operationPageSize, statsMarkPageSize, statsLocationPageSize, attachmentsPageSize]);
  const getResizableListHeight = React.useCallback((pageSize: number, base: number) => {
    const safe = Math.max(5, Number(pageSize || 10));
    const next = base + Math.max(0, safe - 10) * 14;
    return Math.min(980, Math.max(220, next));
  }, []);
  const sortInputRef = React.useRef<any>(null);
  const formatNowText = React.useCallback(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }, []);
  const [groupListUploadLoading, setGroupListUploadLoading] = React.useState<boolean>(false);
  const [groupListDeleteLoading, setGroupListDeleteLoading] = React.useState<boolean>(false);
  const [groupListSaveLoading, setGroupListSaveLoading] = React.useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = React.useState<boolean>(false);
  const [previewLoading, setPreviewLoading] = React.useState<boolean>(false);
  const [previewError, setPreviewError] = React.useState<string>('');
  const [previewTitle, setPreviewTitle] = React.useState<string>('附件预览');
  const [previewUrl, setPreviewUrl] = React.useState<string>('');
  const [previewType, setPreviewType] = React.useState<'csv' | 'xlsx' | 'pdf' | 'image' | 'text' | 'video' | 'other'>('other');
  const [previewCols, setPreviewCols] = React.useState<any[]>([]);
  const [previewData, setPreviewData] = React.useState<any[]>([]);
  const [previewPageSize, setPreviewPageSize] = React.useState<number>(20);
  const [previewText, setPreviewText] = React.useState<string>('');
  const [previewSheetNames, setPreviewSheetNames] = React.useState<string[]>([]);
  const [previewActiveSheet, setPreviewActiveSheet] = React.useState<string>('');
  const [previewContext, setPreviewContext] = React.useState<'attachment' | 'group-list-baseline'>('attachment');
  const [previewDirty, setPreviewDirty] = React.useState<boolean>(false);
  const [previewEditingCell, setPreviewEditingCell] = React.useState<{ rowIdx: number; dataIndex: string } | null>(null);
  const [previewSourceFileName, setPreviewSourceFileName] = React.useState<string>('');
  const [previewBaselineFile, setPreviewBaselineFile] = React.useState<any>(null);
  const previewXlsxWbRef = React.useRef<any>(null);
  const previewXlsxLibRef = React.useRef<any>(null);
  const previewXlsxCacheRef = React.useRef<Record<string, { cols: any[]; data: any[] }>>({});
  const previewBlobUrlRef = React.useRef<string>('');
  const groupListFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [draftHydratedKey, setDraftHydratedKey] = React.useState<string>('');
  const [locationLabelPreviewOpen, setLocationLabelPreviewOpen] = React.useState<boolean>(false);
  const [locationLabelPreviewPdfUrl, setLocationLabelPreviewPdfUrl] = React.useState<string>('');
  const [locationLabelPreviewTitle, setLocationLabelPreviewTitle] = React.useState<string>('仓位标签预览');
  const [locationLabelPreviewLoading, setLocationLabelPreviewLoading] = React.useState<boolean>(false);
  const [locationLabelPayload, setLocationLabelPayload] = React.useState<any>(null);
  const locationLabelIframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const [speechLang, setSpeechLang] = React.useState<LocalSpeechLang>('zh-CN');
  const [speechSupported, setSpeechSupported] = React.useState<boolean>(false);
  const isReadonly = canOperate === false;

  const panelStyle = (bg: string, border: string): React.CSSProperties => ({
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    border: `2px solid ${border}`,
    borderRadius: 10,
    background: bg,
    padding: 12,
    overflow: 'visible',
    boxSizing: 'border-box',
  });
  const locationBadgeStyle: React.CSSProperties = {
    display: 'inline-block',
    minWidth: 44,
    padding: '2px 10px',
    borderRadius: 6,
    background: '#ffeb3b',
    border: '1px solid #fadb14',
    color: '#cf1322',
    fontWeight: 800,
    lineHeight: 1.2,
    textAlign: 'center',
  };
  const sortMarkBadgeStyle: React.CSSProperties = {
    display: 'inline-block',
    minWidth: 64,
    padding: '2px 10px',
    borderRadius: 6,
    background: '#e6f7ff',
    border: '1px solid #91d5ff',
    color: '#0050b3',
    fontWeight: 700,
    lineHeight: 1.2,
  };
  const resolveAttachmentUrl = (att: any): string => {
    try {
      const raw = String(
        att?.url
        || att?.file_url
        || att?.download_url
        || att?.downloadUrl
        || att?.file
        || att?.file_path
        || att?.path
        || '',
      ).trim();
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return raw;
      let backendOrigin = window.location.origin;
      try {
        const u = new URL(backendOrigin);
        if (u.port === '3000') {
          u.port = '8000';
          backendOrigin = u.toString().replace(/\/$/, '');
        }
      } catch {}
      let path = raw;
      if (!path.startsWith('/')) path = `/${path}`;
      if (/^\/(instruction|attachments?)/i.test(path)) path = `/media${path}`;
      else if (!/^\/media\//i.test(path)) path = `/media${path}`;
      return `${backendOrigin}${path}`;
    } catch {
      return '';
    }
  };
  const instructionId = React.useMemo<number>(() => {
    const candidates = [
      (instructionDetail as any)?.id,
      (step as any)?.instruction_id,
      (step as any)?.instruction?.id,
      (data as any)?.instruction_id,
      (data as any)?.instruction?.id,
      (metrics as any)?.instruction_id,
    ];
    for (const c of candidates) {
      const n = Number(c || 0);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 0;
  }, [instructionDetail, step, data, metrics]);
  const instructionProjectId = React.useMemo<number>(() => {
    const detail: any = instructionDetail || {};
    const candidates = [
      detail?.project,
      detail?.project_id,
      detail?.projects_detail?.[0]?.id,
      (step as any)?.instruction_detail?.project,
      (step as any)?.instruction_detail?.project_id,
      (step as any)?.instruction?.project,
      (step as any)?.instruction?.project_id,
      (data as any)?.instruction_detail?.project,
      (data as any)?.instruction_detail?.project_id,
      (data as any)?.instruction?.project,
      (data as any)?.instruction?.project_id,
    ];
    for (const c of candidates) {
      const n = Number(c || 0);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 0;
  }, [instructionDetail, step, data]);
  React.useEffect(() => {
    setSpeechSupported(isLocalSpeechSupported());
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem('local-speech-lang');
      const lang = String(raw || '').trim();
      if (lang === 'zh-CN' || lang === 'en-US') setSpeechLang(lang as LocalSpeechLang);
    } catch {}
  }, []);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('local-speech-lang', speechLang);
    } catch {}
  }, [speechLang]);
  const draftStorageKey = React.useMemo(() => {
    const id = Number(instructionId || 0);
    if (!Number.isFinite(id) || id <= 0) return '';
    return `sort-and-count-stage-draft:${id}`;
  }, [instructionId]);
  React.useEffect(() => {
    if (!draftStorageKey) {
      setDraftHydratedKey('');
      return;
    }
    let parsed: any = null;
    try {
      const raw = window.localStorage.getItem(draftStorageKey);
      parsed = raw ? JSON.parse(raw) : null;
    } catch {
      parsed = null;
    }
    const nextSortDbRows = Array.isArray(parsed?.sortDbRows) ? parsed.sortDbRows : [];
    const nextOperationRows = Array.isArray(parsed?.operationRows) ? parsed.operationRows : [];
    const nextBoxedMap = (parsed?.boxedQtyByLocationSort && typeof parsed.boxedQtyByLocationSort === 'object')
      ? parsed.boxedQtyByLocationSort
      : {};
    setSortDbRows(nextSortDbRows);
    setOperationRows(nextOperationRows);
    setBoxedQtyByLocationSort(nextBoxedMap);
    setCurrentInputMark(String(parsed?.currentInputMark || ''));
    setCurrentSortIdDisplay(String(parsed?.currentSortIdDisplay || '-'));
    setCurrentSortQtyDisplay(Math.max(1, Number(parsed?.currentSortQtyDisplay || 1)));
    setCurrentLocationDisplay(String(parsed?.currentLocationDisplay || '-'));
    setDraftHydratedKey(draftStorageKey);
  }, [draftStorageKey]);
  React.useEffect(() => {
    if (!draftStorageKey) return;
    if (draftHydratedKey !== draftStorageKey) return;
    const payload = {
      version: 1,
      savedAt: Date.now(),
      sortDbRows: Array.isArray(sortDbRows) ? sortDbRows : [],
      operationRows: Array.isArray(operationRows) ? operationRows : [],
      boxedQtyByLocationSort: boxedQtyByLocationSort && typeof boxedQtyByLocationSort === 'object' ? boxedQtyByLocationSort : {},
      currentInputMark: String(currentInputMark || ''),
      currentSortIdDisplay: String(currentSortIdDisplay || '-'),
      currentSortQtyDisplay: Math.max(1, Number(currentSortQtyDisplay || 1)),
      currentLocationDisplay: String(currentLocationDisplay || '-'),
    };
    try {
      window.localStorage.setItem(draftStorageKey, JSON.stringify(payload));
    } catch {}
  }, [
    draftStorageKey,
    draftHydratedKey,
    sortDbRows,
    operationRows,
    boxedQtyByLocationSort,
    currentInputMark,
    currentSortIdDisplay,
    currentSortQtyDisplay,
    currentLocationDisplay,
  ]);
  const loadLatestAttachments = React.useCallback(async () => {
    if (!instructionId) {
      setBackendAttachments([]);
      return;
    }
    setAttachmentsLoading(true);
    try {
      const resp = await request.get('/api/v1/instruction/attachments/', {
        params: { instruction: instructionId, page_size: 500 },
      });
      const payload = resp?.data;
      const rows = Array.isArray(payload?.results)
        ? payload.results
        : (Array.isArray(payload) ? payload : []);
      setBackendAttachments(rows);
    } catch (e: any) {
      setBackendAttachments([]);
      message.warning(e?.response?.data?.detail || '加载指令附件失败');
    } finally {
      setAttachmentsLoading(false);
    }
  }, [instructionId]);
  const loadLatestBaselines = React.useCallback(async () => {
    if (!instructionId) {
      setBaselineRows([]);
      return;
    }
    setBaselineLoading(true);
    try {
      const resp = await request.get('/api/v1/instruction/baselines/', {
        params: { instruction: instructionId, page_size: 1000 },
      });
      const rows = Array.isArray(resp?.data?.results)
        ? resp.data.results
        : (Array.isArray(resp?.data) ? resp.data : []);
      setBaselineRows(rows);
    } catch (e: any) {
      setBaselineRows([]);
      message.warning(e?.response?.data?.detail || '加载基线文件失败');
    } finally {
      setBaselineLoading(false);
    }
  }, [instructionId]);
  // 进入页面后即预拉取“指令附件 + 基线文件”，保证 tab 标题信息首屏可见
  React.useEffect(() => {
    if (!instructionId) {
      setBackendAttachments([]);
      setBaselineRows([]);
      return;
    }
    loadLatestAttachments();
    loadLatestBaselines();
  }, [instructionId, loadLatestAttachments, loadLatestBaselines]);
  // 进入分拣清点阶段后，预拉取“客户创建 + LIU”的项目库存单元及分拣组信息，供后续装箱匹配/手动匹配复用
  React.useEffect(() => {
    if (!instructionId || instructionId <= 0) return;
    if (!instructionProjectId || instructionProjectId <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const contentUpdates: Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>> = {};
        const sortingGroupUpdates: Record<string, { code: string; name: string }> = {};
        let page = 1;
        const pageSize = 200;
        let guard = 0;
        while (!cancelled && guard < 100) {
          guard += 1;
          const resp = await getInventoryUnits({
            page,
            page_size: pageSize,
            project: instructionProjectId,
            unit_type: 'LIU',
            source_type: 'customer',
            ordering: '-id',
          } as any);
          const payload: any = (resp as any)?.data;
          const rows: any[] = Array.isArray(payload?.results)
            ? payload.results
            : (Array.isArray(payload) ? payload : []);
          (Array.isArray(rows) ? rows : []).forEach((unit: any) => {
            const uid = Number(unit?.id || 0);
            if (!Number.isFinite(uid) || uid <= 0) return;
            const idStr = String(uid);
            const sg = unit?.sorting_group_info || {};
            const sgCode = String(sg?.system_code || sg?.code || '').trim();
            const sgName = String(sg?.name || '').trim();
            if (sgCode || sgName) sortingGroupUpdates[idStr] = { code: sgCode, name: sgName };
            const arr = Array.isArray(unit?.content?.items)
              ? unit.content.items
              : (Array.isArray(unit?.content_items) ? unit.content_items : []);
            const mapped = (arr || []).map((it: any) => {
              const p = it?.product || {};
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              const productName = String(it?.product_name || p?.name || '').trim();
              const customCode = String(
                it?.product_custom_code
                || p?.custom_code
                || it?.product_code
                || '',
              ).trim();
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes)
                  ? it.sorting_codes
                  : (Array.isArray(p?.sorting_codes) ? p.sorting_codes : []);
                if (sc.length > 0) {
                  const codes = sc.map((c: any) => String(c?.code || c || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(
                  p?.sorting_barcode
                  || p?.barcode_text
                  || it?.product_sorting_barcode
                  || it?.sorting_barcode
                  || it?.product_system_code
                  || it?.system_code
                  || it?.product_code
                  || '',
                ).trim();
              }
              return { barcode, qty, productName, customCode };
            }).filter((x: any) => x.barcode || x.qty || x.productName || x.customCode);
            if (mapped.length > 0) contentUpdates[idStr] = mapped;
          });
          if (!Array.isArray(payload?.results)) break;
          const hasNext = !!payload?.next;
          const totalCount = Number(payload?.count || 0);
          if (!hasNext) break;
          if (totalCount > 0 && page * pageSize >= totalCount) break;
          page += 1;
        }
        if (cancelled) return;
        if (Object.keys(contentUpdates).length > 0) {
          setOperationTargetUnitContentCache((prev) => ({ ...(prev || {}), ...contentUpdates }));
        }
        if (Object.keys(sortingGroupUpdates).length > 0) {
          setOperationTargetUnitSortingGroupCache((prev) => ({ ...(prev || {}), ...sortingGroupUpdates }));
        }
      } catch {
        // 预拉取失败不阻断主流程，继续沿用按需查询逻辑
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instructionId, instructionProjectId]);
  React.useEffect(() => {
    const total = Array.isArray(backendAttachments) ? backendAttachments.length : 0;
    const max = Math.max(1, Math.ceil(total / attachmentsPageSize));
    if (attachmentsPage > max) setAttachmentsPage(1);
  }, [attachmentsPage, attachmentsPageSize, backendAttachments]);
  React.useEffect(() => () => {
    const old = previewBlobUrlRef.current;
    if (old && old.startsWith('blob:')) {
      try { URL.revokeObjectURL(old); } catch {}
      previewBlobUrlRef.current = '';
    }
  }, []);

  const attachmentRows = React.useMemo(() => {
    const all: any[] = Array.isArray(backendAttachments) ? backendAttachments : [];
    const dedup = new Map<string, any>();
    all.forEach((att: any, idx: number) => {
      const id = Number(att?.id || 0);
      const name = String(att?.file_name || att?.name || att?.label || att?.file || `附件${idx + 1}`).trim();
      const url = resolveAttachmentUrl(att);
      const kind = String(att?.kind || att?.role_code || att?.type || '-').trim() || '-';
      const key = id > 0 ? `id-${id}` : `${name}__${url || idx}`;
      if (!dedup.has(key)) {
        dedup.set(key, {
          key,
          id,
          file_name: name,
          kind,
          url,
          raw: att,
        });
      }
    });
    return Array.from(dedup.values());
  }, [backendAttachments]);
  const groupListBaseline = React.useMemo(() => {
    return baselineRows.find((x: any) => String(x?.code || '').trim().toUpperCase() === 'GROUP_LIST') || null;
  }, [baselineRows]);
  const hasGroupListBaseline = React.useMemo(() => {
    const files = Array.isArray(groupListBaseline?.files) ? groupListBaseline.files : [];
    return files.length > 0;
  }, [groupListBaseline]);
  React.useEffect(() => {
    const parsePreviewRows = async () => {
      setGroupListPreviewError('');
      setGroupListPreviewCols([]);
      setGroupListPreviewRows([]);
      if (!groupListBaseline) return;
      const files = Array.isArray(groupListBaseline?.files) ? groupListBaseline.files : [];
      if (files.length === 0) return;
      const sorted = [...files].sort((a: any, b: any) => Number(b?.id || 0) - Number(a?.id || 0));
      const latest = sorted[0];
      const fileUrl = resolveAttachmentUrl(latest);
      if (!fileUrl) {
        setGroupListPreviewError('GROUP_LIST 已存在，但文件地址无效');
        return;
      }
      const fileName = String(latest?.file_name || latest?.name || '').toLowerCase();
      const ext = (fileName.match(/\.([a-z0-9]+)(?:\?|#|$)/i)?.[1] || '').toLowerCase();
      const buildRawTable = (rows: any[][]) => {
        const arr = Array.isArray(rows) ? rows.filter((r:any) => Array.isArray(r)) : [];
        if (arr.length === 0) return { cols: [], data: [] };
        const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
        const dataRows = arr.slice(1);
        const cols = headers.map((h:any, i:number) => ({
          title: String(h || `列${i + 1}`),
          dataIndex: `c${i}`,
          align: 'center' as const,
        }));
        const data = dataRows.map((r:any[], idx:number) => {
          const obj: any = { __idx: idx + 2 };
          cols.forEach((c:any, i:number) => { obj[c.dataIndex] = String(r?.[i] ?? ''); });
          return obj;
        });
        return { cols, data };
      };
      setGroupListPreviewLoading(true);
      try {
        if (ext === 'csv') {
          const Papa = (await import('papaparse') as any)?.default || (await import('papaparse') as any);
          const resp = await fetch(fileUrl, { credentials: 'include' as any });
          if (!resp.ok) throw new Error('GROUP_LIST 文件加载失败');
          const text = await resp.text();
          const parsed = Papa.parse(text, { header: false, skipEmptyLines: false });
          const arr = Array.isArray(parsed?.data) ? parsed.data : [];
          const { cols, data } = buildRawTable(arr as any[][]);
          setGroupListPreviewCols(cols);
          setGroupListPreviewRows(data);
          return;
        }
        if (ext === 'xlsx' || ext === 'xls') {
          const XLSX = await import('xlsx') as any;
          const resp = await fetch(fileUrl, { credentials: 'include' as any });
          if (!resp.ok) throw new Error('GROUP_LIST 文件加载失败');
          const buf = await resp.arrayBuffer();
          const wb = XLSX.read(buf, { type: 'array' });
          const sheetName = String((wb?.SheetNames || [])[0] || '').trim();
          const sh = sheetName ? wb.Sheets?.[sheetName] : null;
          if (!sheetName || !sh) throw new Error('GROUP_LIST 首个工作表不存在');
          const aoa: any[] = XLSX.utils.sheet_to_json(sh, { header: 1, defval: '', blankrows: false });
          const { cols, data } = buildRawTable(Array.isArray(aoa) ? (aoa as any[][]) : []);
          setGroupListPreviewCols(cols);
          setGroupListPreviewRows(data);
          return;
        }
        setGroupListPreviewError('GROUP_LIST 文件仅支持 csv/xls/xlsx 在线绘制');
      } catch (e: any) {
        setGroupListPreviewError(String(e?.message || 'GROUP_LIST 绘制失败'));
      } finally {
        setGroupListPreviewLoading(false);
      }
    };
    parsePreviewRows();
  }, [groupListBaseline]);
  const ungroupedHandlingAnswer = React.useMemo(() => {
    const answers = (
      (instructionDetail as any)?.customer_answers
      || (step as any)?.instruction_detail?.customer_answers
      || (step as any)?.instruction?.customer_answers
      || (data as any)?.instruction_detail?.customer_answers
      || {}
    ) as Record<string, any>;
    if (!answers || typeof answers !== 'object') return '-';
    const keyList = Object.keys(answers || {});
    const hitKey = keyList.find((k) => {
      const key = String(k || '').toLowerCase();
      return (
        key.includes('未分组')
        || key.includes('ungroup')
        || key.includes('un_group')
        || key.includes('unassigned')
        || key.includes('grouped_product')
      );
    });
    if (hitKey) return String((answers as any)[hitKey] ?? '-').trim() || '-';
    return '-';
  }, [instructionDetail, step, data]);
  const instructionQuestionAnswers = React.useMemo(() => {
    const answers = (
      (instructionDetail as any)?.customer_answers
      || (step as any)?.instruction_detail?.customer_answers
      || (step as any)?.instruction?.customer_answers
      || (data as any)?.instruction_detail?.customer_answers
      || {}
    ) as Record<string, any>;
    const qn = (
      (instructionDetail as any)?.questionnaire_snapshot
      || (step as any)?.instruction_detail?.questionnaire_snapshot
      || (step as any)?.instruction?.questionnaire_snapshot
      || (data as any)?.instruction_detail?.questionnaire_snapshot
      || {}
    ) as any;
    const qRows = Array.isArray(qn?.questions) ? qn.questions : [];
    const toText = (v: any): string => {
      if (v === undefined || v === null || v === '') return '-';
      if (Array.isArray(v)) return v.length > 0 ? v.map((x) => String(x)).join(' / ') : '-';
      if (typeof v === 'boolean') return v ? '是' : '否';
      if (typeof v === 'object') {
        try { return JSON.stringify(v); } catch { return String(v); }
      }
      return String(v);
    };
    const rows = (Array.isArray(qRows) ? qRows : []).map((q: any, idx: number) => {
      const key = String(q?.key || '').trim();
      const label = String(q?.label || key || `问题${idx + 1}`).trim();
      const val = key ? (answers as any)?.[key] : undefined;
      return {
        key: key || `q-${idx}`,
        __idx: idx + 1,
        question: label || '-',
        answer: toText(val),
      };
    });
    return {
      rows,
      answers,
      questionRows: qRows,
    };
  }, [instructionDetail, step, data]);
  const instructionTargetRows = React.useMemo(() => {
    const list = (
      (instructionDetail as any)?.targets_detail
      || (step as any)?.instruction_detail?.targets_detail
      || (step as any)?.instruction?.targets_detail
      || (data as any)?.instruction_detail?.targets_detail
      || []
    ) as any[];
    if (!Array.isArray(list)) return [];
    return list.map((r: any, idx: number) => ({ ...(r || {}), __idx: idx + 1 }));
  }, [instructionDetail, step, data]);
  React.useEffect(() => {
    (async () => {
      try {
        const rows = Array.isArray(instructionTargetRows) ? instructionTargetRows : [];
        const pendingIds = new Set<string>();
        rows.forEach((r: any) => {
          if (String(r?.target_type || '').trim() !== 'inventory_unit') return;
          const uid = r?.inventory_unit_id ?? r?.target?.inventory_unit_id ?? r?.snapshot?.inventory_unit_id;
          if (uid == null) return;
          const idStr = String(uid);
          const snap = r?.target?.snapshot || r?.snapshot || {};
          const info = r?.sorting_group_info || r?.target?.sorting_group_info || snap?.sorting_group_info || {};
          const hasSortingGroup = !!String(
            info?.system_code
            || info?.code
            || info?.name
            || r?.sorting_group_code
            || r?.sorting_group_name
            || '',
          ).trim() || !!operationTargetUnitSortingGroupCache[idStr];
          const invItems = Array.isArray(r?.inventory_content_items) ? r.inventory_content_items : [];
          const hasContent = invItems.length > 0 || !!operationTargetUnitContentCache[idStr];
          if (!hasSortingGroup || !hasContent) pendingIds.add(idStr);
        });
        if (pendingIds.size === 0) return;
        const contentUpdates: Record<string, Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>> = {};
        const sortingGroupUpdates: Record<string, { code: string; name: string }> = {};
        await Promise.all(Array.from(pendingIds).map(async (id) => {
          try {
            const resp = await getInventoryUnitDetail(id);
            const d: any = (resp as any)?.data || resp;
            const sg = d?.sorting_group_info || {};
            const sgCode = String(sg?.system_code || sg?.code || '').trim();
            const sgName = String(sg?.name || '').trim();
            if (sgCode || sgName) sortingGroupUpdates[id] = { code: sgCode, name: sgName };
            const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
            const mapped = (arr || []).map((it: any) => {
              const p = it?.product || {};
              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
              const productName = String(it?.product_name || p?.name || '').trim();
              const customCode = String(
                it?.product_custom_code
                || p?.custom_code
                || it?.product_code
                || ''
              ).trim();
              let barcode = '';
              try {
                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                if (sc.length > 0) {
                  const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                  if (codes.length > 0) barcode = codes.join('/');
                }
              } catch {}
              if (!barcode) {
                barcode = String(
                  p?.sorting_barcode
                  || p?.barcode_text
                  || it?.product_sorting_barcode
                  || it?.sorting_barcode
                  || it?.product_system_code
                  || it?.system_code
                  || it?.product_code
                  || ''
                ).trim();
              }
              return { barcode, qty, productName, customCode };
            }).filter((x: any) => x.barcode || x.qty || x.productName || x.customCode);
            if (mapped.length > 0) contentUpdates[id] = mapped;
          } catch {}
        }));
        if (Object.keys(contentUpdates).length > 0) {
          setOperationTargetUnitContentCache((prev) => ({ ...prev, ...contentUpdates }));
        }
        if (Object.keys(sortingGroupUpdates).length > 0) {
          setOperationTargetUnitSortingGroupCache((prev) => ({ ...prev, ...sortingGroupUpdates }));
        }
      } catch {}
    })();
  }, [instructionTargetRows, operationTargetUnitContentCache, operationTargetUnitSortingGroupCache]);
  const snapshotInstructionTargetRows = React.useMemo(() => {
    const rows = Array.isArray(instructionTargetRows) ? instructionTargetRows : [];
    return rows.map((row: any) => {
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const uid = row?.inventory_unit_id ?? row?.target?.inventory_unit_id ?? snap?.inventory_unit_id;
      const idStr = uid == null ? '' : String(uid);
      const cachedGroup = idStr ? operationTargetUnitSortingGroupCache[idStr] : undefined;
      const cachedItems = idStr ? operationTargetUnitContentCache[idStr] : undefined;
      const next: any = { ...(row || {}) };
      if (cachedGroup && (cachedGroup.code || cachedGroup.name)) {
        next.sorting_group_info = {
          ...(next.sorting_group_info || {}),
          system_code: cachedGroup.code,
          name: cachedGroup.name,
        };
        next.sorting_group_code = next.sorting_group_code || cachedGroup.code;
        next.sorting_group_name = next.sorting_group_name || cachedGroup.name;
      }
      if (Array.isArray(cachedItems) && cachedItems.length > 0) {
        next.inventory_content_items = cachedItems.map((it: any) => ({
          quantity: Number(it?.qty || 0),
          qty: Number(it?.qty || 0),
          product_name: String(it?.productName || '').trim(),
          product_custom_code: String(it?.customCode || '').trim(),
          sorting_barcode: String(it?.barcode || '').trim(),
          barcode: String(it?.barcode || '').trim(),
          productName: String(it?.productName || '').trim(),
          customCode: String(it?.customCode || '').trim(),
        }));
      }
      return next;
    });
  }, [instructionTargetRows, operationTargetUnitContentCache, operationTargetUnitSortingGroupCache]);
  const formatInventoryUnitContentItemsText = React.useCallback(
    (items: Array<{ barcode: string; qty: number; productName?: string; customCode?: string }>) => {
      const list = Array.isArray(items) ? items : [];
      if (list.length === 0) return '-';
      return list.map((it: any) => {
        const productName = String(it?.productName || '').trim();
        const customCode = String(it?.customCode || '').trim();
        const barcode = String(it?.barcode || '').trim();
        const meta = [customCode, barcode].filter(Boolean).join(' ');
        let head = '-';
        if (productName && meta) head = `${productName}(${meta})`;
        else if (productName) head = productName;
        else if (meta) head = meta;
        return `${head} × ${Math.max(0, Number(it?.qty || 0))}`;
      }).join('、');
    },
    [],
  );
  const extractInstructionTargetContentItems = React.useCallback((row: any): Array<{ barcode: string; qty: number; productName?: string; customCode?: string }> => {
    try {
      const snap = row?.target?.snapshot || row?.snapshot || {};
      const uid = row?.inventory_unit_id ?? row?.target?.inventory_unit_id ?? snap?.inventory_unit_id;
      const cachedItems = uid != null ? operationTargetUnitContentCache[String(uid)] : undefined;
      if (Array.isArray(cachedItems) && cachedItems.length > 0) return cachedItems;
      const fromInventoryContentItems = Array.isArray(row?.inventory_content_items) ? row.inventory_content_items : [];
      if (fromInventoryContentItems.length > 0) {
        return fromInventoryContentItems.map((it: any) => {
          const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
          const productName = String(it?.product_name || it?.name || '').trim();
          const customCode = String(it?.product_custom_code || it?.product_code || '').trim();
          let barcode = '';
          try {
            const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
            if (sc.length > 0) {
              const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
              if (codes.length > 0) barcode = codes.join('/');
            }
          } catch {}
          if (!barcode) {
            barcode = String(
              it?.product_sorting_barcode
              || it?.sorting_barcode
              || it?.product_system_code
              || it?.system_code
              || it?.product_code
              || ''
            ).trim();
          }
          return { barcode, qty, productName, customCode };
        }).filter((x: any) => x.barcode || x.qty);
      }
      const candidates: any[] = [
        snap?.content?.items,
        row?.content?.items,
        row?.target?.content?.items,
        row?.items,
      ].filter(Boolean);
      for (const cand of candidates) {
        const arr = Array.isArray(cand) ? cand : [];
        if (arr.length > 0) {
          return arr.map((it: any) => {
            const p = it?.product || {};
            const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
            const productName = String(it?.product_name || p?.name || '').trim();
            const customCode = String(
              it?.product_custom_code
              || p?.custom_code
              || it?.product_code
              || ''
            ).trim();
            let barcode = '';
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              if (sc.length > 0) {
                const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
                if (codes.length > 0) barcode = codes.join('/');
              }
            } catch {}
            if (!barcode) {
              barcode = String(
                it?.product_system_code
                || it?.system_code
                || it?.product_code
                || ''
              );
            }
            return { barcode, qty, productName, customCode };
          }).filter((x: any) => x.barcode || x.qty);
        }
      }
    } catch {}
    return [];
  }, [operationTargetUnitContentCache]);
  const renderInstructionTargetContentItems = React.useCallback((row: any) => {
    const items = extractInstructionTargetContentItems(row);
    if (!items || items.length === 0) return <span style={{ color: '#999' }}>-</span>;
    const itemLines = items.map((it: any, idx: number) => {
      const productName = String(it?.productName || '').trim();
      const customCode = String(it?.customCode || '').trim();
      const barcode = String(it?.barcode || '').trim();
      const meta = [customCode, barcode].filter(Boolean).join(' ');
      let head = '-';
      if (productName && meta) head = `${productName}(${meta})`;
      else if (productName) head = productName;
      else if (meta) head = meta;
      return (
        <div key={`target-item-${idx}`}>
          {`${head} × ${Math.max(0, Number(it?.qty || 0))}`}
        </div>
      );
    });
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {itemLines}
      </div>
    );
  }, [extractInstructionTargetContentItems]);
  const sortDemandAndConversionDisplay = React.useMemo(() => {
    const answers = instructionQuestionAnswers.answers || {};
    const qRows = instructionQuestionAnswers.questionRows || [];
    const demandQuestion = (Array.isArray(qRows) ? qRows : []).find((q: any) => {
      const key = String(q?.key || '').trim().toLowerCase();
      const label = String(q?.label || '').trim();
      return key === 'sorting_requirement' || label === '分拣需求';
    });
    const demandKey = String(demandQuestion?.key || '').trim();
    const demandText = String(
      (demandKey ? (answers as any)?.[demandKey] : undefined)
      || (answers as any)?.sorting_requirement
      || ''
    ).trim();
    const normalizedDemandText = (() => {
      const raw = String(demandText || '').trim();
      if (!raw) return '分拣需求未填写';
      return raw
        .replace(/^需求\s*[123]\s*[:：]\s*/i, '')
        .replace(/^need[_\s-]*[123]\s*[:：]\s*/i, '')
        .trim() || '分拣需求未填写';
    })();
    const demandPart = `分拣需求:${normalizedDemandText}`;
    return demandPart;
  }, [instructionDetail, step, data, instructionQuestionAnswers]);
  const sortCountDemandMode = React.useMemo<'need1' | 'need2' | 'need3' | ''>(() => {
    try {
      const answers = instructionQuestionAnswers.answers || {};
      const qRows = instructionQuestionAnswers.questionRows || [];
      const demandQuestion = (Array.isArray(qRows) ? qRows : []).find((q: any) => {
        const key = String(q?.key || '').trim().toLowerCase();
        const label = String(q?.label || '').trim();
        return key === 'sorting_requirement' || label === '分拣需求';
      });
      const demandKey = String(demandQuestion?.key || '').trim();
      const raw = String(
        (demandKey ? (answers as any)?.[demandKey] : undefined)
        || (answers as any)?.sorting_requirement
        || ''
      ).trim().toLowerCase();
      if (!raw) return '';
      if (raw.includes('需求1') || raw.includes('need1') || raw.includes('混装')) return 'need1';
      if (raw.includes('需求2') || raw.includes('need2') || raw.includes('箱库存单元') || raw.includes('托盘库存单元')) return 'need2';
      if (raw.includes('需求3') || raw.includes('need3') || raw.includes('再次细分') || raw.includes('按照规则')) return 'need3';
      return '';
    } catch {
      return '';
    }
  }, [instructionQuestionAnswers]);
  const groupListBaselineFiles = React.useMemo(() => {
    const files = Array.isArray(groupListBaseline?.files) ? groupListBaseline.files : [];
    return files.map((f: any, idx: number) => ({
      key: String(f?.id || `f-${idx}`),
      name: String(f?.file_name || f?.name || f?.file || `文件${idx + 1}`).trim() || `文件${idx + 1}`,
      id: Number(f?.id || 0),
      file_name: String(f?.file_name || f?.name || f?.file || `文件${idx + 1}`),
      url: resolveAttachmentUrl(f),
      raw: f,
    }));
  }, [groupListBaseline]);
  const groupListSearchDataIndex = React.useMemo(() => {
    const cols = Array.isArray(groupListPreviewCols) ? groupListPreviewCols : [];
    const byTitle = cols.find((c: any) => String(c?.title || '').includes('分拣标识'));
    if (byTitle?.dataIndex) return String(byTitle.dataIndex);
    if (cols?.[1]?.dataIndex) return String(cols[1].dataIndex);
    return '';
  }, [groupListPreviewCols]);
  const filteredGroupListPreviewRows = React.useMemo(() => {
    const rows = Array.isArray(groupListPreviewRows) ? groupListPreviewRows : [];
    const kw = String(groupListSearchKeyword || '').trim().toUpperCase();
    if (!kw) return rows;
    const key = String(groupListSearchDataIndex || '').trim();
    if (!key) return rows;
    return rows.filter((r: any) => String(r?.[key] ?? '').trim().toUpperCase().includes(kw));
  }, [groupListPreviewRows, groupListSearchKeyword, groupListSearchDataIndex]);
  const groupListGroupNoDataIndex = React.useMemo(() => {
    const cols = Array.isArray(groupListPreviewCols) ? groupListPreviewCols : [];
    const byTitle = cols.find((c: any) => String(c?.title || '').includes('分组编号'));
    if (byTitle?.dataIndex) return String(byTitle.dataIndex);
    if (cols?.[0]?.dataIndex) return String(cols[0].dataIndex);
    return '';
  }, [groupListPreviewCols]);
  const groupedGroupListPreviewRows = React.useMemo(() => {
    const rows = (Array.isArray(filteredGroupListPreviewRows) ? filteredGroupListPreviewRows : [])
      .map((r: any, idx: number) => ({ ...(r || {}), __original_order: idx }));
    const groupKey = String(groupListGroupNoDataIndex || '').trim();
    if (!groupKey) return rows;

    const getGroupSortValue = (row: any) => {
      const raw = String(row?.[groupKey] ?? '').trim();
      const numeric = Number(raw);
      return Number.isFinite(numeric) && raw !== '' ? { type: 0, value: numeric } : { type: 1, value: raw || 'ZZZ_EMPTY' };
    };
    const sorted = [...rows].sort((a: any, b: any) => {
      const ag = getGroupSortValue(a);
      const bg = getGroupSortValue(b);
      if (ag.type !== bg.type) return ag.type - bg.type;
      if (ag.value < bg.value) return -1;
      if (ag.value > bg.value) return 1;
      return Number(a.__original_order || 0) - Number(b.__original_order || 0);
    });

    const displayNoByGroup = new Map<string, number>();
    sorted.forEach((row: any) => {
      const raw = String(row?.[groupKey] ?? '').trim();
      const key = raw || '__EMPTY__';
      if (!displayNoByGroup.has(key)) displayNoByGroup.set(key, displayNoByGroup.size + 1);
    });

    const start = (Math.max(1, Number(groupListTablePage || 1)) - 1) * Math.max(1, Number(groupListTablePageSize || 10));
    const end = start + Math.max(1, Number(groupListTablePageSize || 10));
    const currentPageRows = sorted.slice(start, end);
    const pageSpanMap = new Map<string, { firstKey: string; count: number }>();
    currentPageRows.forEach((row: any, pageIdx: number) => {
      const raw = String(row?.[groupKey] ?? '').trim();
      const key = raw || '__EMPTY__';
      const rowKey = String(row?.__idx ?? row?.__original_order ?? pageIdx);
      const hit = pageSpanMap.get(key);
      if (!hit) pageSpanMap.set(key, { firstKey: rowKey, count: 1 });
      else hit.count += 1;
    });

    return sorted.map((row: any, idx: number) => {
      const raw = String(row?.[groupKey] ?? '').trim();
      const key = raw || '__EMPTY__';
      const rowKey = String(row?.__idx ?? row?.__original_order ?? idx);
      const pageHit = idx >= start && idx < end ? pageSpanMap.get(key) : undefined;
      const rowSpan = pageHit ? (pageHit.firstKey === rowKey ? pageHit.count : 0) : 1;
      return {
        ...row,
        __preset_group_display_no: displayNoByGroup.get(key) || '',
        __preset_group_row_span: rowSpan,
      };
    });
  }, [filteredGroupListPreviewRows, groupListGroupNoDataIndex, groupListTablePage, groupListTablePageSize]);
  React.useEffect(() => {
    setGroupListTablePage(1);
  }, [groupListSearchKeyword, groupListPreviewRows]);
  const presetGroupListEntries = React.useMemo(() => {
    const rows = Array.isArray(groupListPreviewRows) ? groupListPreviewRows : [];
    const groupKey = String(groupListGroupNoDataIndex || '').trim();
    const markKey = String(groupListSearchDataIndex || '').trim();
    if (!groupKey || !markKey) return [] as Array<{
      rowIdx: number;
      groupNo: string;
      markRaw: string;
      markNorm: string;
    }>;
    return rows.map((r: any, idx: number) => {
      const groupNo = String(r?.[groupKey] ?? '').trim();
      const markRaw = String(r?.[markKey] ?? '').trim();
      return {
        rowIdx: idx,
        groupNo,
        markRaw,
        markNorm: String(markRaw || '').trim().toUpperCase(),
      };
    }).filter((x) => x.groupNo && x.markNorm);
  }, [groupListPreviewRows, groupListGroupNoDataIndex, groupListSearchDataIndex]);
  const ensureGroupListBaselineHead = React.useCallback(async (): Promise<number> => {
    if (!instructionId) throw new Error('缺少指令ID');
    const existedId = Number(groupListBaseline?.id || 0);
    if (Number.isFinite(existedId) && existedId > 0) return existedId;
    const payload = {
      instruction: instructionId,
      code: 'GROUP_LIST',
      name: '分组列表',
      state: 'draft',
    } as any;
    const created = await request.post('/api/v1/instruction/baselines/', payload);
    const bid = Number(created?.data?.id || 0);
    if (!Number.isFinite(bid) || bid <= 0) throw new Error('创建 GROUP_LIST 基线失败');
    return bid;
  }, [instructionId, groupListBaseline]);
  const uploadGroupListBaselineFile = React.useCallback(async (file: File) => {
    if (isReadonly) return;
    if (!file) return;
    setGroupListUploadLoading(true);
    try {
      const baselineId = await ensureGroupListBaselineHead();
      let existing: any[] = [];
      try {
        const resp = await request.get('/api/v1/instruction/baseline-files/', {
          params: { baseline: baselineId, page_size: 1000 },
        });
        existing = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      } catch {}
      for (const f of existing) {
        const fid = Number(f?.id || 0);
        if (!Number.isFinite(fid) || fid <= 0) continue;
        try { await request.delete(`/api/v1/instruction/baseline-files/${fid}/`); } catch {}
      }
      const fd = new FormData();
      fd.append('baseline', String(baselineId));
      fd.append('file', file, file.name || 'GROUP_LIST.xlsx');
      await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      try { await request.patch(`/api/v1/instruction/baselines/${baselineId}/`, { state: 'ready' }); } catch {}
      await loadLatestBaselines();
      message.success('分组列表基线文件已上传');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '上传基线文件失败');
    } finally {
      setGroupListUploadLoading(false);
    }
  }, [isReadonly, ensureGroupListBaselineHead, loadLatestBaselines]);
  const deleteGroupListBaselineFiles = React.useCallback(async () => {
    if (isReadonly) return;
    const files = Array.isArray(groupListBaseline?.files) ? groupListBaseline.files : [];
    if (!files.length) {
      message.info('当前没有可删除的基线文件');
      return;
    }
    const ok = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '确认删除基线文件',
        content: `确认删除当前分组列表基线文件吗？共 ${files.length} 个文件。`,
        okText: '确认删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    setGroupListDeleteLoading(true);
    try {
      for (const f of files) {
        const fid = Number(f?.id || 0);
        if (!Number.isFinite(fid) || fid <= 0) continue;
        try { await request.delete(`/api/v1/instruction/baseline-files/${fid}/`); } catch {}
      }
      await loadLatestBaselines();
      setGroupListPreviewCols([]);
      setGroupListPreviewRows([]);
      setGroupListPreviewError('');
      message.success('基线文件已删除');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '删除基线文件失败');
    } finally {
      setGroupListDeleteLoading(false);
    }
  }, [isReadonly, groupListBaseline, loadLatestBaselines]);
  const triggerPickGroupListFile = () => {
    if (isReadonly) return;
    try {
      if (groupListFileInputRef.current) {
        groupListFileInputRef.current.value = '';
        groupListFileInputRef.current.click();
      }
    } catch {}
  };
  const ungroupedAnswerBadgeStyle = React.useMemo<React.CSSProperties>(() => {
    const txt = String(ungroupedHandlingAnswer || '').trim();
    if (txt.includes('分为一组')) {
      return {
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 6,
        border: '1px solid #b7eb8f',
        background: '#f6ffed',
        color: '#389e0d',
        fontWeight: 600,
      };
    }
    if (txt.includes('继续分拣')) {
      return {
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 6,
        border: '1px solid #ffa39e',
        background: '#fff1f0',
        color: '#cf1322',
        fontWeight: 600,
      };
    }
    return {
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 6,
      border: '1px solid #d9d9d9',
      background: '#fafafa',
      color: '#595959',
      fontWeight: 500,
    };
  }, [ungroupedHandlingAnswer]);
  const currentSortModeLabel = React.useMemo(() => {
    const ans = String(ungroupedHandlingAnswer || '').trim();
    if (!hasGroupListBaseline) return '标准模式';
    if (ans.includes('分为一组')) return '分组-简化模式';
    if (ans.includes('继续分拣')) return '分组-细分模式';
    // 兜底：有分组列表但答案未命中时，保持分组流程中的简化模式
    return '分组-简化模式';
  }, [hasGroupListBaseline, ungroupedHandlingAnswer]);
  const sortAndCountHeaderCenterDisplay = React.useMemo(() => {
    const left = String(sortDemandAndConversionDisplay || '').trim();
    const answers = instructionQuestionAnswers.answers || {};
    const qRows = instructionQuestionAnswers.questionRows || [];
    const methodQuestion = (Array.isArray(qRows) ? qRows : []).find((q: any) => {
      const key = String(q?.key || '').trim().toLowerCase();
      const label = String(q?.label || '').trim();
      return key === 'sorting_method' || key === 'sort_method' || label === '分拣方式';
    });
    const methodKey = String(methodQuestion?.key || '').trim();
    const methodText = String(
      (methodKey ? (answers as any)?.[methodKey] : undefined)
      || (answers as any)?.sorting_method
      || (answers as any)?.sort_method
      || ''
    ).trim();
    const modePart = `当前分拣模式:${currentSortModeLabel}`;
    const parts = [left, methodText, modePart].map((x) => String(x || '').trim()).filter(Boolean);
    return parts.join('|');
  }, [sortDemandAndConversionDisplay, currentSortModeLabel, instructionQuestionAnswers]);
  const calcAdaptiveFontSize = React.useCallback((value: string, opt: {
    minPx: number;
    maxPx: number;
    baseLen: number;
  }) => {
    const raw = String(value || '').trim();
    const len = Math.max(raw.length, 1);
    const ratio = Math.min(1, opt.baseLen / len);
    const px = Math.round(opt.minPx + (opt.maxPx - opt.minPx) * ratio);
    return `${Math.max(opt.minPx, Math.min(opt.maxPx, px))}px`;
  }, []);
  const renderLocationBadge = React.useCallback((v: any) => {
    const txt = String(v ?? '').trim();
    if (!txt) return '-';
    return <span style={locationBadgeStyle}>{txt}</span>;
  }, []);
  const renderSortMarkBadge = React.useCallback((v: any) => {
    const txt = String(v ?? '').trim().toUpperCase();
    if (!txt) return '-';
    const shown = txt.length > 20 ? `${txt.slice(0, 10)}...${txt.slice(-10)}` : txt;
    return (
      <span
        style={{
          ...sortMarkBadgeStyle,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
        }}
        title={txt}
      >
        {shown}
      </span>
    );
  }, []);
  const renderDateTimeTwoLines = React.useCallback((v: any) => {
    const raw = String(v || '').trim();
    if (!raw) return '-';
    const normalizedRaw = raw.replace('T', ' ').replace('Z', '');
    const [datePartRaw, timePartRaw = ''] = normalizedRaw.split(/\s+/, 2);
    const datePart = datePartRaw || '-';
    const normalizedTime = String(timePartRaw || '').replace(/-/g, ':').split('.')[0];
    return (
      <div style={{ lineHeight: 1.2, textAlign: 'center' }}>
        <div>{datePart}</div>
        <div>{normalizedTime || '-'}</div>
      </div>
    );
  }, []);
  const revokeLocationLabelPdfUrl = React.useCallback(() => {
    const old = String(locationLabelPreviewPdfUrl || '').trim();
    if (!old) return;
    try { URL.revokeObjectURL(old); } catch {}
  }, [locationLabelPreviewPdfUrl]);
  React.useEffect(() => () => {
    revokeLocationLabelPdfUrl();
  }, [revokeLocationLabelPdfUrl]);
  const buildLocationLabelPayload = React.useCallback((locationNo: string, rows: any[]) => {
    const sortedRows = [...(Array.isArray(rows) ? rows : [])].sort((a: any, b: any) => Number(a?.sort_id || 0) - Number(b?.sort_id || 0));
    return {
      label_variant: 'sort_location',
      instruction_id: instructionId || undefined,
      container_code: `SORT-LOC-${locationNo}`,
      instruction_code: instructionCodeFromParent || '-',
      location_no: locationNo,
      sort_rows: sortedRows.map((r: any) => ({
        sort_id: String(r?.sort_id ?? '').trim(),
        sorting_mark: String(r?.sorting_mark ?? '').trim(),
      })),
    };
  }, [instructionId, instructionCodeFromParent]);
  const buildSortIdLabelPayload = React.useCallback((row: any) => {
    const sid = String(row?.sort_id ?? '').trim();
    const mark = String(row?.sorting_mark ?? '').trim();
    const locationNo = String(row?.location_no ?? '').trim();
    return {
      label_variant: 'sort_id',
      instruction_id: instructionId || undefined,
      container_code: `SORT-ID-${sid || 'NA'}`,
      instruction_code: instructionCodeFromParent || '-',
      sort_id: sid,
      sorting_mark: mark,
      location_no: locationNo,
    };
  }, [instructionId, instructionCodeFromParent]);
  const openPdfLabelPreview = React.useCallback(async (payload: any, title: string, failText: string) => {
    setLocationLabelPreviewTitle(title);
    setLocationLabelPreviewOpen(true);
    setLocationLabelPreviewLoading(true);
    setLocationLabelPayload(null);
    revokeLocationLabelPdfUrl();
    setLocationLabelPreviewPdfUrl('');
    try {
      const resp = await request.post('/api/v1/storage/output-containers/labels/preview/', payload, { responseType: 'blob' as any });
      const blob = resp?.data as Blob;
      const nextUrl = URL.createObjectURL(blob);
      setLocationLabelPreviewPdfUrl(nextUrl);
      setLocationLabelPayload(payload);
    } catch (e: any) {
      const text = String(e?.message || '').trim();
      message.error(text || failText);
    } finally {
      setLocationLabelPreviewLoading(false);
    }
  }, [revokeLocationLabelPdfUrl]);
  const openLocationLabelPreview = React.useCallback(async (row: any) => {
    if (isReadonly) return;
    const loc = String(row?.location_no ?? '').trim();
    if (!loc) {
      message.warning('仓位为空，无法生成标签');
      return;
    }
    const rows = (Array.isArray(sortDbRows) ? sortDbRows : []).filter((r: any) => String(r?.location_no ?? '').trim() === loc);
    if (rows.length === 0) {
      message.warning('当前仓位无可打印数据');
      return;
    }
    const payload = buildLocationLabelPayload(loc, rows);
    await openPdfLabelPreview(payload, `仓位标签预览（${loc}）`, '生成仓位标签失败');
  }, [isReadonly, sortDbRows, buildLocationLabelPayload, openPdfLabelPreview]);
  const openSortIdLabelPreview = React.useCallback(async (row: any) => {
    if (isReadonly) return;
    const sid = String(row?.sort_id ?? '').trim();
    if (!sid) {
      message.warning('分拣ID为空，无法生成标签');
      return;
    }
    const payload = buildSortIdLabelPayload(row);
    await openPdfLabelPreview(payload, `分拣ID标签预览（${sid}）`, '生成分拣ID标签失败');
  }, [isReadonly, buildSortIdLabelPayload, openPdfLabelPreview]);
  const printLocationLabel = React.useCallback(() => {
    if (isReadonly) return;
    const frame = locationLabelIframeRef.current;
    if (!frame || !frame.contentWindow) {
      message.warning('暂无可打印预览');
      return;
    }
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch {
      message.error('打印失败');
    }
  }, [isReadonly]);
  const downloadLocationLabel = React.useCallback(async () => {
    if (isReadonly) return;
    if (!locationLabelPayload) return;
    try {
      const resp = await request.post('/api/v1/storage/output-containers/labels/download/', locationLabelPayload, { responseType: 'blob' as any });
      const url = URL.createObjectURL(resp?.data as Blob);
      const a = document.createElement('a');
      const t = String(locationLabelPreviewTitle || 'location-label').replace(/[\\/:*?"<>|]+/g, '-');
      a.href = url;
      a.download = `${t}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      const text = String(e?.message || '').trim();
      message.error(text || '下载失败');
    }
  }, [isReadonly, locationLabelPayload, locationLabelPreviewTitle]);
  const currentSortIdWithQtyDisplay = React.useMemo(() => {
    const sid = String(currentSortIdDisplay || '').trim();
    const qty = Math.max(1, Number(currentSortQtyDisplay || 1));
    if (!sid || sid === '-') return '-';
    return `ID ${sid} × ${qty}件`;
  }, [currentSortIdDisplay, currentSortQtyDisplay]);
  const announceLocation = React.useCallback((locationNo: any) => {
    if (!speechSupported) return;
    const loc = String(locationNo ?? '').trim();
    if (!loc) return;
    const text = loc;
    speakTextLocal(text, {
      lang: speechLang,
      rate: 1,
      pitch: 1,
      volume: 1,
      interrupt: false,
    });
  }, [speechLang, speechSupported]);
  const normalizeSortMark = React.useCallback((v: any) => String(v ?? '').trim().toUpperCase(), []);
  const openEditQtyModal = React.useCallback((row: any) => {
    if (isReadonly) return;
    const id = String(row?.id || '').trim();
    if (!id) return;
    const qty = Math.max(1, Number(row?.qty || 1));
    setEditQtyRowId(id);
    setEditQtyValue(qty);
    setEditQtyModalOpen(true);
  }, [isReadonly]);
  const submitEditQty = React.useCallback(() => {
    if (isReadonly) return;
    const id = String(editQtyRowId || '').trim();
    if (!id) {
      setEditQtyModalOpen(false);
      return;
    }
    const qty = Math.max(1, Number(editQtyValue || 1));
    setOperationRows((prev) => (Array.isArray(prev) ? prev : []).map((r: any) => {
      if (String(r?.id || '') !== id) return r;
      return { ...r, qty };
    }));
    setEditQtyModalOpen(false);
    setEditQtyRowId('');
    message.success('数量已修改');
  }, [isReadonly, editQtyRowId, editQtyValue]);
  const removeOperationRow = React.useCallback((row: any) => {
    if (isReadonly) return;
    const id = String(row?.id || '').trim();
    if (!id) return;
    setOperationRows((prev) => (Array.isArray(prev) ? prev : []).filter((r: any) => String(r?.id || '') !== id));
  }, [isReadonly]);
  const openTempBoxModal = React.useCallback((locationNo: any) => {
    if (isReadonly) return;
    const loc = String(locationNo ?? '').trim();
    if (!loc) {
      message.warning('仓位为空，无法放入容器');
      return;
    }
    setTempBoxLocationNo(loc);
    setTempBoxSelectionMap({});
    setTempBoxCreateContainerOpen(false);
    setTempBoxContainerType('');
    setTempBoxContainerRemarks('');
    setTempBoxAddExistingOpen(false);
    setTempBoxAddExistingLoading(false);
    setTempBoxAddExistingSubmitting(false);
    setTempBoxAddExistingContainers([]);
    setTempBoxAddExistingSelectedContainerId(null);
    setTempBoxManualMatchOpen(false);
    setTempBoxManualMatchRows([]);
    setTempBoxManualMatchSelection({});
    setTempBoxPendingPayload(null);
    setTempBoxPendingAction('create');
    setTempBoxModalOpen(true);
  }, [isReadonly]);
  const ensureTempBoxContainerTypeChoices = React.useCallback(async () => {
    if ((tempBoxContainerTypeChoices || []).length > 0) return;
    try {
      setTempBoxContainerTypeLoading(true);
      const resp = await request.get('/api/v1/storage/containers/type-choices/', { params: { t: Date.now() } as any });
      const listRaw = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      const list = listRaw
        .map((x: any) => {
          const value = String(x?.value || '').trim();
          const rawLabel = String(x?.label || '').trim();
          const combined = rawLabel && value && rawLabel.toLowerCase() !== value.toLowerCase()
            ? `${rawLabel} (${value})`
            : (rawLabel || value);
          return { value, label: combined };
        })
        .filter((x: any) => !!x.value);
      const fallback = list.length > 0 ? list : [{ value: 'box', label: '箱子 (box)' }];
      setTempBoxContainerTypeChoices(fallback);
      const def = String(resp?.data?.default || '').trim();
      const boxValue = fallback.find((x: any) => String(x?.value || '').trim().toLowerCase() === 'box')?.value;
      setTempBoxContainerType((prev) => {
        if (prev && fallback.some((x: any) => x.value === prev)) return prev;
        if (boxValue) return String(boxValue);
        if (def && fallback.some((x: any) => x.value === def)) return def;
        return String(fallback?.[0]?.value || 'box');
      });
    } catch {
      const fallback = [{ value: 'box', label: '箱子 (box)' }];
      setTempBoxContainerTypeChoices(fallback);
      setTempBoxContainerType((prev) => prev || 'box');
    } finally {
      setTempBoxContainerTypeLoading(false);
    }
  }, [tempBoxContainerTypeChoices]);
  const openCreateTempContainerModal = React.useCallback(async () => {
    if (isReadonly) return;
    await ensureTempBoxContainerTypeChoices();
    setTempBoxContainerType((prev) => {
      if (String(prev || '').trim()) return prev;
      const list = Array.isArray(tempBoxContainerTypeChoices) ? tempBoxContainerTypeChoices : [];
      const boxValue = list.find((x: any) => String(x?.value || '').trim().toLowerCase() === 'box')?.value;
      if (boxValue) return String(boxValue);
      return String(list?.[0]?.value || 'box');
    });
    setTempBoxCreateContainerOpen(true);
  }, [isReadonly, ensureTempBoxContainerTypeChoices, tempBoxContainerTypeChoices]);
  const addSortMarkToInput = React.useCallback((mark: any) => {
    if (isReadonly) return;
    const txt = String(mark ?? '').trim();
    if (!txt) return;
    setSortInputValue(txt);
    try { sortInputRef.current?.focus?.(); } catch {}
  }, [isReadonly]);
  const currentEditingOperationRow = React.useMemo(() => {
    const id = String(editQtyRowId || '').trim();
    if (!id) return null;
    const rows = Array.isArray(operationRows) ? operationRows : [];
    return rows.find((r: any) => String(r?.id || '') === id) || null;
  }, [editQtyRowId, operationRows]);
  const doClearSortAndCountDraft = React.useCallback(() => {
    if (isReadonly) return;
    setSortDbRows([]);
    setOperationRows([]);
    setBoxedQtyByLocationSort({});
    setGroupListPreviewRows((prev) => (Array.isArray(prev) ? prev : []).map((r: any) => {
      if (!Object.prototype.hasOwnProperty.call(r || {}, '__assigned_location_placeholder')) return r;
      return { ...r, __assigned_location_placeholder: '' };
    }));
    setCurrentInputMark('');
    setCurrentSortIdDisplay('-');
    setCurrentSortQtyDisplay(1);
    setCurrentLocationDisplay('-');
    setSortInputValue('');
    setSortInputQty(1);
    setSortDbMarkFilter('');
    setSortDbLocationFilter('');
    setSortDbPage(1);
    setOperationPage(1);
    setStatsMarkPage(1);
    setStatsLocationPage(1);
    try { sortInputRef.current?.focus?.(); } catch {}
    if (draftStorageKey) {
      try { window.localStorage.removeItem(draftStorageKey); } catch {}
    }
    message.success('草稿已清空，已从头开始分拣流程');
  }, [isReadonly, draftStorageKey]);
  const clearSortAndCountDraft = React.useCallback(() => {
    if (isReadonly) return;
    if (Array.isArray(generatedDraftContainers) && generatedDraftContainers.length > 0) {
      message.warning('已存在装入容器记录，不能清空草稿');
      return;
    }
    Modal.confirm({
      title: '确认清空草稿',
      content: '清空后将移除分拣数据库与仓位表、操作记录和统计信息，是否继续？',
      okText: '确认清空',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: doClearSortAndCountDraft,
    });
  }, [isReadonly, generatedDraftContainers, doClearSortAndCountDraft]);
  const handleSortInputSubmit = React.useCallback(() => {
    if (isReadonly) return;
    const resetInputForNext = () => {
      setSortInputValue('');
      setSortInputQty(1);
      setTimeout(() => {
        try { sortInputRef.current?.focus?.(); } catch {}
      }, 0);
    };
    const liveInputVal = String(sortInputRef.current?.input?.value ?? '').trim();
    const raw = liveInputVal || String(sortInputValue || '').trim();
    if (!raw) {
      message.warning('请输入分拣标识');
      try { sortInputRef.current?.focus?.(); } catch {}
      return;
    }
    const cmp = normalizeSortMark(raw);
    let effectiveRaw = raw;
    let effectiveCmp = cmp;
    const submitQty = Math.max(1, Number(sortInputQty || 1));
    const rowsNow = Array.isArray(sortDbRows) ? sortDbRows : [];
    const maxSortIdNow = rowsNow.reduce((m, r) => {
      const v = Number(r?.sort_id || 0);
      return Number.isFinite(v) ? Math.max(m, v) : m;
    }, 0);
    const maxLocationNoNow = rowsNow.reduce((m, r) => {
      const v = Number(r?.location_no || 0);
      return Number.isFinite(v) ? Math.max(m, v) : m;
    }, 0);
    const targetRowsNow = Array.isArray(snapshotInstructionTargetRows) ? snapshotInstructionTargetRows : [];
    const targetBySortLoc = new Map<string, any>();
    const targetBySortId = new Map<string, any[]>();
    const targetByMark = new Map<string, any[]>();
    targetRowsNow.forEach((t: any) => {
      if (!t || typeof t !== 'object') return;
      const snap = t?.target?.snapshot || t?.snapshot || {};
      const iuId = Number(
        t?.inventory_unit_id
        ?? t?.target_inventory_unit_id
        ?? t?.iu_id
        ?? snap?.inventory_unit_id
        ?? 0,
      ) || 0;
      const iuCode = String(
        t?.inventory_unit_system_barcode
        || t?.inventory_unit_code
        || t?.target_code
        || snap?.system_barcode
        || snap?.system_code
        || '',
      ).trim();
      if (iuId <= 0 && !iuCode) return;
      const sortId = String(t?.sort_id ?? t?.sortid ?? '').trim();
      const locationNo = String(t?.location_no ?? t?.location ?? '').trim();
      const markNorm = normalizeSortMark(t?.sorting_mark || t?.mark || '');
      const item = { ...t, __resolved_iu_id: iuId, __resolved_iu_code: iuCode };
      if (sortId && locationNo) {
        targetBySortLoc.set(`${locationNo}__${sortId}`, item);
      }
      if (sortId) {
        const arr = targetBySortId.get(sortId) || [];
        arr.push(item);
        targetBySortId.set(sortId, arr);
      }
      if (markNorm) {
        const arr = targetByMark.get(markNorm) || [];
        arr.push(item);
        targetByMark.set(markNorm, arr);
      }
    });
    const getTargetUnitType = (target: any): string => {
      const snap = target?.target?.snapshot || target?.snapshot || {};
      const direct = String(
        target?.unit_type
        || target?.unit_type_display
        || snap?.unit_type
        || snap?.unit_type_display
        || '',
      ).trim().toUpperCase();
      if (direct === 'BIU' || direct.includes('箱库存单元')) return 'BIU';
      if (direct === 'LIU' || direct.includes('散货库存单元')) return 'LIU';
      if (direct === 'PIU' || direct.includes('托盘库存单元')) return 'PIU';
      const code = normalizeSortMark(
        target?.inventory_unit_system_barcode
        || target?.inventory_unit_code
        || target?.target_code
        || snap?.system_barcode
        || snap?.system_code
        || '',
      );
      if (code.startsWith('BIU')) return 'BIU';
      if (code.startsWith('LIU')) return 'LIU';
      if (code.startsWith('PIU')) return 'PIU';
      return '';
    };
    const getTargetUnitCode = (target: any): string => {
      const snap = target?.target?.snapshot || target?.snapshot || {};
      return String(
        target?.inventory_unit_system_barcode
        || target?.inventory_unit_code
        || target?.target_code
        || snap?.system_barcode
        || snap?.system_code
        || '',
      ).trim();
    };
    const inventoryTargets = targetRowsNow.filter((t: any) => String(t?.target_type || 'inventory_unit').trim() === 'inventory_unit');
    const biuTargets = inventoryTargets.filter((t: any) => getTargetUnitType(t) === 'BIU');
    const isNeed1BiuMode = sortCountDemandMode === 'need1' && inventoryTargets.length > 0 && biuTargets.length === inventoryTargets.length;
    if (isNeed1BiuMode) {
      const scanCmp = normalizeSortMark(raw);
      let matchedTarget: any | null = null;
      const directCodeMatches = biuTargets.filter((t: any) => normalizeSortMark(getTargetUnitCode(t)) === scanCmp);
      if (directCodeMatches.length > 0) {
        matchedTarget = directCodeMatches[0];
      } else {
        const barcodeMatches = biuTargets.filter((t: any) => {
          const items = extractInstructionTargetContentItems(t);
          if (!Array.isArray(items) || items.length <= 0) return false;
          return items.some((it: any) => {
            const rawCode = String(it?.barcode || '').trim();
            if (!rawCode) return false;
            const tokens = rawCode.split(/[\/,，;\s]+/).map((x) => normalizeSortMark(x)).filter(Boolean);
            return tokens.includes(scanCmp);
          });
        });
        if (barcodeMatches.length === 1) {
          matchedTarget = barcodeMatches[0];
        } else if (barcodeMatches.length > 1) {
          const candidates = barcodeMatches.map((t: any) => {
            const code = getTargetUnitCode(t);
            const uid = Number(
              t?.inventory_unit_id
              ?? t?.target_inventory_unit_id
              ?? t?.iu_id
              ?? t?.target?.snapshot?.inventory_unit_id
              ?? t?.snapshot?.inventory_unit_id
              ?? 0,
            ) || 0;
            const sgCode = String(
              t?.sorting_group_info?.system_code
              || t?.sorting_group_code
              || t?.target?.snapshot?.sorting_group_info?.system_code
              || t?.snapshot?.sorting_group_info?.system_code
              || '',
            ).trim();
            const sgName = String(
              t?.sorting_group_info?.name
              || t?.sorting_group_name
              || t?.target?.snapshot?.sorting_group_info?.name
              || t?.snapshot?.sorting_group_info?.name
              || '',
            ).trim();
            const rawItems = extractInstructionTargetContentItems(t) as any[];
            const itemLines = (Array.isArray(rawItems) ? rawItems : []).map((it: any) => {
              const productName = String(it?.productName || '').trim();
              const customCode = String(it?.customCode || '').trim();
              const barcode = String(it?.barcode || '').trim();
              const meta = [customCode, barcode].filter(Boolean).join(' ');
              let head = '-';
              if (productName && meta) head = `${productName}(${meta})`;
              else if (productName) head = productName;
              else if (meta) head = meta;
              return `${head} × ${Math.max(0, Number(it?.qty || 0))}`;
            }).filter((x: any) => String(x || '').trim());
            return {
              key: `${code || '-'}::${uid || 0}`,
              code,
              inventory_unit_id: uid > 0 ? uid : undefined,
              sorting_group_text: sgCode && sgName ? `${sgCode} (${sgName})` : (sgCode || sgName || '-'),
              content_items_lines: itemLines,
              label: uid > 0 ? `${code || '-'} (IU:${uid})` : (code || '-'),
            };
          }).filter((x: any) => String(x?.code || '').trim());
          setNeed1BiuMatchPickerCandidates(candidates);
          setNeed1BiuMatchPickerSelectedCode(String(candidates?.[0]?.code || '').trim());
          setNeed1BiuMatchPendingScanText(String(raw || '').trim());
          setNeed1BiuMatchPendingQty(Math.max(1, Number(submitQty || 1)));
          setNeed1BiuMatchPickerOpen(true);
          return;
        } else {
          Modal.warning({
            title: '无法匹配源BIU',
            content: `扫描值 ${String(raw || '-')}: 未匹配到任何操作目标BIU的系统编码或内容项分拣条码，请检查后重试。`,
            okText: '关闭',
          });
          return;
        }
      }
      const matchedCode = String(getTargetUnitCode(matchedTarget) || '').trim();
      if (!matchedCode) {
        Modal.warning({
          title: '无法匹配源BIU',
          content: `扫描值 ${String(raw || '-')}: 命中目标BIU但缺少系统编码，无法继续。`,
          okText: '关闭',
        });
        return;
      }
      effectiveRaw = matchedCode;
      effectiveCmp = normalizeSortMark(matchedCode);
    }
    const resolveTargetIu = (target: any, markText: string) => {
      const snap = target?.target?.snapshot || target?.snapshot || {};
      let iuId = Number(
        target?.inventory_unit_id
        ?? target?.target_inventory_unit_id
        ?? target?.iu_id
        ?? snap?.inventory_unit_id
        ?? 0,
      ) || 0;
      let iuCode = String(
        target?.inventory_unit_system_barcode
        || target?.inventory_unit_code
        || target?.target_code
        || snap?.system_barcode
        || snap?.system_code
        || '',
      ).trim();
      if (iuId > 0 || iuCode) return { iuId, iuCode };
      const sid = String(target?.sort_id ?? target?.sortid ?? '').trim();
      const loc = String(target?.location_no ?? target?.location ?? '').trim();
      if (sid && loc) {
        const hit = targetBySortLoc.get(`${loc}__${sid}`);
        if (hit) {
          iuId = Number(hit?.__resolved_iu_id || 0) || 0;
          iuCode = String(hit?.__resolved_iu_code || '').trim();
          if (iuId > 0 || iuCode) return { iuId, iuCode };
        }
      }
      if (sid) {
        const arr = (targetBySortId.get(sid) || []).filter((x: any) => Number(x?.__resolved_iu_id || 0) > 0 || String(x?.__resolved_iu_code || '').trim());
        if (arr.length === 1) {
          iuId = Number(arr[0]?.__resolved_iu_id || 0) || 0;
          iuCode = String(arr[0]?.__resolved_iu_code || '').trim();
          if (iuId > 0 || iuCode) return { iuId, iuCode };
        }
      }
      const markNorm = normalizeSortMark(markText || target?.sorting_mark || target?.mark || '');
      if (markNorm) {
        const arr = (targetByMark.get(markNorm) || []).filter((x: any) => Number(x?.__resolved_iu_id || 0) > 0 || String(x?.__resolved_iu_code || '').trim());
        if (arr.length === 1) {
          iuId = Number(arr[0]?.__resolved_iu_id || 0) || 0;
          iuCode = String(arr[0]?.__resolved_iu_code || '').trim();
        }
      }
      return { iuId, iuCode };
    };
    const appendOperation = (target: any, markText: string, qtyVal: number) => {
      const { iuId: resolvedIuId, iuCode: resolvedCode } = resolveTargetIu(target, markText);
      // 反向回填分拣数据库行的 IU 映射，避免终审依赖 operation_targets 唯一链路。
      setSortDbRows((prev) => (Array.isArray(prev) ? prev : []).map((row: any) => {
        const sameMark = normalizeSortMark(row?.sorting_mark) === normalizeSortMark(markText);
        const sameSort = String(row?.sort_id ?? '') === String(target?.sort_id ?? '');
        const sameLoc = String(row?.location_no ?? '') === String(target?.location_no ?? '');
        if (!sameMark && !(sameSort && sameLoc)) return row;
        const rowIuId = Number(row?.inventory_unit_id ?? row?.target_inventory_unit_id ?? 0) || 0;
        if (rowIuId > 0 && String(row?.inventory_unit_code || row?.target_code || '').trim()) return row;
        return {
          ...row,
          inventory_unit_id: resolvedIuId > 0 ? resolvedIuId : (row?.inventory_unit_id ?? 0),
          target_inventory_unit_id: resolvedIuId > 0 ? resolvedIuId : (row?.target_inventory_unit_id ?? 0),
          inventory_unit_code: resolvedCode || row?.inventory_unit_code || '',
          target_code: resolvedCode || row?.target_code || '',
        };
      }));
      setOperationRows((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        {
          id: `op-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          seq: (Array.isArray(prev) ? prev : []).reduce((m: number, r: any) => Math.max(m, Number(r?.seq || 0)), 0) + 1,
          location_no: target?.location_no,
          group_code: String(target?.group_code || ''),
          sort_id: target?.sort_id,
          qty: qtyVal,
          sorting_mark: markText,
          inventory_unit_id: resolvedIuId,
          target_inventory_unit_id: resolvedIuId,
          inventory_unit_code: resolvedCode,
          target_code: resolvedCode,
          preset_group_no: String(target?.preset_group_no || ''),
          time_text: formatNowText(),
        },
      ]);
    };
    const hit = rowsNow.find((r) => normalizeSortMark(r?.sorting_mark) === effectiveCmp);
    if (hit) {
      setCurrentInputMark(effectiveRaw);
      setCurrentSortIdDisplay(String(hit.sort_id));
      setCurrentSortQtyDisplay(submitQty);
      setCurrentLocationDisplay(String(hit.location_no));
      announceLocation(hit.location_no);
      appendOperation(hit, effectiveRaw, submitQty);
      resetInputForNext();
      return;
    }
    if (currentSortModeLabel === '分组-简化模式') {
      const entries = Array.isArray(presetGroupListEntries) ? presetGroupListEntries : [];
      const hitPreset = entries.find((x) => x.markNorm === effectiveCmp);
      if (hitPreset) {
        const sameGroup = entries.filter((x) => x.groupNo === hitPreset.groupNo);
        const nextLocationNo = maxLocationNoNow + 1;
        const maxGroupNoNow = rowsNow.reduce((m, r) => {
          const g = String(r?.group_code || '').trim().toUpperCase();
          const n = Number((g.match(/^G(\d+)$/)?.[1] || 0));
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, 0);
        const nextGroupCode = `G${maxGroupNoNow + 1}`;
        let nextSortId = maxSortIdNow + 1;
        const existedMarkSet = new Set(rowsNow.map((r) => normalizeSortMark(r?.sorting_mark)));
        const newRows: any[] = [];
        sameGroup.forEach((item) => {
          if (!item.markNorm) return;
          if (existedMarkSet.has(item.markNorm)) return;
          const resolved = resolveTargetIu({
            sort_id: nextSortId,
            location_no: nextLocationNo,
            sorting_mark: item.markRaw || item.markNorm,
          }, item.markRaw || item.markNorm);
          newRows.push({
            id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${newRows.length}`,
            location_no: nextLocationNo,
            group_code: nextGroupCode,
            sort_id: nextSortId++,
            sorting_mark: item.markRaw || item.markNorm,
            preset_group_no: hitPreset.groupNo,
            inventory_unit_id: resolved.iuId > 0 ? resolved.iuId : undefined,
            target_inventory_unit_id: resolved.iuId > 0 ? resolved.iuId : undefined,
            inventory_unit_code: resolved.iuCode || undefined,
            target_code: resolved.iuCode || undefined,
          });
        });
        if (newRows.length > 0) {
          setSortDbRows((prev) => [...(Array.isArray(prev) ? prev : []), ...newRows]);
        }
        setGroupListPreviewRows((prev) => (Array.isArray(prev) ? prev : []).map((r: any) => {
          const g = String(r?.[groupListGroupNoDataIndex] ?? '').trim();
          if (g !== hitPreset.groupNo) return r;
          return { ...r, __assigned_location_placeholder: String(nextLocationNo) };
        }));
        const target = newRows.find((r: any) => normalizeSortMark(r?.sorting_mark) === effectiveCmp)
          || rowsNow.find((r: any) => normalizeSortMark(r?.sorting_mark) === effectiveCmp)
          || null;
        if (target) {
          setCurrentInputMark(effectiveRaw);
          setCurrentSortIdDisplay(String(target.sort_id));
          setCurrentSortQtyDisplay(submitQty);
          setCurrentLocationDisplay(String(target.location_no));
          announceLocation(target.location_no);
          appendOperation(target, effectiveRaw, submitQty);
          resetInputForNext();
          return;
        }
      }
      const othersLocationText = String(
        (rowsNow.find((r: any) => String(r?.group_code || '').trim().toUpperCase() === 'G-OTHERS')?.location_no) ?? '',
      ).trim();
      const assignedOthersLocation = othersLocationText ? Number(othersLocationText) : (maxLocationNoNow + 1);
      const resolvedOthers = resolveTargetIu({
        sort_id: maxSortIdNow + 1,
        location_no: assignedOthersLocation,
        sorting_mark: effectiveRaw,
      }, effectiveRaw);
      const nextRow = {
        id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        location_no: assignedOthersLocation,
        group_code: 'G-OTHERS',
        sort_id: maxSortIdNow + 1,
        sorting_mark: effectiveRaw,
        preset_group_no: '',
        inventory_unit_id: resolvedOthers.iuId > 0 ? resolvedOthers.iuId : undefined,
        target_inventory_unit_id: resolvedOthers.iuId > 0 ? resolvedOthers.iuId : undefined,
        inventory_unit_code: resolvedOthers.iuCode || undefined,
        target_code: resolvedOthers.iuCode || undefined,
      };
      setSortDbRows((prev) => [...(Array.isArray(prev) ? prev : []), nextRow]);
      setCurrentInputMark(effectiveRaw);
      setCurrentSortIdDisplay(String(nextRow.sort_id));
      setCurrentSortQtyDisplay(submitQty);
      setCurrentLocationDisplay(String(nextRow.location_no));
      announceLocation(nextRow.location_no);
      appendOperation(nextRow, effectiveRaw, submitQty);
      resetInputForNext();
      return;
    }
    if (currentSortModeLabel === '分组-细分模式') {
      const entries = Array.isArray(presetGroupListEntries) ? presetGroupListEntries : [];
      const hitPreset = entries.find((x) => x.markNorm === effectiveCmp);
      if (hitPreset) {
        const sameGroup = entries.filter((x) => x.groupNo === hitPreset.groupNo);
        const nextLocationNo = maxLocationNoNow + 1;
        const maxGroupNoNow = rowsNow.reduce((m, r) => {
          const g = String(r?.group_code || '').trim().toUpperCase();
          const n = Number((g.match(/^G(\d+)$/)?.[1] || 0));
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, 0);
        const nextGroupCode = `G${maxGroupNoNow + 1}`;
        let nextSortId = maxSortIdNow + 1;
        const existedMarkSet = new Set(rowsNow.map((r) => normalizeSortMark(r?.sorting_mark)));
        const newRows: any[] = [];
        sameGroup.forEach((item) => {
          if (!item.markNorm) return;
          if (existedMarkSet.has(item.markNorm)) return;
          const resolved = resolveTargetIu({
            sort_id: nextSortId,
            location_no: nextLocationNo,
            sorting_mark: item.markRaw || item.markNorm,
          }, item.markRaw || item.markNorm);
          newRows.push({
            id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${newRows.length}`,
            location_no: nextLocationNo,
            group_code: nextGroupCode,
            sort_id: nextSortId++,
            sorting_mark: item.markRaw || item.markNorm,
            preset_group_no: hitPreset.groupNo,
            inventory_unit_id: resolved.iuId > 0 ? resolved.iuId : undefined,
            target_inventory_unit_id: resolved.iuId > 0 ? resolved.iuId : undefined,
            inventory_unit_code: resolved.iuCode || undefined,
            target_code: resolved.iuCode || undefined,
          });
        });
        if (newRows.length > 0) {
          setSortDbRows((prev) => [...(Array.isArray(prev) ? prev : []), ...newRows]);
        }
        setGroupListPreviewRows((prev) => (Array.isArray(prev) ? prev : []).map((r: any) => {
          const g = String(r?.[groupListGroupNoDataIndex] ?? '').trim();
          if (g !== hitPreset.groupNo) return r;
          return { ...r, __assigned_location_placeholder: String(nextLocationNo) };
        }));
        const target = newRows.find((r: any) => normalizeSortMark(r?.sorting_mark) === effectiveCmp)
          || rowsNow.find((r: any) => normalizeSortMark(r?.sorting_mark) === effectiveCmp)
          || null;
        if (target) {
          setCurrentInputMark(effectiveRaw);
          setCurrentSortIdDisplay(String(target.sort_id));
          setCurrentSortQtyDisplay(submitQty);
          setCurrentLocationDisplay(String(target.location_no));
          announceLocation(target.location_no);
          appendOperation(target, effectiveRaw, submitQty);
          resetInputForNext();
          return;
        }
      }
      const maxGroupNoNow = rowsNow.reduce((m, r) => {
        const g = String(r?.group_code || '').trim().toUpperCase();
        const n = Number((g.match(/^G(\d+)$/)?.[1] || 0));
        return Number.isFinite(n) ? Math.max(m, n) : m;
      }, 0);
      const nextRow = {
        id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        location_no: maxLocationNoNow + 1,
        group_code: `G${maxGroupNoNow + 1}`,
        sort_id: maxSortIdNow + 1,
        sorting_mark: effectiveRaw,
        preset_group_no: '',
        ...(() => {
          const resolved = resolveTargetIu({
            sort_id: maxSortIdNow + 1,
            location_no: maxLocationNoNow + 1,
            sorting_mark: effectiveRaw,
          }, effectiveRaw);
          return {
            inventory_unit_id: resolved.iuId > 0 ? resolved.iuId : undefined,
            target_inventory_unit_id: resolved.iuId > 0 ? resolved.iuId : undefined,
            inventory_unit_code: resolved.iuCode || undefined,
            target_code: resolved.iuCode || undefined,
          };
        })(),
      };
      setSortDbRows((prev) => [...(Array.isArray(prev) ? prev : []), nextRow]);
      setCurrentInputMark(effectiveRaw);
      setCurrentSortIdDisplay(String(nextRow.sort_id));
      setCurrentSortQtyDisplay(submitQty);
      setCurrentLocationDisplay(String(nextRow.location_no));
      announceLocation(nextRow.location_no);
      appendOperation(nextRow, effectiveRaw, submitQty);
      resetInputForNext();
      return;
    }
    if (currentSortModeLabel !== '标准模式') {
      message.info('当前仅已实现标准模式、分组-简化模式与分组-细分模式分拣逻辑');
      resetInputForNext();
      return;
    }
    const maxGroupNo = rowsNow.reduce((m, r) => {
      const g = String(r?.group_code || '').trim().toUpperCase();
      const n = Number((g.match(/^G(\d+)$/)?.[1] || 0));
      return Number.isFinite(n) ? Math.max(m, n) : m;
    }, 0);
    const nextSortId = maxSortIdNow + 1;
    const nextLocationNo = maxLocationNoNow + 1;
    const nextGroupNo = maxGroupNo + 1;
    const nextRow = {
      id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      location_no: nextLocationNo,
      group_code: `G${nextGroupNo}`,
      sort_id: nextSortId,
      sorting_mark: effectiveRaw,
      preset_group_no: '',
    };
    setSortDbRows((prev) => [...(Array.isArray(prev) ? prev : []), nextRow]);
    setCurrentInputMark(effectiveRaw);
    setCurrentSortIdDisplay(String(nextSortId));
    setCurrentSortQtyDisplay(submitQty);
    setCurrentLocationDisplay(String(nextLocationNo));
    announceLocation(nextLocationNo);
    appendOperation(nextRow, effectiveRaw, submitQty);
    resetInputForNext();
  }, [
    sortInputValue,
    currentSortModeLabel,
    sortDbRows,
    normalizeSortMark,
    sortInputQty,
    isReadonly,
    formatNowText,
    announceLocation,
    sortCountDemandMode,
    presetGroupListEntries,
    groupListGroupNoDataIndex,
    snapshotInstructionTargetRows,
    extractInstructionTargetContentItems,
    formatInventoryUnitContentItemsText,
  ]);
  const sortDbColumns = React.useMemo(() => ([
    {
      title: '仓位',
      dataIndex: 'location_no',
      key: 'location_no',
      width: 100,
      align: 'center' as const,
      render: (v: any) => renderLocationBadge(v),
    },
    {
      title: '分拣标识',
      dataIndex: 'sorting_mark',
      key: 'sorting_mark',
      width: 240,
      align: 'center' as const,
      render: (v: any) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
          {renderSortMarkBadge(v)}
          <Tooltip title="添加到操作区域扫描框">
            <Button
              size="small"
              type="text"
                  icon={<SwapLeftOutlined style={{ color: '#1677ff' }} />}
              disabled={isReadonly}
              onClick={() => addSortMarkToInput(v)}
                  style={{ paddingInline: 4 }}
            />
          </Tooltip>
        </span>
      ),
    },
    {
      title: '分拣组',
      dataIndex: 'group_code',
      key: 'group_code',
      width: 90,
      align: 'center' as const,
      render: (v: any) => String(v || '-'),
    },
    {
      title: '分拣ID',
      dataIndex: 'sort_id',
      key: 'sort_id',
      width: 90,
      align: 'center' as const,
      render: (v: any) => String(v || '-'),
    },
    {
      title: '预设分组编号',
      dataIndex: 'preset_group_no',
      key: 'preset_group_no',
      width: 100,
      align: 'center' as const,
      render: (v: any) => String(v || '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 190,
      align: 'center' as const,
      render: (_: any, row: any) => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button size="small" disabled={isReadonly} onClick={() => openLocationLabelPreview(row)}>
            打印仓位
          </Button>
          <Button size="small" disabled={isReadonly} onClick={() => openSortIdLabelPreview(row)}>
            打印ID
          </Button>
        </div>
      ),
    },
  ]), [renderLocationBadge, renderSortMarkBadge, openLocationLabelPreview, openSortIdLabelPreview, addSortMarkToInput, isReadonly]);
  const filteredSortDbRows = React.useMemo(() => {
    const markKw = String(sortDbMarkFilter || '').trim().toUpperCase();
    const locationKw = String(sortDbLocationFilter || '').trim().toUpperCase();
    const rows = Array.isArray(sortDbRows) ? sortDbRows : [];
    const filtered = rows.filter((r) => {
      const markVal = String(r?.sorting_mark || '').trim().toUpperCase();
      const locationVal = String(r?.location_no ?? '').trim().toUpperCase();
      if (markKw && !markVal.includes(markKw)) return false;
      if (locationKw && !locationVal.includes(locationKw)) return false;
      return true;
    });
    return [...filtered].sort((a: any, b: any) => {
      const la = Number(a?.location_no || 0);
      const lb = Number(b?.location_no || 0);
      if (la !== lb) return la - lb;
      const sa = Number(a?.sort_id || 0);
      const sb = Number(b?.sort_id || 0);
      return sa - sb;
    });
  }, [sortDbRows, sortDbMarkFilter, sortDbLocationFilter]);
  const operationRowsDesc = React.useMemo(() => {
    const rows = Array.isArray(operationRows) ? [...operationRows] : [];
    rows.sort((a: any, b: any) => Number(b?.seq || 0) - Number(a?.seq || 0));
    return rows;
  }, [operationRows]);
  const statsByMarkRows = React.useMemo(() => {
    const map = new Map<string, { sorting_mark: string; sort_id: string; location_no: string; operation_count: number; total_qty: number; last_time: string }>();
    (Array.isArray(operationRowsDesc) ? operationRowsDesc : []).forEach((r: any) => {
      const key = String(r?.sorting_mark || '').trim().toUpperCase();
      if (!key) return;
      const prev = map.get(key) || {
        sorting_mark: key,
        sort_id: String(r?.sort_id ?? '').trim(),
        location_no: String(r?.location_no ?? '').trim(),
        operation_count: 0,
        total_qty: 0,
        last_time: '',
      };
      if (!prev.sort_id) prev.sort_id = String(r?.sort_id ?? '').trim();
      if (!prev.location_no) prev.location_no = String(r?.location_no ?? '').trim();
      prev.operation_count += 1;
      prev.total_qty += Math.max(0, Number(r?.qty || 0));
      if (!prev.last_time) prev.last_time = String(r?.time_text || '');
      map.set(key, prev);
    });
    return Array.from(map.values())
      .sort((a: any, b: any) => {
        const sa = Number(a?.sort_id || 0);
        const sb = Number(b?.sort_id || 0);
        if (sa !== sb) return sa - sb;
        return String(a?.sorting_mark || '').localeCompare(String(b?.sorting_mark || ''));
      })
      .map((x, idx) => ({ ...x, __idx: idx + 1 }));
  }, [operationRowsDesc]);
  const filteredStatsByMarkRows = React.useMemo(() => {
    const kw = String(statsMarkFilter || '').trim().toUpperCase();
    if (!kw) return statsByMarkRows;
    return (Array.isArray(statsByMarkRows) ? statsByMarkRows : []).filter((r: any) =>
      String(r?.sorting_mark || '').trim().toUpperCase().includes(kw),
    );
  }, [statsByMarkRows, statsMarkFilter]);
  const statsByLocationGroups = React.useMemo(() => {
    const sortedQtyMap = new Map<string, number>(); // key: location__sortId
    const sortMarkMap = new Map<string, string>();  // key: location__sortId -> sorting_mark
    const locationLastTime = new Map<string, string>();
    (Array.isArray(operationRowsDesc) ? operationRowsDesc : []).forEach((r: any) => {
      const loc = String(r?.location_no ?? '').trim();
      const sid = String(r?.sort_id ?? '').trim();
      if (!loc || !sid) return;
      const key = `${loc}__${sid}`;
      const qty = Math.max(0, Number(r?.qty || 0));
      sortedQtyMap.set(key, (sortedQtyMap.get(key) || 0) + qty);
      if (!sortMarkMap.get(key)) {
        sortMarkMap.set(key, String(r?.sorting_mark || '').trim());
      }
      if (!locationLastTime.get(loc)) {
        locationLastTime.set(loc, String(r?.time_text || ''));
      }
    });
    const map = new Map<string, {
      location_no: string;
      total_qty: number;
      last_time: string;
      mark_map: Map<string, { sorting_mark: string; total_qty: number }>;
    }>();
    (Array.isArray(sortDbRows) ? sortDbRows : []).forEach((r: any) => {
      const loc = String(r?.location_no ?? '').trim();
      const sid = String(r?.sort_id ?? '').trim();
      if (!loc || !sid) return;
      const key = `${loc}__${sid}`;
      const sortedQty = Math.max(0, Number(sortedQtyMap.get(key) || 0));
      const boxedQty = Math.max(0, Number((boxedQtyByLocationSort || {})[key] || 0));
      const currentQty = Math.max(0, sortedQty - boxedQty);
      if (currentQty <= 0) return;
      const prev = map.get(loc) || {
        location_no: loc,
        total_qty: 0,
        last_time: String(locationLastTime.get(loc) || ''),
        mark_map: new Map<string, { sorting_mark: string; total_qty: number }>(),
      };
      prev.total_qty += currentQty;
      const markText = String(sortMarkMap.get(key) || r?.sorting_mark || '').trim() || '-';
      const markKey = markText.toUpperCase();
      const markPrev = prev.mark_map.get(markKey) || { sorting_mark: markText, total_qty: 0 };
      markPrev.total_qty += currentQty;
      prev.mark_map.set(markKey, markPrev);
      map.set(loc, prev);
    });
    return Array.from(map.values())
      .sort((a, b) => Number(a.location_no || 0) - Number(b.location_no || 0))
      .map((g, idx) => {
        const mark_rows = Array.from(g.mark_map.values())
          .sort((a, b) => String(a.sorting_mark || '').localeCompare(String(b.sorting_mark || '')));
        return {
          __idx: idx + 1,
          __groupKey: g.location_no,
          __groupColor: idx % 2 === 0 ? '#f6ffed' : '#e6f7ff',
          location_no: g.location_no,
          total_qty: g.total_qty,
          last_time: g.last_time,
          mark_rows,
        };
      });
  }, [operationRowsDesc, sortDbRows, boxedQtyByLocationSort]);
  const pagedStatsByLocationGroups = React.useMemo(() => {
    const page = Math.max(1, Number(statsLocationPage || 1));
    const size = Math.max(1, Number(statsLocationPageSize || 10));
    const start = (page - 1) * size;
    return (Array.isArray(statsByLocationGroups) ? statsByLocationGroups : []).slice(start, start + size);
  }, [statsByLocationGroups, statsLocationPage, statsLocationPageSize]);
  const statsByLocationRows = React.useMemo(() => {
    const rows: any[] = [];
    (Array.isArray(pagedStatsByLocationGroups) ? pagedStatsByLocationGroups : []).forEach((g: any) => {
      const markRows = Array.isArray(g?.mark_rows) && g.mark_rows.length > 0
        ? g.mark_rows
        : [{ sorting_mark: '-', total_qty: 0 }];
      const span = markRows.length;
      markRows.forEach((m: any, i: number) => {
        rows.push({
          __rowKey: `${String(g?.__groupKey || 'g')}-${i}`,
          __idx: g.__idx,
          __isGroupFirst: i === 0,
          __groupRowSpan: span,
          __groupColor: g.__groupColor,
          location_no: g.location_no,
          total_qty: g.total_qty,
          last_time: g.last_time,
          sorting_mark: m.sorting_mark,
          mark_qty: m.total_qty,
        });
      });
    });
    return rows;
  }, [pagedStatsByLocationGroups]);
  const tempBoxLocationRows = React.useMemo(() => {
    const loc = String(tempBoxLocationNo || '').trim();
    const rows = Array.isArray(sortDbRows) ? sortDbRows : [];
    const ops = Array.isArray(operationRows) ? operationRows : [];
    if (!loc) return [] as any[];
    const sortedQtyMap = new Map<string, number>();
    ops.forEach((op: any) => {
      const opLoc = String(op?.location_no ?? '').trim();
      if (opLoc !== loc) return;
      const sid = String(op?.sort_id ?? '').trim();
      if (!sid) return;
      const qty = Math.max(0, Number(op?.qty || 0));
      if (!Number.isFinite(qty) || qty <= 0) return;
      sortedQtyMap.set(sid, (sortedQtyMap.get(sid) || 0) + qty);
    });
    return rows
      .filter((r: any) => String(r?.location_no ?? '').trim() === loc)
      .sort((a: any, b: any) => Number(a?.sort_id || 0) - Number(b?.sort_id || 0))
      .map((r: any, idx: number) => {
        const sid = String(r?.sort_id ?? '').trim();
        const sortedQty = Math.max(0, Number(sortedQtyMap.get(sid) || 0));
        const boxedKey = `${loc}__${sid}`;
        const boxedQty = Math.max(0, Number((boxedQtyByLocationSort || {})[boxedKey] || 0));
        const currentQty = Math.max(0, sortedQty - boxedQty);
        return {
          __idx: idx + 1,
          sorted_qty: currentQty,
          ...r,
        };
      })
      .filter((r: any) => Number(r?.sorted_qty || 0) > 0)
      .map((r: any, idx: number) => ({
        __idx: idx + 1,
        ...r,
      }));
  }, [sortDbRows, operationRows, tempBoxLocationNo, boxedQtyByLocationSort]);
  const getTempBoxRowKey = React.useCallback((row: any, idx?: number) => String(row?.id || row?.sort_id || row?.__idx || idx || 0), []);
  React.useEffect(() => {
    if (!tempBoxModalOpen) return;
    const nextMap: Record<string, { checked: boolean; qty: number }> = {};
    (Array.isArray(tempBoxLocationRows) ? tempBoxLocationRows : []).forEach((row: any, idx: number) => {
      const key = getTempBoxRowKey(row, idx);
      const sortedQty = Math.max(0, Number(row?.sorted_qty || 0));
      nextMap[key] = {
        checked: sortedQty > 0,
        qty: sortedQty,
      };
    });
    setTempBoxSelectionMap(nextMap);
    setTempBoxPage(1);
  }, [tempBoxModalOpen, tempBoxLocationRows, getTempBoxRowKey]);
  const tempBoxStats = React.useMemo(() => {
    const rows = Array.isArray(tempBoxLocationRows) ? tempBoxLocationRows : [];
    let sortedQtyTotal = 0;
    let selectedIdCount = 0;
    let selectedQtyTotal = 0;
    rows.forEach((row: any, idx: number) => {
      const sortedQty = Math.max(0, Number(row?.sorted_qty || 0));
      sortedQtyTotal += sortedQty;
      const key = getTempBoxRowKey(row, idx);
      const selected = tempBoxSelectionMap[key];
      const selectedQty = Math.max(0, Number(selected?.qty || 0));
      if (selected?.checked && selectedQty > 0) {
        selectedIdCount += 1;
        selectedQtyTotal += selectedQty;
      }
    });
    return {
      sortedIdCount: rows.length,
      sortedQtyTotal,
      selectedIdCount,
      selectedQtyTotal,
    };
  }, [tempBoxLocationRows, tempBoxSelectionMap, getTempBoxRowKey]);
  const tempBoxCurrentPageRows = React.useMemo(() => {
    const rows = Array.isArray(tempBoxLocationRows) ? tempBoxLocationRows : [];
    const page = Math.max(1, Number(tempBoxPage || 1));
    const size = Math.max(1, Number(tempBoxPageSize || 10));
    const start = (page - 1) * size;
    return rows.slice(start, start + size);
  }, [tempBoxLocationRows, tempBoxPage, tempBoxPageSize]);
  const tempBoxHeaderCheckState = React.useMemo(() => {
    const rows = Array.isArray(tempBoxCurrentPageRows) ? tempBoxCurrentPageRows : [];
    if (rows.length === 0) return { checked: false, indeterminate: false };
    let selectableCount = 0;
    let checkedCount = 0;
    rows.forEach((row: any) => {
      const sortedQty = Math.max(0, Number(row?.sorted_qty || 0));
      if (sortedQty <= 0) return;
      selectableCount += 1;
      const key = getTempBoxRowKey(row);
      const selectedQty = Math.max(0, Number(tempBoxSelectionMap[key]?.qty || 0));
      if (tempBoxSelectionMap[key]?.checked && selectedQty > 0) checkedCount += 1;
    });
    if (selectableCount <= 0) return { checked: false, indeterminate: false };
    return {
      checked: checkedCount > 0 && checkedCount === selectableCount,
      indeterminate: checkedCount > 0 && checkedCount < selectableCount,
    };
  }, [tempBoxCurrentPageRows, tempBoxSelectionMap, getTempBoxRowKey]);
  const toggleTempBoxSelectAll = React.useCallback((checked: boolean) => {
    if (isReadonly) return;
    const rows = Array.isArray(tempBoxCurrentPageRows) ? tempBoxCurrentPageRows : [];
    setTempBoxSelectionMap((prev) => {
      const next = { ...(prev || {}) };
      rows.forEach((row: any) => {
        const key = getTempBoxRowKey(row);
        const sortedQty = Math.max(0, Number(row?.sorted_qty || 0));
        next[key] = {
          checked: checked && sortedQty > 0,
          qty: sortedQty,
        };
      });
      return next;
    });
  }, [isReadonly, tempBoxCurrentPageRows, getTempBoxRowKey]);
  const toggleTempBoxRowChecked = React.useCallback((row: any, idx: number, checked: boolean) => {
    if (isReadonly) return;
    const key = getTempBoxRowKey(row, idx);
    const sortedQty = Math.max(0, Number(row?.sorted_qty || 0));
    setTempBoxSelectionMap((prev) => {
      const currQty = Math.max(0, Number(prev?.[key]?.qty || sortedQty));
      const nextQty = checked ? Math.max(1, currQty || sortedQty) : 0;
      return {
        ...(prev || {}),
        [key]: {
          checked: checked && sortedQty > 0,
          qty: nextQty,
        },
      };
    });
  }, [isReadonly, getTempBoxRowKey]);
  const changeTempBoxRowQty = React.useCallback((row: any, idx: number, qtyVal: number) => {
    if (isReadonly) return;
    const key = getTempBoxRowKey(row, idx);
    const safeQty = Math.max(0, Math.floor(Number(qtyVal || 0)));
    setTempBoxSelectionMap((prev) => ({
      ...(prev || {}),
      [key]: {
        checked: safeQty > 0,
        qty: safeQty,
      },
    }));
  }, [isReadonly, getTempBoxRowKey]);
  const selectedTempBoxRows = React.useMemo(() => {
    const rows = Array.isArray(tempBoxLocationRows) ? tempBoxLocationRows : [];
    const picked: Array<{ sort_id: string; sorting_mark: string; quantity: number }> = [];
    rows.forEach((row: any, idx: number) => {
      const key = getTempBoxRowKey(row, idx);
      const selected = tempBoxSelectionMap[key];
      const qty = Math.max(0, Math.floor(Number(selected?.qty || 0)));
      if (!selected?.checked || qty <= 0) return;
      picked.push({
        sort_id: String(row?.sort_id ?? '').trim(),
        sorting_mark: String(row?.sorting_mark ?? '').trim(),
        quantity: qty,
      });
    });
    return picked;
  }, [tempBoxLocationRows, tempBoxSelectionMap, getTempBoxRowKey]);
  const tempBoxCreateSelectionSummary = React.useMemo(() => {
    const rows = Array.isArray(selectedTempBoxRows) ? selectedTempBoxRows : [];
    const sortIdSet = new Set<string>();
    let qtyTotal = 0;
    rows.forEach((r: any, idx: number) => {
      const sid = String(r?.sort_id || '').trim();
      const fallback = String(r?.sorting_mark || '').trim();
      const key = sid || fallback || `#${idx + 1}`;
      sortIdSet.add(key);
      qtyTotal += Math.max(0, Number(r?.quantity || 0));
    });
    return `分拣ID:${sortIdSet.size}种 共${qtyTotal}件 将放入此容器`;
  }, [selectedTempBoxRows]);
  const buildTempBoxCreatePayload = React.useCallback(() => {
    return {
      instruction_id: Number(instructionId),
      location_no: String(tempBoxLocationNo || '').trim(),
      container_type: String(tempBoxContainerType || '').trim(),
      remarks: String(tempBoxContainerRemarks || '').trim(),
      selected_results: selectedTempBoxRows,
    } as any;
  }, [
    instructionId,
    tempBoxLocationNo,
    tempBoxContainerType,
    tempBoxContainerRemarks,
    selectedTempBoxRows,
  ]);
  const extractSourceLocationFromRemarks = React.useCallback((raw: any) => {
    const text = String(raw || '').trim();
    if (!text) return '';
    try {
      const m = text.match(/来源仓位\s*:\s*([^;；]+)/);
      if (m?.[1]) return String(m[1]).trim();
    } catch {}
    try {
      const m = text.match(/仓位\s*:\s*([^;；]+)/);
      if (m?.[1]) return String(m[1]).trim();
    } catch {}
    return '';
  }, []);
  const openAddToExistingContainerModal = React.useCallback(async () => {
    if (isReadonly) return;
    if (!instructionId || instructionId <= 0) {
      message.warning('缺少指令信息，无法加载已有容器');
      return;
    }
    if (!Array.isArray(selectedTempBoxRows) || selectedTempBoxRows.length === 0) {
      message.warning('请先勾选需要放入容器的分拣记录');
      return;
    }
    const currLoc = String(tempBoxLocationNo || '').trim();
    if (!currLoc) {
      message.warning('缺少当前仓位信息，无法筛选已有容器');
      return;
    }
    try {
      setTempBoxAddExistingLoading(true);
      setTempBoxAddExistingOpen(true);
      const resp = await request.get('/api/v1/storage/containers/sort-count/drafts/', {
        params: { instruction_id: instructionId },
      } as any);
      const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      const filtered = list.filter((x: any) => {
        const sourceLoc = String(x?.source_location_no || extractSourceLocationFromRemarks(x?.remarks) || '').trim();
        return sourceLoc && sourceLoc === currLoc;
      });
      setTempBoxAddExistingContainers(filtered);
      setTempBoxAddExistingSelectedContainerId(null);
      setTempBoxAddExistingSearch('');
    } catch (e: any) {
      setTempBoxAddExistingContainers([]);
      message.error(String(e?.response?.data?.detail || e?.message || '加载已有容器失败'));
    } finally {
      setTempBoxAddExistingLoading(false);
    }
  }, [isReadonly, instructionId, selectedTempBoxRows, tempBoxLocationNo, extractSourceLocationFromRemarks]);
  const tempBoxAddExistingOptions = React.useMemo(() => {
    const rows = Array.isArray(tempBoxAddExistingContainers) ? tempBoxAddExistingContainers : [];
    return rows.map((r: any) => {
      const code = String(r?.system_code || '').trim();
      const sourceLoc = String(r?.source_location_no || '').trim();
      const totalFromDetail = (Array.isArray(r?.detail_rows) ? r.detail_rows : []).reduce((sum: number, dr: any) => {
        const q = Number(dr?.qty);
        return sum + (Number.isFinite(q) ? Math.max(0, q) : 0);
      }, 0);
      const totalFromSummary = (Array.isArray(r?.contents_summary) ? r.contents_summary : []).reduce((sum: number, it: any) => {
        const q = Number(it?.qty);
        return sum + (Number.isFinite(q) ? Math.max(0, q) : 0);
      }, 0);
      const existingQty = totalFromDetail > 0 ? totalFromDetail : totalFromSummary;
      return {
        value: Number(r?.id || 0),
        label: `${code} | 来源仓位:${sourceLoc || '-'} | 已有数量:${existingQty}`,
        rawCode: code,
      } as any;
    }).filter((x: any) => Number.isFinite(x.value) && x.value > 0);
  }, [tempBoxAddExistingContainers]);
  const buildStorageContainerLabelData = React.useCallback((
    containerBrief: any,
    payload: any,
    createdRow?: any,
  ) => {
    try {
      const containerId = Number(containerBrief?.id || 0);
      const systemCode = String(containerBrief?.system_code || '').trim();
      if (!containerId || !systemCode) return null;
      const nowIso = new Date().toISOString();
      const containerType = String(
        containerBrief?.container_type
        || payload?.container_type
        || tempBoxContainerType
        || 'box',
      ).trim();
      const typeDisplayMap: Record<string, string> = {};
      (Array.isArray(tempBoxContainerTypeChoices) ? tempBoxContainerTypeChoices : []).forEach((it: any) => {
        const key = String(it?.value || '').trim();
        if (key) typeDisplayMap[key] = String(it?.label || key);
      });
      const containerTypeDisplay = String(
        containerBrief?.container_type_display
        || typeDisplayMap[containerType]
        || containerType
        || '-',
      ).trim();
      const statusVal = String(containerBrief?.status || 'draft').trim() || 'draft';
      const statusDisplay = String(containerBrief?.status_display || '草稿').trim() || '草稿';
      const detail = (instructionDetail as any) || {};
      const projectId = Number(
        detail?.project
        || detail?.project_id
        || detail?.projects_detail?.[0]?.id
        || 0,
      ) || 0;
      const projectCode = String(
        detail?.project_code
        || detail?.projects_detail?.[0]?.project_code
        || '',
      ).trim();
      const projectName = String(
        detail?.project_name
        || detail?.projects_detail?.[0]?.name
        || detail?.projects_detail?.[0]?.project_name
        || '-',
      ).trim() || '-';
      const warehouseId = Number(
        detail?.warehouse
        || detail?.warehouse_id
        || 0,
      ) || 0;
      const warehouseName = String(
        detail?.warehouse_name
        || detail?.warehouse_display
        || '-',
      ).trim() || '-';
      const rawItems = Array.isArray(createdRow?.detail_rows)
        ? createdRow.detail_rows
        : [];
      const qtyByUnit = new Map<string, number>();
      rawItems.forEach((x: any) => {
        const code = String(x?.unit_code || '').trim();
        if (!code) return;
        const q = Number(x?.qty);
        const safe = Number.isFinite(q) ? Math.max(0, q) : 0;
        qtyByUnit.set(code, (qtyByUnit.get(code) || 0) + safe);
      });
      const items = qtyByUnit.size > 0
        ? Array.from(qtyByUnit.entries()).map(([code, quantity]) => ({ code, quantity }))
        : (Array.isArray(payload?.selected_results) ? payload.selected_results : []).map((x: any) => ({
          code: `SID-${String(x?.sort_id || '-')}`,
          quantity: Math.max(0, Number(x?.quantity || 0)),
        })).filter((x: any) => x.quantity > 0);
      const totalQty = items.reduce((sum: number, x: any) => sum + Math.max(0, Number(x?.quantity || 0)), 0);
      const contentSummary = {
        totalItems: items.length,
        itemTypes: ['库存单元'],
        totalQuantity: totalQty,
        items,
      };
      const containerLike: any = {
        id: containerId,
        system_code: systemCode,
        name: systemCode,
        container_type: containerType,
        container_type_display: containerTypeDisplay,
        status: statusVal,
        status_display: statusDisplay,
        warehouse: warehouseId,
        warehouse_name: warehouseName,
        project: projectId,
        project_name: projectName,
        project_code: projectCode,
        current_location: null,
        created_by_username: String((detail as any)?.created_by_username || '-'),
        updated_at: String(containerBrief?.created_at || nowIso),
        remarks: String(createdRow?.remarks || payload?.remarks || tempBoxContainerRemarks || ''),
      };
      return storageContainerAdapter.transform(containerLike, {
        contentSummary,
        printOptions: {
          copies: 1,
          directPrint: true,
        },
      });
    } catch {
      return null;
    }
  }, [instructionDetail, tempBoxContainerType, tempBoxContainerTypeChoices, tempBoxContainerRemarks]);
  const need2CurrentUnit = React.useMemo(() => {
    const rows = Array.isArray(need2UnconfirmedUnits) ? need2UnconfirmedUnits : [];
    const idx = Math.max(0, Number(need2CurrentUnitIdx || 0));
    if (idx >= rows.length) return null;
    return rows[idx] || null;
  }, [need2UnconfirmedUnits, need2CurrentUnitIdx]);
  const need2ProgressMeta = React.useMemo(() => {
    const total = Math.max(0, Number((need2UnconfirmedUnits || []).length || 0));
    const current = Math.max(0, Number(need2CurrentUnitIdx || 0)) + (total > 0 ? 1 : 0);
    const done = total > 0 && current >= total;
    return { current, total, done };
  }, [need2UnconfirmedUnits, need2CurrentUnitIdx]);
  const resetNeed2WeightConfirmState = React.useCallback(() => {
    setNeed2WeightConfirmOpen(false);
    setNeed2UnconfirmedUnits([]);
    setNeed2CurrentUnitIdx(0);
    setNeed2WeightForm({
      actual_weight: 0,
      actual_length: 0,
      actual_width: 0,
      actual_height: 0,
      logistics_package: false,
    });
    setNeed2PendingAction('');
    setNeed2PendingPayload(null);
    setNeed2PendingContainerId(0);
  }, []);
  const resetNeed2PrecheckState = React.useCallback(() => {
    setNeed2PrecheckOpen(false);
    setNeed2PrecheckMisses([]);
    setNeed2PrecheckUnconfirmedUnits([]);
    setNeed2PrecheckMatchedCount(0);
    setNeed2PrecheckDetailText('');
    setNeed2PrecheckAction('');
    setNeed2PrecheckPayload(null);
    setNeed2PrecheckContainerId(0);
    setNeed2ResolvedMatches({});
  }, []);
  const openNeed2PrecheckModal = React.useCallback((args: {
    misses?: any[];
    unconfirmedUnits?: any[];
    matchedCount?: number;
    detail?: string;
    action: 'create' | 'add-existing';
    payload: any;
    containerId?: number;
  }) => {
    if (isReadonly) return;
    setNeed2PrecheckMisses(Array.isArray(args?.misses) ? args.misses : []);
    setNeed2PrecheckUnconfirmedUnits(Array.isArray(args?.unconfirmedUnits) ? args.unconfirmedUnits : []);
    setNeed2PrecheckMatchedCount(Math.max(0, Number(args?.matchedCount || 0)));
    setNeed2PrecheckDetailText(String(args?.detail || '').trim());
    setNeed2PrecheckAction(args?.action || '');
    setNeed2PrecheckPayload(args?.payload || null);
    setNeed2PrecheckContainerId(Number(args?.containerId || 0) || 0);
    setNeed2ResolvedMatches({});
    setNeed2PrecheckOpen(true);
  }, [isReadonly]);
  const need2PrecheckStats = React.useMemo(() => {
    const misses = Array.isArray(need2PrecheckMisses) ? need2PrecheckMisses.length : 0;
    const unconfirmed = Array.isArray(need2PrecheckUnconfirmedUnits) ? need2PrecheckUnconfirmedUnits.length : 0;
    const matched = Math.max(0, Number(need2PrecheckMatchedCount || 0));
    const totalCandidates = matched + misses;
    const ready = Math.max(0, matched - unconfirmed);
    return { misses, unconfirmed, matched, totalCandidates, ready };
  }, [need2PrecheckMisses, need2PrecheckUnconfirmedUnits, need2PrecheckMatchedCount]);
  const fillNeed2WeightFormByUnit = React.useCallback((unit: any) => {
    const toNum = (v: any) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(0, n) : 0;
    };
    const normalizeLogisticsPackage = (raw: any) => {
      if (typeof raw === 'boolean') return raw;
      const txt = String(raw || '').trim().toLowerCase();
      return txt === 'self_contained' || txt === '自带物流包装' || txt === '1' || txt === 'true' || txt === 'yes' || txt === 'on';
    };
    setNeed2WeightForm({
      // 优先预填客户声明值（weight/length/width/height）；为空时回退 actual
      actual_weight: toNum(unit?.weight ?? unit?.actual_weight),
      actual_length: toNum(unit?.length ?? unit?.actual_length),
      actual_width: toNum(unit?.width ?? unit?.actual_width),
      actual_height: toNum(unit?.height ?? unit?.actual_height),
      logistics_package: normalizeLogisticsPackage(unit?.logistics_package_value ?? unit?.logistics_package),
    });
  }, []);
  const openNeed2WeightConfirmModal = React.useCallback((
    units: any[],
    action: 'create' | 'add-existing',
    payload: any,
    containerId?: number,
  ) => {
    if (isReadonly) return;
    const rows = (Array.isArray(units) ? units : [])
      .map((x: any) => ({ ...(x || {}), id: Number(x?.id || 0) }))
      .filter((x: any) => Number.isFinite(x.id) && x.id > 0);
    if (rows.length <= 0) return;
    setNeed2PendingAction(action);
    setNeed2PendingPayload(payload || null);
    setNeed2PendingContainerId(Number(containerId || 0) || 0);
    setNeed2UnconfirmedUnits(rows);
    setNeed2CurrentUnitIdx(0);
    fillNeed2WeightFormByUnit(rows[0]);
    setNeed2WeightConfirmOpen(true);
  }, [isReadonly, fillNeed2WeightFormByUnit]);
  const triggerNeed2WeightConfirmFromPrecheck = React.useCallback(() => {
    if (isReadonly) return;
    const action = String(need2PrecheckAction || '').trim();
    const payload = need2PrecheckPayload;
    const units = Array.isArray(need2PrecheckUnconfirmedUnits) ? need2PrecheckUnconfirmedUnits : [];
    if (!action || !payload || units.length <= 0) return;
    openNeed2WeightConfirmModal(
      units,
      action === 'add-existing' ? 'add-existing' : 'create',
      payload,
      Number(need2PrecheckContainerId || 0) || 0,
    );
  }, [isReadonly, need2PrecheckAction, need2PrecheckPayload, need2PrecheckUnconfirmedUnits, need2PrecheckContainerId, openNeed2WeightConfirmModal]);
  const need2MissingCurrentRow = React.useMemo(() => {
    const rows = Array.isArray(need2MissingCreateRows) ? need2MissingCreateRows : [];
    const idx = Number(need2MissingCreateIdx || 0);
    if (idx < 0 || idx >= rows.length) return null;
    return rows[idx] || null;
  }, [need2MissingCreateRows, need2MissingCreateIdx]);
  const fillNeed2MissingCreateForm = React.useCallback((prefill: any) => {
    const toNum = (v: any) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(0, n) : 0;
    };
    setNeed2MissingCreateForm({
      weight_g: toNum(prefill?.weight_g),
      length_cm: toNum(prefill?.length_cm),
      width_cm: toNum(prefill?.width_cm),
      height_cm: toNum(prefill?.height_cm),
      logistics_package: !!prefill?.logistics_package,
    });
  }, []);
  const loadNeed2MissingCreateContext = React.useCallback(async (row: any) => {
    const sortingMark = String(row?.sorting_mark || '').trim();
    const rowKey = String(row?.row_key || '').trim();
    if (!sortingMark) {
      setNeed2MissingCreateContext(null);
      fillNeed2MissingCreateForm(null);
      return;
    }
    try {
      setNeed2MissingCreateLoading(true);
      const resp = await request.post('/api/v1/storage/containers/sort-count/need2/missing-liu/', {
        mode: 'prepare',
        instruction_id: instructionId,
        row_key: rowKey,
        sorting_mark: sortingMark,
      });
      const dataResp = resp?.data || {};
      setNeed2MissingCreateContext(dataResp);
      fillNeed2MissingCreateForm(dataResp?.prefill);
      if (dataResp?.already_exists) {
        message.info(`分拣标识 ${sortingMark} 已存在可用LIU，将从未匹配列表移除`);
        setNeed2MissingCreateRows((prev) => {
          const arr = Array.isArray(prev) ? [...prev] : [];
          const currentIdx = arr.findIndex((x: any) => String(x?.row_key || '') === rowKey);
          if (currentIdx >= 0) arr.splice(currentIdx, 1);
          return arr;
        });
        setNeed2PrecheckMisses((prev) => {
          const arr = Array.isArray(prev) ? [...prev] : [];
          const currentIdx = arr.findIndex((x: any) => String(x?.row_key || '') === rowKey);
          if (currentIdx >= 0) arr.splice(currentIdx, 1);
          return arr;
        });
      }
    } catch (e: any) {
      setNeed2MissingCreateContext(null);
      fillNeed2MissingCreateForm(null);
      message.error(String(e?.response?.data?.detail || e?.message || '加载未匹配LIU处理信息失败'));
    } finally {
      setNeed2MissingCreateLoading(false);
    }
  }, [instructionId, fillNeed2MissingCreateForm]);
  const openNeed2MissingCreateModal = React.useCallback(() => {
    if (isReadonly) return;
    const rows = Array.isArray(need2PrecheckMisses) ? need2PrecheckMisses : [];
    if (rows.length <= 0) {
      message.info('当前没有未匹配记录');
      return;
    }
    const normalized = rows.map((r: any, i: number) => ({
      ...(r || {}),
      row_key: String(r?.row_key || `${String(r?.sort_id || '-') || '-'}||${String(r?.sorting_mark || '-')}||${i}`),
    }));
    setNeed2MissingCreateRows(normalized);
    setNeed2MissingCreateIdx(0);
    setNeed2MissingCreateOpen(true);
  }, [isReadonly, need2PrecheckMisses]);
  const closeNeed2MissingCreateModal = React.useCallback(() => {
    setNeed2MissingCreateOpen(false);
    setNeed2MissingCreateRows([]);
    setNeed2MissingCreateIdx(0);
    setNeed2MissingCreateContext(null);
    setNeed2MissingCreateLoading(false);
    setNeed2MissingCreateSubmitting(false);
    fillNeed2MissingCreateForm(null);
  }, [fillNeed2MissingCreateForm]);
  const submitNeed2MissingCreate = React.useCallback(async () => {
    if (isReadonly) return;
    const row = need2MissingCurrentRow;
    if (!row) return;
    const sortingMark = String(row?.sorting_mark || '').trim();
    const rowKey = String(row?.row_key || '').trim();
    if (!sortingMark) {
      message.warning('缺少分拣标识，无法创建');
      return;
    }
    const payload = {
      mode: 'create',
      instruction_id: instructionId,
      row_key: rowKey,
      sorting_mark: sortingMark,
      weight_g: Math.max(0, Number(need2MissingCreateForm.weight_g || 0)),
      length_cm: Math.max(0, Number(need2MissingCreateForm.length_cm || 0)),
      width_cm: Math.max(0, Number(need2MissingCreateForm.width_cm || 0)),
      height_cm: Math.max(0, Number(need2MissingCreateForm.height_cm || 0)),
      logistics_package: !!need2MissingCreateForm.logistics_package,
    };
    if (!payload.weight_g || !payload.length_cm || !payload.width_cm || !payload.height_cm) {
      message.warning('请填写完整且大于0的重量和尺寸');
      return;
    }
    try {
      setNeed2MissingCreateSubmitting(true);
      const resp = await request.post('/api/v1/storage/containers/sort-count/need2/missing-liu/', payload);
      const productCreated = !!resp?.data?.product_created;
      const unitCode = String(resp?.data?.inventory_unit?.system_barcode || '').trim();
      const createdUnitId = Number(resp?.data?.inventory_unit?.id || 0);
      message.success(`已创建LIU${unitCode ? `：${unitCode}` : ''}${productCreated ? '（并创建未知产品，价值默认1 USD）' : ''}`);
      if (createdUnitId > 0) {
        setNeed2ResolvedMatches((prev) => ({
          ...(prev || {}),
          [rowKey]: createdUnitId,
        }));
      }
      setNeed2MissingCreateRows((prev) => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const idx = arr.findIndex((x: any) => String(x?.row_key || '') === rowKey);
        if (idx >= 0) arr.splice(idx, 1);
        return arr;
      });
      setNeed2PrecheckMisses((prev) => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const idx = arr.findIndex((x: any) => String(x?.row_key || '') === rowKey);
        if (idx >= 0) arr.splice(idx, 1);
        return arr;
      });
      setNeed2PrecheckMatchedCount((prev) => Math.max(0, Number(prev || 0)) + 1);
    } catch (e: any) {
      message.error(String(e?.response?.data?.detail || e?.message || '创建库存单元失败'));
    } finally {
      setNeed2MissingCreateSubmitting(false);
    }
  }, [isReadonly, need2MissingCurrentRow, instructionId, need2MissingCreateForm]);
  React.useEffect(() => {
    if (!need2MissingCreateOpen) return;
    const rows = Array.isArray(need2MissingCreateRows) ? need2MissingCreateRows : [];
    if (rows.length <= 0) {
      closeNeed2MissingCreateModal();
      message.success('未匹配记录已处理完成，请继续预检测流程');
      return;
    }
    const idx = Math.max(0, Math.min(Number(need2MissingCreateIdx || 0), rows.length - 1));
    if (idx !== need2MissingCreateIdx) {
      setNeed2MissingCreateIdx(idx);
      return;
    }
    loadNeed2MissingCreateContext(rows[idx]);
  }, [
    need2MissingCreateOpen,
    need2MissingCreateRows,
    need2MissingCreateIdx,
    loadNeed2MissingCreateContext,
    closeNeed2MissingCreateModal,
  ]);
  const applyTempBoxSelectionDeduction = React.useCallback(() => {
    setBoxedQtyByLocationSort((prev) => {
      const next = { ...(prev || {}) };
      const currLoc = String(tempBoxLocationNo || '').trim();
      selectedTempBoxRows.forEach((x) => {
        const sid = String(x?.sort_id || '').trim();
        if (!sid) return;
        const key = `${currLoc}__${sid}`;
        const q = Math.max(0, Number(x?.quantity || 0));
        next[key] = Math.max(0, Number(next[key] || 0) + q);
      });
      return next;
    });
  }, [tempBoxLocationNo, selectedTempBoxRows]);
  const openGeneratedDraftContainerLabelPreview = React.useCallback((row: any) => {
    if (isReadonly) return;
    const systemCode = String(row?.system_code || '').trim();
    if (!systemCode) {
      message.warning('缺少容器系统编码，无法预览标签');
      return;
    }
    const createdRow = (Array.isArray(generatedDraftContainers) ? generatedDraftContainers : []).find((x: any) => {
      if (row?.id && Number(x?.id || 0) === Number(row.id || 0)) return true;
      return String(x?.system_code || '') === systemCode;
    }) || row;
    const labelData = buildStorageContainerLabelData(
      {
        id: Number(createdRow?.id || row?.id || 0),
        system_code: systemCode,
        status: String(createdRow?.status || row?.status || 'draft'),
        container_type: String(createdRow?.container_type || row?.container_type || ''),
        container_type_display: String(createdRow?.container_type_display || row?.container_type_display || ''),
        created_at: createdRow?.created_at || row?.created_at,
        remarks: String(createdRow?.remarks || row?.remarks || ''),
      },
      null,
      createdRow,
    );
    if (!labelData) {
      message.warning('标签数据准备失败，无法预览');
      return;
    }
    setLabelPreviewData(labelData);
    setLabelPreviewVisible(true);
  }, [isReadonly, generatedDraftContainers, buildStorageContainerLabelData]);
  const doCreateSortCountContainerDraft = React.useCallback(async (payload: any, manualMatches?: Record<string, number | null>) => {
    if (isReadonly) return;
    const nextPayload = {
      ...(payload || {}),
      ...(manualMatches ? { manual_matches: manualMatches } : {}),
    };
    const resp = await request.post('/api/v1/storage/containers/sort-count/create-draft/', nextPayload);
    const code = String(resp?.data?.container?.system_code || '');
    const cnt = Number(resp?.data?.contents_count || 0);
    message.success(`已创建草稿容器${code ? `：${code}` : ''}，内容物 ${cnt} 项`);
    applyTempBoxSelectionDeduction();
    let refreshList: any[] = [];
    try {
      const refreshResp = await request.get('/api/v1/storage/containers/sort-count/drafts/', {
        params: { instruction_id: instructionId },
      } as any);
      refreshList = Array.isArray(refreshResp?.data?.results) ? refreshResp.data.results : [];
      setGeneratedDraftContainers(refreshList);
    } catch {}
    try {
      const createdRow = (Array.isArray(refreshList) ? refreshList : []).find(
        (x: any) => String(x?.system_code || '') === code,
      );
      const labelData = buildStorageContainerLabelData(resp?.data?.container, payload, createdRow);
      if (labelData) {
        setLabelPreviewData(labelData);
        setLabelPreviewVisible(true);
      }
    } catch {}
    setTempBoxManualMatchOpen(false);
    setTempBoxManualMatchRows([]);
    setTempBoxManualMatchSelection({});
    setTempBoxPendingPayload(null);
    setTempBoxPendingAction('create');
    setTempBoxCreateContainerOpen(false);
    setTempBoxAddExistingOpen(false);
    setTempBoxModalOpen(false);
  }, [isReadonly, applyTempBoxSelectionDeduction, instructionId, buildStorageContainerLabelData]);
  const doAddSortCountResultsToExistingDraft = React.useCallback(async (
    payload: any,
    containerId: number,
    manualMatches?: Record<string, number | null>,
  ) => {
    if (isReadonly) return;
    const nextPayload = {
      ...(payload || {}),
      container_id: Number(containerId || 0),
      ...(manualMatches ? { manual_matches: manualMatches } : {}),
    };
    const resp = await request.post('/api/v1/storage/containers/sort-count/add-to-existing-draft/', nextPayload);
    const code = String(resp?.data?.container?.system_code || '');
    const cnt = Number(resp?.data?.touched_contents_count || 0);
    message.success(`已放入已有容器${code ? `：${code}` : ''}，影响内容物 ${cnt} 项`);
    applyTempBoxSelectionDeduction();
    try {
      const refreshResp = await request.get('/api/v1/storage/containers/sort-count/drafts/', {
        params: { instruction_id: instructionId },
      } as any);
      const refreshList = Array.isArray(refreshResp?.data?.results) ? refreshResp.data.results : [];
      setGeneratedDraftContainers(refreshList);
    } catch {}
    setTempBoxManualMatchOpen(false);
    setTempBoxManualMatchRows([]);
    setTempBoxManualMatchSelection({});
    setTempBoxPendingPayload(null);
    setTempBoxPendingAction('create');
    setTempBoxAddExistingSelectedContainerId(null);
    setTempBoxAddExistingOpen(false);
    setTempBoxModalOpen(false);
  }, [isReadonly, applyTempBoxSelectionDeduction, instructionId]);
  const continueNeed2PendingAction = React.useCallback(async () => {
    if (isReadonly) return;
    const action = String(need2PendingAction || '').trim();
    const payload = need2PendingPayload;
    if (!action || !payload) return;
    if (action === 'add-existing') {
      const cid = Number(need2PendingContainerId || 0);
      if (!Number.isFinite(cid) || cid <= 0) {
        message.warning('缺少目标容器信息，请重新操作');
        return;
      }
      await doAddSortCountResultsToExistingDraft(payload, cid);
      return;
    }
    await doCreateSortCountContainerDraft(payload);
  }, [isReadonly, need2PendingAction, need2PendingPayload, need2PendingContainerId, doAddSortCountResultsToExistingDraft, doCreateSortCountContainerDraft]);
  const submitNeed2WeightConfirm = React.useCallback(async () => {
    if (isReadonly) return;
    const unit = need2CurrentUnit;
    if (!unit || !unit?.id) {
      resetNeed2WeightConfirmState();
      return;
    }
    const payload = {
      actual_weight: Math.max(0, Number(need2WeightForm.actual_weight || 0)),
      actual_length: Math.max(0, Number(need2WeightForm.actual_length || 0)),
      actual_width: Math.max(0, Number(need2WeightForm.actual_width || 0)),
      actual_height: Math.max(0, Number(need2WeightForm.actual_height || 0)),
      logistics_package: !!need2WeightForm.logistics_package,
    };
    if (!payload.actual_weight || !payload.actual_length || !payload.actual_width || !payload.actual_height) {
      message.warning('请填写完整的实际重量和长宽高');
      return;
    }
    try {
      setNeed2WeightConfirmSubmitting(true);
      await confirmInventoryUnitWeightDimensions(Number(unit.id), payload as any);
      setNeed2PrecheckUnconfirmedUnits((prev) => {
        const rows = Array.isArray(prev) ? [...prev] : [];
        return rows.filter((x: any) => Number(x?.id || 0) !== Number(unit.id || 0));
      });
      const nextIdx = Number(need2CurrentUnitIdx || 0) + 1;
      const allRows = Array.isArray(need2UnconfirmedUnits) ? need2UnconfirmedUnits : [];
      if (nextIdx < allRows.length) {
        setNeed2CurrentUnitIdx(nextIdx);
        fillNeed2WeightFormByUnit(allRows[nextIdx]);
        message.success('已确认，继续下一个库存单元');
        return;
      }
      message.success('物理信息确认完成，请返回预检测弹窗确认后再放入容器');
      setNeed2WeightConfirmOpen(false);
      resetNeed2WeightConfirmState();
    } catch (e: any) {
      message.error(String(e?.response?.data?.detail || e?.message || '确认重量尺寸失败'));
    } finally {
      setNeed2WeightConfirmSubmitting(false);
    }
  }, [
    need2CurrentUnit,
    isReadonly,
    need2WeightForm,
    need2CurrentUnitIdx,
    need2UnconfirmedUnits,
    fillNeed2WeightFormByUnit,
    resetNeed2WeightConfirmState,
  ]);
  const submitNeed2PrecheckContinue = React.useCallback(async () => {
    if (isReadonly) return;
    const action = String(need2PrecheckAction || '').trim();
    const payload = need2PrecheckPayload;
    if (!action || !payload) return;
    if (need2PrecheckStats.misses > 0 || need2PrecheckStats.unconfirmed > 0) return;
    const resolvedPayload = {
      ...(payload || {}),
      resolved_matches: need2ResolvedMatches || {},
    };
    try {
      if (action === 'add-existing') {
        const cid = Number(need2PrecheckContainerId || 0);
        if (!Number.isFinite(cid) || cid <= 0) {
          message.warning('缺少目标容器信息，请重新操作');
          return;
        }
        await doAddSortCountResultsToExistingDraft(resolvedPayload, cid);
      } else {
        await doCreateSortCountContainerDraft(resolvedPayload);
      }
      resetNeed2PrecheckState();
    } catch (e: any) {
      message.error(String(e?.response?.data?.detail || e?.message || '放入容器失败'));
    }
  }, [
    need2PrecheckAction,
    isReadonly,
    need2PrecheckPayload,
    need2PrecheckContainerId,
    need2PrecheckStats,
    need2ResolvedMatches,
    doAddSortCountResultsToExistingDraft,
    doCreateSortCountContainerDraft,
    resetNeed2PrecheckState,
  ]);
  const submitCreateSortCountContainerDraft = React.useCallback(async () => {
    if (isReadonly) return;
    if (!instructionId || instructionId <= 0) {
      message.warning('缺少指令信息，无法创建容器');
      return;
    }
    if (!tempBoxContainerType) {
      message.warning('请选择容器类型');
      return;
    }
    if (!Array.isArray(selectedTempBoxRows) || selectedTempBoxRows.length === 0) {
      message.warning('请先勾选需要放入容器的分拣记录');
      return;
    }
    const payload = buildTempBoxCreatePayload();
    try {
      setTempBoxCreateSubmitting(true);
      await doCreateSortCountContainerDraft(payload);
    } catch (e: any) {
      const detail = String(e?.response?.data?.detail || e?.message || '创建容器失败');
      const errCode = String(e?.response?.data?.code || '').trim();
      const misses = Array.isArray(e?.response?.data?.misses) ? e.response.data.misses : [];
      const manualRequired = !!e?.response?.data?.manual_match_required;
      const unconfirmedUnits = Array.isArray(e?.response?.data?.unconfirmed_units) ? e.response.data.unconfirmed_units : [];
      if (sortCountDemandMode === 'need2' && (
        errCode === 'sort_count_need2_weight_dimensions_unconfirmed'
        || errCode === 'sort_count_need2_unmatched_liu_placeholder'
        || errCode === 'sort_count_need2_precheck_required'
      )) {
        openNeed2PrecheckModal({
          misses,
          unconfirmedUnits,
          matchedCount: Number(e?.response?.data?.matched_count || 0),
          detail,
          action: 'create',
          payload,
        });
      } else if (manualRequired && misses.length > 0) {
        const nextRows = misses.map((m: any, i: number) => {
          const rowKey = String(m?.row_key || `${String(m?.sort_id || '-') || '-'}||${String(m?.sorting_mark || '-')}||${i}`);
          return {
            ...m,
            row_key: rowKey,
            candidate_units: Array.isArray(m?.candidate_units) ? m.candidate_units : [],
          };
        });
        setTempBoxPendingPayload(payload);
        setTempBoxPendingAction('create');
        setTempBoxManualMatchRows(nextRows);
        setTempBoxManualMatchSelection({});
        setTempBoxManualMatchOpen(true);
      } else if (misses.length > 0) {
        Modal.warning({
          title: '分拣标识匹配失败',
          width: 820,
          content: (
            <div style={{ maxHeight: 340, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <div style={{ marginBottom: 8, color: '#595959' }}>{detail}</div>
              {misses.slice(0, 50).map((m: any, i: number) => (
                <div key={`${String(m?.sort_id || '')}-${i}`}>
                  {i + 1}. 分拣ID:{String(m?.sort_id || '-')} 分拣标识:{String(m?.sorting_mark || '-')} 原因:{String(m?.reason || '-')}
                </div>
              ))}
              {misses.length > 50 ? <div>...其余 {misses.length - 50} 条已省略</div> : null}
            </div>
          ),
        });
      } else {
        message.error(detail);
      }
    } finally {
      setTempBoxCreateSubmitting(false);
    }
  }, [
    instructionId,
    isReadonly,
    sortCountDemandMode,
    tempBoxContainerType,
    selectedTempBoxRows,
    buildTempBoxCreatePayload,
    doCreateSortCountContainerDraft,
    openNeed2PrecheckModal,
  ]);
  const submitAddToExistingContainer = React.useCallback(async () => {
    if (isReadonly) return;
    if (!instructionId || instructionId <= 0) {
      message.warning('缺少指令信息，无法放入已有容器');
      return;
    }
    const containerId = Number(tempBoxAddExistingSelectedContainerId || 0);
    if (!Number.isFinite(containerId) || containerId <= 0) {
      message.warning('请选择目标容器');
      return;
    }
    if (!Array.isArray(selectedTempBoxRows) || selectedTempBoxRows.length === 0) {
      message.warning('请先勾选需要放入容器的分拣记录');
      return;
    }
    const payload = buildTempBoxCreatePayload();
    try {
      setTempBoxAddExistingSubmitting(true);
      await doAddSortCountResultsToExistingDraft(payload, containerId);
    } catch (e: any) {
      const detail = String(e?.response?.data?.detail || e?.message || '放入已有容器失败');
      const errCode = String(e?.response?.data?.code || '').trim();
      const misses = Array.isArray(e?.response?.data?.misses) ? e.response.data.misses : [];
      const manualRequired = !!e?.response?.data?.manual_match_required;
      const unconfirmedUnits = Array.isArray(e?.response?.data?.unconfirmed_units) ? e.response.data.unconfirmed_units : [];
      if (sortCountDemandMode === 'need2' && (
        errCode === 'sort_count_need2_weight_dimensions_unconfirmed'
        || errCode === 'sort_count_need2_unmatched_liu_placeholder'
        || errCode === 'sort_count_need2_precheck_required'
      )) {
        openNeed2PrecheckModal({
          misses,
          unconfirmedUnits,
          matchedCount: Number(e?.response?.data?.matched_count || 0),
          detail,
          action: 'add-existing',
          payload,
          containerId,
        });
      } else if (manualRequired && misses.length > 0) {
        const nextRows = misses.map((m: any, i: number) => {
          const rowKey = String(m?.row_key || `${String(m?.sort_id || '-') || '-'}||${String(m?.sorting_mark || '-')}||${i}`);
          return {
            ...m,
            row_key: rowKey,
            candidate_units: Array.isArray(m?.candidate_units) ? m.candidate_units : [],
          };
        });
        setTempBoxPendingPayload({ ...(payload || {}), container_id: containerId });
        setTempBoxPendingAction('add-existing');
        setTempBoxManualMatchRows(nextRows);
        setTempBoxManualMatchSelection({});
        setTempBoxManualMatchOpen(true);
      } else {
        message.error(detail);
      }
    } finally {
      setTempBoxAddExistingSubmitting(false);
    }
  }, [
    instructionId,
    isReadonly,
    sortCountDemandMode,
    tempBoxAddExistingSelectedContainerId,
    selectedTempBoxRows,
    buildTempBoxCreatePayload,
    doAddSortCountResultsToExistingDraft,
    openNeed2PrecheckModal,
  ]);
  const submitTempBoxManualMatches = React.useCallback(async () => {
    if (isReadonly) return;
    if (!tempBoxPendingPayload) {
      setTempBoxManualMatchOpen(false);
      return;
    }
    const rows = Array.isArray(tempBoxManualMatchRows) ? tempBoxManualMatchRows : [];
    const manualMatches: Record<string, number | null> = {};
    for (const r of rows) {
      const key = String(r?.row_key || '').trim();
      const uid = Number(tempBoxManualMatchSelection[key]);
      if (!key) continue;
      if (!Number.isFinite(uid) || uid <= 0) {
        message.warning(`请为分拣ID:${String(r?.sort_id || '-')} 选择库存单元`);
        return;
      }
      manualMatches[key] = uid;
    }
    try {
      setTempBoxCreateSubmitting(true);
      if (tempBoxPendingAction === 'add-existing') {
        const cid = Number((tempBoxPendingPayload as any)?.container_id || 0);
        if (!Number.isFinite(cid) || cid <= 0) {
          message.warning('缺少目标容器信息，请重新选择容器');
          return;
        }
        await doAddSortCountResultsToExistingDraft(tempBoxPendingPayload, cid, manualMatches);
      } else {
        await doCreateSortCountContainerDraft(tempBoxPendingPayload, manualMatches);
      }
    } catch (e: any) {
      const detail = String(e?.response?.data?.detail || e?.message || '手动匹配提交失败');
      message.error(detail);
    } finally {
      setTempBoxCreateSubmitting(false);
    }
  }, [
    tempBoxPendingPayload,
    isReadonly,
    tempBoxPendingAction,
    tempBoxManualMatchRows,
    tempBoxManualMatchSelection,
    doCreateSortCountContainerDraft,
    doAddSortCountResultsToExistingDraft,
  ]);
  const loadGeneratedDraftContainers = React.useCallback(async () => {
    if (!instructionId || instructionId <= 0) {
      setGeneratedDraftContainers([]);
      return;
    }
    try {
      setGeneratedDraftContainersLoading(true);
      const resp = await request.get('/api/v1/storage/containers/sort-count/drafts/', {
        params: { instruction_id: instructionId },
      } as any);
      const list = Array.isArray(resp?.data?.results) ? resp.data.results : [];
      setGeneratedDraftContainers(list);
    } catch {
      setGeneratedDraftContainers([]);
    } finally {
      setGeneratedDraftContainersLoading(false);
    }
  }, [instructionId]);
  React.useEffect(() => {
    loadGeneratedDraftContainers();
  }, [loadGeneratedDraftContainers]);
  const generatedDraftContainerDetailRows = React.useMemo(() => {
    const rows: any[] = [];
    (Array.isArray(generatedDraftContainers) ? generatedDraftContainers : []).forEach((c: any, containerIdx: number) => {
      const detailsRaw = Array.isArray(c?.detail_rows) ? c.detail_rows : [];
      const details = (detailsRaw.length > 0 ? detailsRaw : [{
        unit_code: '-',
        sorting_mark: '',
        qty: null,
      }]).slice().sort((a: any, b: any) => {
        const ua = String(a?.unit_code || '-');
        const ub = String(b?.unit_code || '-');
        if (ua !== ub) return ua.localeCompare(ub);
        const ma = String(a?.sorting_mark || '');
        const mb = String(b?.sorting_mark || '');
        return ma.localeCompare(mb);
      });
      const containerSpan = details.length;
      const containerTotalQty = details.reduce((sum: number, d: any) => {
        const q = Number(d?.qty);
        if (!Number.isFinite(q)) return sum;
        return sum + Math.max(0, q);
      }, 0);
      const unitCountMap = new Map<string, number>();
      details.forEach((d: any) => {
        const unitCode = String(d?.unit_code || '-');
        unitCountMap.set(unitCode, (unitCountMap.get(unitCode) || 0) + 1);
      });
      const unitFirstShown = new Set<string>();
      details.forEach((d: any, idx: number) => {
        const unitCode = String(d?.unit_code || '-');
        const isUnitFirst = !unitFirstShown.has(unitCode);
        if (isUnitFirst) unitFirstShown.add(unitCode);
        rows.push({
          __rowKey: `${String(c?.id || c?.system_code || 'c')}-${idx}`,
          id: Number(c?.id || 0),
          system_code: String(c?.system_code || ''),
          container_type: String(c?.container_type_display || c?.container_type || ''),
          remarks: String(c?.remarks || ''),
          unit_code: unitCode,
          sorting_mark: String(d?.sorting_mark || ''),
          qty: d?.qty == null ? null : Number(d?.qty || 0),
          total_qty: containerTotalQty,
          created_at: c?.created_at,
          __containerGroupIndex: containerIdx,
          __containerRowSpan: idx === 0 ? containerSpan : 0,
          __unitRowSpan: isUnitFirst ? Number(unitCountMap.get(unitCode) || 1) : 0,
        });
      });
    });
    return rows;
  }, [generatedDraftContainers]);
  const hasGeneratedDraftContainerRecords = React.useMemo(() => (
    Array.isArray(generatedDraftContainerDetailRows) && generatedDraftContainerDetailRows.length > 0
  ), [generatedDraftContainerDetailRows]);
  const statsByMarkTabSummary = React.useMemo(() => {
    const rows = Array.isArray(statsByMarkRows) ? statsByMarkRows : [];
    const markCount = rows.length;
    const totalQty = rows.reduce((sum: number, r: any) => sum + Math.max(0, Number(r?.total_qty || 0)), 0);
    return { markCount, totalQty };
  }, [statsByMarkRows]);
  const statsByLocationTabSummary = React.useMemo(() => {
    const groups = Array.isArray(statsByLocationGroups) ? statsByLocationGroups : [];
    const locationCount = groups.length;
    const totalQty = groups.reduce((sum: number, g: any) => sum + Math.max(0, Number(g?.total_qty || 0)), 0);
    return { locationCount, totalQty };
  }, [statsByLocationGroups]);
  React.useEffect(() => {
    const blocked = Math.max(0, Number(statsByLocationTabSummary.totalQty || 0)) > 0;
    onChange('sort_count_has_location_retention', blocked);
    onChange('sort_count_location_retention_total_qty', Math.max(0, Number(statsByLocationTabSummary.totalQty || 0)));
    onChange('sort_count_location_retention_location_count', Math.max(0, Number(statsByLocationTabSummary.locationCount || 0)));
  }, [onChange, statsByLocationTabSummary]);
  const generatedContainerTabSummary = React.useMemo(() => {
    const containers = Array.isArray(generatedDraftContainers) ? generatedDraftContainers : [];
    const containerCount = containers.length;
    const totalQty = containers.reduce((sum: number, c: any) => {
      const details = Array.isArray(c?.detail_rows) ? c.detail_rows : [];
      if (details.length > 0) {
        const rowSum = details.reduce((s: number, d: any) => s + Math.max(0, Number(d?.qty || 0)), 0);
        return sum + rowSum;
      }
      const fallback = Math.max(0, Number(c?.total_qty || 0));
      return sum + fallback;
    }, 0);
    return { containerCount, totalQty };
  }, [generatedDraftContainers]);
  React.useEffect(() => {
    const total = Array.isArray(generatedDraftContainerDetailRows) ? generatedDraftContainerDetailRows.length : 0;
    const size = Math.max(1, Number(generatedDraftContainersPageSize || 10));
    const maxPage = Math.max(1, Math.ceil(total / size));
    if (generatedDraftContainersPage > maxPage) setGeneratedDraftContainersPage(1);
  }, [generatedDraftContainerDetailRows, generatedDraftContainersPage, generatedDraftContainersPageSize]);
  const summaryStats = React.useMemo(() => {
    const rows = Array.isArray(sortDbRows) ? sortDbRows : [];
    const locationSet = new Set<string>();
    const sortIdSet = new Set<string>();
    rows.forEach((r) => {
      const loc = String(r?.location_no ?? '').trim();
      const sid = String(r?.sort_id ?? '').trim();
      if (loc) locationSet.add(loc);
      if (sid) sortIdSet.add(sid);
    });
    const totalQty = (Array.isArray(operationRows) ? operationRows : []).reduce((s: number, r: any) => {
      const q = Number(r?.qty || 0);
      return s + (Number.isFinite(q) ? Math.max(q, 0) : 0);
    }, 0);
    return {
      locationCount: locationSet.size,
      sortIdCount: sortIdSet.size,
      totalQty,
    };
  }, [operationRows, sortDbRows]);
  const operationTargetExpectedQtyTotal = React.useMemo(() => {
    const rows = Array.isArray(instructionTargetRows) ? instructionTargetRows : [];
    return rows.reduce((sum: number, row: any) => {
      const v = Number(row?.expected_qty ?? 0);
      if (!Number.isFinite(v)) return sum;
      return sum + Math.max(0, v);
    }, 0);
  }, [instructionTargetRows]);
  const calcEstimatedPendingProductQtyForTarget = React.useCallback((row: any): number | null => {
    const unitQty = Math.max(0, Number(row?.expected_qty ?? 0) || 0);
    const items = extractInstructionTargetContentItems(row);
    if (!Array.isArray(items) || items.length <= 0) return null;
    const perUnitContentQty = items.reduce((sum: number, it: any) => {
      const q = Number(it?.qty ?? 0);
      if (!Number.isFinite(q) || q <= 0) return sum;
      return sum + q;
    }, 0);
    return Math.floor(perUnitContentQty * unitQty);
  }, [extractInstructionTargetContentItems]);
  const getInstructionTargetUnitType = React.useCallback((row: any): string => {
    const snap = row?.target?.snapshot || row?.snapshot || {};
    const direct = String(
      row?.unit_type
      || row?.unit_type_display
      || snap?.unit_type
      || snap?.unit_type_display
      || '',
    ).trim().toUpperCase();
    if (direct === 'BIU' || direct.includes('箱库存单元')) return 'BIU';
    if (direct === 'LIU' || direct.includes('散货库存单元')) return 'LIU';
    if (direct === 'PIU' || direct.includes('托盘库存单元')) return 'PIU';
    const iuCode = String(
      row?.inventory_unit_system_barcode
      || row?.inventory_unit_code
      || row?.target_code
      || snap?.system_barcode
      || snap?.system_code
      || '',
    ).trim().toUpperCase();
    if (iuCode.startsWith('BIU')) return 'BIU';
    if (iuCode.startsWith('LIU')) return 'LIU';
    if (iuCode.startsWith('PIU')) return 'PIU';
    return '';
  }, []);
  const operationTargetEstimatedProductQtyTotal = React.useMemo(() => {
    const rows = Array.isArray(instructionTargetRows) ? instructionTargetRows : [];
    if (rows.length <= 0) return 0;
    if (sortCountDemandMode === 'need1') {
      const inventoryRows = rows.filter((row: any) => String(row?.target_type || 'inventory_unit').trim() === 'inventory_unit');
      if (inventoryRows.length > 0) {
        const allBIU = inventoryRows.every((row: any) => getInstructionTargetUnitType(row) === 'BIU');
        if (allBIU) {
          const biuQtyTotal = inventoryRows.reduce((sum: number, row: any) => {
            const v = Number(row?.expected_qty ?? 0);
            if (!Number.isFinite(v)) return sum;
            return sum + Math.max(0, v);
          }, 0);
          return Math.floor(biuQtyTotal);
        }
      }
    }
    let sum = 0;
    for (const row of rows) {
      const v = calcEstimatedPendingProductQtyForTarget(row);
      if (v == null) return null;
      sum += v;
    }
    return Math.floor(sum);
  }, [instructionTargetRows, sortCountDemandMode, getInstructionTargetUnitType, calcEstimatedPendingProductQtyForTarget]);
  const calcEstimatedOperationTargetQtyForTab = React.useCallback((row: any): number | null => {
    if (sortCountDemandMode === 'need1' && getInstructionTargetUnitType(row) === 'BIU') {
      const v = Number(row?.expected_qty ?? 0);
      if (!Number.isFinite(v)) return 0;
      return Math.floor(Math.max(0, v));
    }
    return calcEstimatedPendingProductQtyForTarget(row);
  }, [sortCountDemandMode, getInstructionTargetUnitType, calcEstimatedPendingProductQtyForTarget]);
  const sortCountSnapshotDraft = React.useMemo(() => {
    return {
      schema_version: 1,
      kind: 'sort_count',
      instruction_context: {
        instruction_id: instructionId || null,
        instruction_code: String(instructionCodeFromParent || '').trim(),
        demand_mode: sortCountDemandMode || '',
        header_display: sortAndCountHeaderCenterDisplay || '',
        sort_mode_label: currentSortModeLabel || '',
      },
      stats: {
        location_count: summaryStats.locationCount,
        sort_id_count: summaryStats.sortIdCount,
        sorted_total_qty: summaryStats.totalQty,
        operation_target_expected_qty_total: operationTargetExpectedQtyTotal,
        by_mark_count: statsByMarkTabSummary.markCount,
        by_mark_total_qty: statsByMarkTabSummary.totalQty,
        location_retention_location_count: statsByLocationTabSummary.locationCount,
        location_retention_total_qty: statsByLocationTabSummary.totalQty,
        generated_container_count: generatedContainerTabSummary.containerCount,
        generated_container_total_qty: generatedContainerTabSummary.totalQty,
      },
      sections: {
        operation_targets: {
          rows: Array.isArray(snapshotInstructionTargetRows) ? snapshotInstructionTargetRows : [],
        },
        sort_database_rows: {
          rows: Array.isArray(sortDbRows) ? sortDbRows : [],
        },
        operation_records: {
          rows: Array.isArray(operationRowsDesc) ? operationRowsDesc : [],
        },
        stats_by_mark: {
          rows: Array.isArray(statsByMarkRows) ? statsByMarkRows : [],
        },
        location_retention: {
          groups: Array.isArray(statsByLocationGroups) ? statsByLocationGroups : [],
        },
        generated_containers: {
          rows: Array.isArray(generatedDraftContainers) ? generatedDraftContainers : [],
          detail_rows: Array.isArray(generatedDraftContainerDetailRows) ? generatedDraftContainerDetailRows : [],
        },
        preset_group_list: {
          has_baseline: !!hasGroupListBaseline,
          files: Array.isArray(groupListBaselineFiles) ? groupListBaselineFiles : [],
          columns: Array.isArray(groupListPreviewCols) ? groupListPreviewCols : [],
          rows: Array.isArray(groupedGroupListPreviewRows) ? groupedGroupListPreviewRows : [],
          ungrouped_handling_answer: ungroupedHandlingAnswer || '',
        },
        attachments: {
          rows: Array.isArray(attachmentRows) ? attachmentRows : [],
        },
      },
    };
  }, [
    instructionId,
    instructionCodeFromParent,
    sortCountDemandMode,
    sortAndCountHeaderCenterDisplay,
    currentSortModeLabel,
    summaryStats,
    operationTargetExpectedQtyTotal,
    statsByMarkTabSummary,
    statsByLocationTabSummary,
    generatedContainerTabSummary,
    snapshotInstructionTargetRows,
    sortDbRows,
    operationRowsDesc,
    statsByMarkRows,
    statsByLocationGroups,
    generatedDraftContainers,
    generatedDraftContainerDetailRows,
    hasGroupListBaseline,
    groupListBaselineFiles,
    groupListPreviewCols,
    groupedGroupListPreviewRows,
    ungroupedHandlingAnswer,
    attachmentRows,
  ]);
  React.useEffect(() => {
    onChange('sort_count_snapshot_draft', sortCountSnapshotDraft);
  }, [onChange, sortCountSnapshotDraft]);
  const sortDbStats = React.useMemo(() => {
    const rows = Array.isArray(sortDbRows) ? sortDbRows : [];
    const locationSet = new Set<string>();
    const sortIdSet = new Set<string>();
    rows.forEach((r) => {
      const loc = String(r?.location_no ?? '').trim();
      const sid = String(r?.sort_id ?? '').trim();
      if (loc) locationSet.add(loc);
      if (sid) sortIdSet.add(sid);
    });
    return { locationCount: locationSet.size, sortIdCount: sortIdSet.size };
  }, [sortDbRows]);
  const simplifiedModeOthersLocation = React.useMemo(() => {
    const rows = Array.isArray(sortDbRows) ? sortDbRows : [];
    const hit = rows.find((r: any) => String(r?.group_code || '').trim().toUpperCase() === 'G-OTHERS');
    if (!hit) return '';
    return String(hit?.location_no ?? '').trim();
  }, [sortDbRows]);
  React.useEffect(() => {
    const total = filteredSortDbRows.length;
    const maxPage = Math.max(1, Math.ceil(total / sortDbPageSize));
    if (sortDbPage > maxPage) setSortDbPage(1);
  }, [filteredSortDbRows, sortDbPage, sortDbPageSize]);
  React.useEffect(() => {
    const total = operationRowsDesc.length;
    const maxPage = Math.max(1, Math.ceil(total / operationPageSize));
    if (operationPage > maxPage) setOperationPage(1);
  }, [operationRowsDesc, operationPage, operationPageSize]);
  React.useEffect(() => {
    const total = filteredStatsByMarkRows.length;
    const maxPage = Math.max(1, Math.ceil(total / statsMarkPageSize));
    if (statsMarkPage > maxPage) setStatsMarkPage(1);
  }, [filteredStatsByMarkRows, statsMarkPage, statsMarkPageSize]);
  React.useEffect(() => {
    const total = statsByLocationGroups.length;
    const maxPage = Math.max(1, Math.ceil(total / statsLocationPageSize));
    if (statsLocationPage > maxPage) setStatsLocationPage(1);
  }, [statsByLocationGroups, statsLocationPage, statsLocationPageSize]);
  const getExtLower = (row: any) => {
    const raw = String(
      row?.file_name
      || row?.raw?.file_name
      || row?.raw?.file
      || row?.raw?.file_path
      || '',
    ).trim();
    const matched = raw.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
    return String(matched?.[1] || '').toLowerCase();
  };
  const clearPreviewBlobUrl = () => {
    const old = previewBlobUrlRef.current;
    if (old && old.startsWith('blob:')) {
      try { URL.revokeObjectURL(old); } catch {}
    }
    previewBlobUrlRef.current = '';
  };
  const setPreviewBlobUrl = (next: string) => {
    clearPreviewBlobUrl();
    if (next && next.startsWith('blob:')) previewBlobUrlRef.current = next;
    setPreviewUrl(next);
  };
  const downloadAttachment = async (row: any) => {
    const url = String(row?.url || '').trim();
    if (!url) {
      message.warning('该附件暂无可下载链接');
      return;
    }
    const fileName = String(row?.file_name || '附件').trim() || '附件';
    try {
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('下载失败');
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => {
        try { URL.revokeObjectURL(href); } catch {}
      }, 1500);
    } catch {
      message.error('下载失败');
    }
  };
  const saveEditedGroupListBaseline = async () => {
    if (isReadonly) return;
    if (!(previewType === 'csv' || previewType === 'xlsx')) return;
    if (!previewDirty) {
      message.info('当前没有未保存修改');
      return;
    }
    setGroupListSaveLoading(true);
    try {
      const baselineId = await ensureGroupListBaselineHead();
      const extLower = String(previewType === 'csv' ? 'csv' : 'xlsx').toLowerCase();
      const baseName = String(previewSourceFileName || previewBaselineFile?.file_name || 'GROUP_LIST').trim();
      const normalizedName = (() => {
        const hasExt = /\.[a-z0-9]+$/i.test(baseName);
        if (!hasExt) return `${baseName}.${extLower}`;
        if (extLower === 'csv') return baseName.replace(/\.[a-z0-9]+$/i, '.csv');
        return baseName.replace(/\.[a-z0-9]+$/i, '.xlsx');
      })();
      const cols = (previewCols || []).filter(Boolean);
      const headers = cols.map((c: any) => String(c?.title || '').trim() || String(c?.dataIndex || '').trim());
      const rows = (previewData || []).map((r: any) => cols.map((c: any) => String(r?.[c?.dataIndex] ?? '')));
      const aoa = [headers, ...rows];

      let fileBlob: Blob;
      if (extLower === 'csv') {
        const Papa = (await import('papaparse') as any)?.default || (await import('papaparse') as any);
        const csvText = Papa.unparse(aoa);
        fileBlob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
      } else {
        const XLSX = previewXlsxLibRef.current || await import('xlsx');
        const wb = previewXlsxWbRef.current || XLSX.utils.book_new();
        const activeSheet = String(previewActiveSheet || previewSheetNames?.[0] || 'Sheet1').trim() || 'Sheet1';
        wb.Sheets = wb.Sheets || {};
        wb.Sheets[activeSheet] = XLSX.utils.aoa_to_sheet(aoa);
        if (!Array.isArray(wb.SheetNames) || wb.SheetNames.length === 0) wb.SheetNames = [activeSheet];
        if (!wb.SheetNames.includes(activeSheet)) wb.SheetNames = [activeSheet, ...wb.SheetNames];
        const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        fileBlob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      }
      const fileObj = new File([fileBlob], normalizedName, { type: fileBlob.type || 'application/octet-stream' });
      await uploadGroupListBaselineFile(fileObj);
      setPreviewDirty(false);
      setPreviewEditingCell(null);
      await loadLatestBaselines();
      message.success('基线文件修改已保存');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '保存基线文件失败');
    } finally {
      setGroupListSaveLoading(false);
    }
  };
  const openAttachmentPreview = async (
    row: any,
    options?: { context?: 'attachment' | 'group-list-baseline'; baselineFile?: any },
  ) => {
    const url = String(row?.url || '').trim();
    if (!url) {
      message.warning('该附件暂无可预览链接');
      return;
    }
    const context = options?.context || 'attachment';
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewTitle(String(row?.file_name || '附件预览'));
    setPreviewContext(context);
    setPreviewDirty(false);
    setPreviewEditingCell(null);
    setPreviewBaselineFile(options?.baselineFile || null);
    setPreviewSourceFileName(String(row?.file_name || ''));
    setPreviewCols([]);
    setPreviewData([]);
    setPreviewText('');
    setPreviewSheetNames([]);
    setPreviewActiveSheet('');
    previewXlsxWbRef.current = null;
    previewXlsxLibRef.current = null;
    previewXlsxCacheRef.current = {};
    setPreviewPageSize(20);
    setPreviewBlobUrl('');
    const extLower = getExtLower(row);
    try {
      if (extLower === 'csv') {
        setPreviewType('csv');
        const Papa = (await import('papaparse') as any)?.default || (await import('papaparse') as any);
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('CSV 加载失败');
        const text = await resp.text();
        const parsed = Papa.parse(text, { skipEmptyLines: true });
        const rows: any[] = Array.isArray(parsed?.data) ? parsed.data : [];
        const arr = rows.filter((r: any) => Array.isArray(r) && r.length > 0);
        if (arr.length === 0) {
          setPreviewError('文件为空');
          return;
        }
        const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
        const dataRows = arr.slice(1);
        const cols = headers.map((h: any, i: number) => ({
          title: String(h || `列${i + 1}`),
          dataIndex: `c${i}`,
          align: 'center',
        }));
        const data = dataRows.map((r: any[], i: number) => {
          const obj: any = { __idx: i + 2, __rowIndex: i };
          cols.forEach((c: any, ci: number) => { obj[c.dataIndex] = String(r?.[ci] ?? ''); });
          return obj;
        });
        setPreviewCols(cols);
        setPreviewData(data);
        return;
      }
      if (extLower === 'xlsx' || extLower === 'xls') {
        setPreviewType('xlsx');
        const XLSX = await import('xlsx') as any;
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('Excel 加载失败');
        const buf = await resp.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetNames = Array.isArray(wb?.SheetNames) ? (wb.SheetNames as string[]) : [];
        const firstSheet = String(sheetNames?.[0] || '').trim();
        const firstSh = firstSheet ? wb.Sheets?.[firstSheet] : null;
        if (!firstSheet || !firstSh) throw new Error('未找到工作表');
        previewXlsxWbRef.current = wb;
        previewXlsxLibRef.current = XLSX;
        previewXlsxCacheRef.current = {};
        setPreviewSheetNames(sheetNames);
        setPreviewActiveSheet(firstSheet);
        const aoa: any[] = XLSX.utils.sheet_to_json(firstSh, { header: 1, defval: '', blankrows: false });
        const arr = Array.isArray(aoa) ? aoa.filter((r: any) => Array.isArray(r) && r.length > 0) : [];
        if (arr.length === 0) {
          setPreviewError('工作表为空');
          return;
        }
        const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
        const dataRows = arr.slice(1);
        const cols = headers.map((h: any, i: number) => ({
          title: String(h || `列${i + 1}`),
          dataIndex: `c${i}`,
          align: 'center',
        }));
        const data = dataRows.map((r: any[], i: number) => {
          const obj: any = { __idx: i + 2, __rowIndex: i };
          cols.forEach((c: any, ci: number) => { obj[c.dataIndex] = String(r?.[ci] ?? ''); });
          return obj;
        });
        previewXlsxCacheRef.current[firstSheet] = { cols, data };
        setPreviewCols(cols);
        setPreviewData(data);
        return;
      }
      if (extLower === 'pdf') {
        setPreviewType('pdf');
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('PDF 加载失败');
        const blob = await resp.blob();
        setPreviewBlobUrl(URL.createObjectURL(blob));
        return;
      }
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(extLower)) {
        setPreviewType('image');
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('图片加载失败');
        const blob = await resp.blob();
        setPreviewBlobUrl(URL.createObjectURL(blob));
        return;
      }
      if (['mp4', 'webm', 'ogg', 'mov', 'm4v', 'avi', 'mkv'].includes(extLower)) {
        setPreviewType('video');
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('视频加载失败');
        const blob = await resp.blob();
        setPreviewBlobUrl(URL.createObjectURL(blob));
        return;
      }
      if (['txt', 'text', 'md', 'log', 'json', 'xml', 'yaml', 'yml', 'ini', 'csv'].includes(extLower)) {
        setPreviewType('text');
        const resp = await fetch(url, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('文本加载失败');
        setPreviewText(await resp.text());
        return;
      }
      setPreviewType('other');
      setPreviewUrl(url);
    } catch (e: any) {
      setPreviewError(String(e?.message || '预览失败'));
    } finally {
      setPreviewLoading(false);
    }
  };
  const closePreviewModal = async () => {
    if (previewContext === 'group-list-baseline' && (previewType === 'csv' || previewType === 'xlsx')) {
      const cols = Array.isArray(previewCols) ? previewCols.filter(Boolean) : [];
      const aKey = String(cols?.[0]?.dataIndex || '').trim();
      const bKey = String(cols?.[1]?.dataIndex || '').trim();
      const rows = Array.isArray(previewData) ? previewData : [];
      const norm = (v: any) => String(v ?? '').trim().toUpperCase();
      const missingABRows: number[] = [];
      const invalidARows: number[] = [];
      const comboRowsByKey = new Map<string, number[]>();
      const bRowsByKey = new Map<string, number[]>();
      rows.forEach((r: any, idx: number) => {
        const rowNo = idx + 2; // 第一行是表头，数据从第2行起
        const a = norm(r?.[aKey]);
        const b = norm(r?.[bKey]);
        const hasAny = String(a || b).trim() !== '';
        if (!hasAny) return;
        if (!a || !b) {
          missingABRows.push(rowNo);
        }
        if (a) {
          const n = Number(a);
          if (!Number.isInteger(n) || n <= 0) invalidARows.push(rowNo);
        }
        // 规则3：A+B 组合不能重复（仅对 A/B 都有值的行）
        if (a && b) {
          const comboKey = `${a}__${b}`;
          const arr = comboRowsByKey.get(comboKey) || [];
          arr.push(rowNo);
          comboRowsByKey.set(comboKey, arr);
        }
        // 规则4：B 列分拣标识不能重复（仅对 B 有值的行）
        if (b) {
          const arr = bRowsByKey.get(b) || [];
          arr.push(rowNo);
          bRowsByKey.set(b, arr);
        }
      });
      const duplicateABGroups = Array.from(comboRowsByKey.values()).filter((arr) => arr.length > 1);
      const duplicateBGroups = Array.from(bRowsByKey.values()).filter((arr) => arr.length > 1);
      const formatDupGroups = (groups: number[][]) => groups.map((g) => `第${g.join('、')}行`).join('；');
      if (missingABRows.length > 0 || invalidARows.length > 0 || duplicateABGroups.length > 0 || duplicateBGroups.length > 0) {
        const msgParts: string[] = [];
        if (missingABRows.length > 0) {
          msgParts.push(`以下行分组编号和分拣标识必须同时有值：${missingABRows.join('，')}`);
        }
        if (invalidARows.length > 0) {
          msgParts.push(`以下行分组编号必须为正整数：${invalidARows.join('，')}`);
        }
        if (duplicateABGroups.length > 0) {
          msgParts.push(`以下行存在重复记录（分组编号+分拣标识组合重复）：${formatDupGroups(duplicateABGroups)}`);
        }
        if (duplicateBGroups.length > 0) {
          msgParts.push(`以下行分拣标识重复（同一分拣标识只能属于一个分组编号）：${formatDupGroups(duplicateBGroups)}`);
        }
        const proceedClose = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '基线文件校验未通过',
            content: (
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {msgParts.join('\n')}
              </div>
            ),
            okText: '依然关闭',
            cancelText: '返回修改',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!proceedClose) return;
      }
    }
    if (previewContext === 'group-list-baseline' && previewDirty) {
      const ok = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '存在未保存修改',
          content: '当前基线文件有未保存修改，确认直接关闭吗？',
          okText: '直接关闭',
          cancelText: '继续编辑',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }
    setPreviewOpen(false);
    setPreviewEditingCell(null);
    clearPreviewBlobUrl();
    setPreviewUrl('');
    setPreviewError('');
  };

  return (
    <Card
      title={(
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifySelf: 'start' }}>
            <span>{String(step?.title || '分拣与清点')}</span>
            <Button
              size="small"
              style={{
                borderColor: '#1677ff',
                color: '#1677ff',
              }}
              onClick={() => setFlowGuideOpen(true)}
            >
              流程说明
            </Button>
          </div>
          <span
            style={{
              color: '#fa541c',
              border: '1px solid #fa541c',
              borderRadius: 6,
              padding: '2px 10px',
              textAlign: 'center',
              fontWeight: 400,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              {sortAndCountHeaderCenterDisplay.split('|').map((part: string, idx: number) => (
                <React.Fragment key={`hdr-${idx}`}>
                  {idx > 0 ? <span style={{ display: 'inline-block', width: 1, height: 14, background: '#fa541c' }} /> : null}
                  <span>{String(part || '').trim()}</span>
                </React.Fragment>
              ))}
            </span>
          </span>
          <Button
            danger
            disabled={isReadonly || hasGeneratedDraftContainerRecords}
            title={hasGeneratedDraftContainerRecords ? '已存在装入容器记录，不能清空草稿' : undefined}
            style={{ justifySelf: 'end' }}
            onClick={clearSortAndCountDraft}
          >
            清空草稿
          </Button>
        </div>
      )}
      bodyStyle={{ padding: 12 }}
    >
      <div style={{ width: '100%', overflowX: 'auto', overflowY: 'visible' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '0.85fr 1.15fr',
            gridTemplateRows: 'auto auto',
            gap: 12,
            minHeight: adaptiveLayoutHeight,
            minWidth: 1180,
            width: 'max(100%, 1180px)',
            boxSizing: 'border-box',
          }}
        >
        <section style={{ ...panelStyle('#f0f5ff', '#85a5ff'), minHeight: 320 }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ marginBottom: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <span>操作区域</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: '#8c8c8c', marginLeft: 20 }}>播报语言</span>
                <Select
                  size="small"
                  value={speechLang}
                  style={{ width: 120 }}
                  onChange={(v) => setSpeechLang(v as LocalSpeechLang)}
                  options={[
                    { value: 'zh-CN', label: '中文' },
                    { value: 'en-US', label: 'English' },
                  ]}
                  disabled={!speechSupported}
                />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <InputNumber
                min={1}
                precision={0}
                disabled={isReadonly}
                value={sortInputQty}
                onChange={(v) => setSortInputQty(Math.max(1, Number(v || 1)))}
                style={{ width: 130 }}
                placeholder="数量"
              />
              <Input
                ref={sortInputRef}
                placeholder="请扫描或输入分拣值"
                disabled={isReadonly}
                value={sortInputValue}
                onChange={(e) => setSortInputValue(e.target.value)}
                onPressEnter={handleSortInputSubmit}
                style={{ flex: 1, minWidth: 160 }}
              />
              <Button type="primary" disabled={isReadonly} onClick={handleSortInputSubmit}>输入</Button>
            </div>
            <div
              style={{
                flex: '0 0 auto',
                height: 430,
                minHeight: 430,
                maxHeight: 430,
                border: '1px dashed #adc6ff',
                borderRadius: 8,
                padding: 10,
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, width: '100%', height: '100%' }}>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    textAlign: 'center',
                    display: 'grid',
                    gridTemplateRows: '1fr 1px 1fr',
                    height: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '6px 8px',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ color: '#595959', fontWeight: 600, marginBottom: 6 }}>输入值:</div>
                    <div
                      style={{
                        color: '#262626',
                        fontWeight: 800,
                        fontSize: calcAdaptiveFontSize(currentInputMark || '-', { minPx: 7, maxPx: 44, baseLen: 10 }),
                        lineHeight: 1.2,
                        paddingBottom: 2,
                        whiteSpace: 'normal',
                        wordBreak: 'break-all',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      {currentInputMark || '-'}
                    </div>
                  </div>
                  <div style={{ borderTop: '1px dashed #d9d9d9' }} />
                  <div
                    style={{
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '6px 8px',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ color: '#595959', fontWeight: 600, marginBottom: 6 }}>分拣ID×数量</div>
                    <div
                      style={{
                        fontSize: calcAdaptiveFontSize(currentSortIdWithQtyDisplay || '-', { minPx: 12, maxPx: 48, baseLen: 14 }),
                        lineHeight: 1.1,
                        fontWeight: 800,
                        letterSpacing: 0.5,
                        color: '#1d39c4',
                        whiteSpace: 'normal',
                        wordBreak: 'break-all',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      {currentSortIdWithQtyDisplay || '-'}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    borderRadius: 8,
                    background: '#ffeb3b',
                    border: '2px solid #fadb14',
                    padding: 6,
                  }}
                >
                  <div style={{ color: '#8c8c8c', fontWeight: 600, fontSize: 18, marginBottom: 8 }}>仓位</div>
                  <div
                    style={{
                      fontSize: 'clamp(40px, 5vw, 86px)',
                      lineHeight: 1.05,
                      fontWeight: 900,
                      letterSpacing: 1,
                      color: '#cf1322',
                      wordBreak: 'break-word',
                    }}
                  >
                    {currentLocationDisplay || '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section style={{ ...panelStyle('#fff7e6', '#faad14'), minHeight: 320, overflow: 'hidden' }}>
          <div style={{ fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span>操作信息看板</span>
            <span style={{ color: '#595959', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <span>{`预估分拣目标总数量:${operationTargetEstimatedProductQtyTotal == null ? '-' : `${Math.max(0, Math.floor(operationTargetEstimatedProductQtyTotal))}件`}`}</span>
              <span style={{ color: '#d9d9d9' }}>|</span>
              <span>{`仓位:${sortDbStats.locationCount}个`}</span>
              <span>{`分拣ID:${sortDbStats.sortIdCount}个`}</span>
            </span>
          </div>
          <Tabs
            size="small"
            activeKey={activeInfoTab}
            onChange={setActiveInfoTab}
            items={[
              {
                key: 'db-location',
                label: (
                  <span style={{ color: activeInfoTab === 'db-location' ? undefined : '#8c8c8c' }}>
                    分拣数据库与仓位表
                  </span>
                ),
                children: (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ color: '#595959', fontWeight: 600 }}>分拣标识筛选：</span>
                      <Input
                        size="small"
                        allowClear
                        value={sortDbMarkFilter}
                        onChange={(e) => { setSortDbMarkFilter(e.target.value); setSortDbPage(1); }}
                        placeholder="输入分拣标识"
                        style={{ width: 220 }}
                      />
                      <span style={{ color: '#595959', fontWeight: 600 }}>仓位筛选：</span>
                      <Input
                        size="small"
                        allowClear
                        value={sortDbLocationFilter}
                        onChange={(e) => { setSortDbLocationFilter(e.target.value); setSortDbPage(1); }}
                        placeholder="输入仓位"
                        style={{ width: 160 }}
                      />
                    </div>
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                      <Table
                        size="small"
                        rowKey={(r: any, i?: number) => String(r?.id || i || 0)}
                        pagination={{
                          size: 'small',
                          current: sortDbPage,
                          pageSize: sortDbPageSize,
                          total: filteredSortDbRows.length,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          pageSizeOptions: ['5', '10', '20', '50', '100'] as any,
                          onChange: (p: number, ps: number) => {
                            setSortDbPage(p);
                            setSortDbPageSize(ps);
                          },
                          showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                        }}
                        dataSource={filteredSortDbRows}
                        columns={sortDbColumns as any}
                        scroll={{ x: 'max-content' as any }}
                        summary={() => {
                          if (currentSortModeLabel !== '分组-简化模式') return null;
                          return (
                            <Table.Summary>
                              <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={5}>
                                  <div style={{ color: '#595959', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <span>分组-简化模式 分拣组G-OTHERS 已分配仓位:</span>
                                    {renderLocationBadge(simplifiedModeOthersLocation || '-')}
                                  </div>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </Table.Summary>
                          );
                        }}
                        locale={{ emptyText: '分拣开始前暂无记录，执行分拣后将实时更新' }}
                        tableLayout="fixed"
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: 'operation-targets',
                label: (
                  <span style={{ color: activeInfoTab === 'operation-targets' ? undefined : '#8c8c8c' }}>
                    操作对象
                  </span>
                ),
                children: (
                  <div>
                    <Table
                      size="small"
                      rowKey={(r: any, i?: number) => String(r?.id || r?.target_id || r?.inventory_unit_id || i || 0)}
                      pagination={{
                        size: 'small',
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['10', '20', '50', '100'] as any,
                        showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                      }}
                      dataSource={instructionTargetRows}
                      scroll={{ x: 'max-content' as any }}
                      columns={[
                        { title: '序号', dataIndex: '__idx', key: '__idx', width: 70, align: 'center' as const },
                        {
                          title: '库存单元系统编码',
                          key: 'inventory_unit',
                          width: 200,
                          align: 'center' as const,
                          render: (_: any, row: any) => {
                            const code = String(
                              row?.inventory_unit_system_barcode
                              || row?.snapshot?.system_barcode
                              || row?.snapshot?.system_code
                              || row?.inventory_unit_code
                              || row?.inventory_unit_id
                              || '-',
                            ).trim();
                            const text = code || '-';
                            if (text === '-') return '-';
                            return (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span>{text}</span>
                                <Tooltip title="复制">
                                  <CopyOutlined
                                    style={{ color: '#1677ff', cursor: 'pointer' }}
                                    onClick={async () => {
                                      try {
                                        await navigator.clipboard.writeText(text);
                                        message.success('已复制');
                                      } catch {
                                        message.error('复制失败');
                                      }
                                    }}
                                  />
                                </Tooltip>
                              </span>
                            );
                          },
                        },
                        {
                          title: '入库分拣组',
                          key: 'sorting_group',
                          width: 200,
                          align: 'center' as const,
                          render: (_: any, row: any) => {
                            const uid = row?.inventory_unit_id ?? row?.target?.inventory_unit_id ?? row?.snapshot?.inventory_unit_id;
                            const cached = uid != null ? operationTargetUnitSortingGroupCache[String(uid)] : undefined;
                            const sgCode = String(
                              row?.sorting_group_info?.system_code
                              || row?.sorting_group_code
                              || row?.snapshot?.sorting_group_info?.system_code
                              || cached?.code
                              || '',
                            ).trim();
                            const sgName = String(
                              row?.sorting_group_info?.name
                              || row?.sorting_group_name
                              || row?.snapshot?.sorting_group_info?.name
                              || cached?.name
                              || '',
                            ).trim();
                            if (sgCode && sgName) return `${sgCode} (${sgName})`;
                            return sgCode || sgName || '-';
                          },
                        },
                        {
                          title: '库存单元内容项',
                          key: 'inventory_unit_items',
                          width: 420,
                          render: (_: any, row: any) => renderInstructionTargetContentItems(row),
                        },
                        {
                          title: '预估库存单元数量',
                          key: 'qty',
                          width: 150,
                          align: 'center' as const,
                          render: (_: any, row: any) => {
                            const qty = row?.expected_qty;
                            return qty ?? 0;
                          },
                        },
                        {
                          title: '预估分拣目标数量',
                          key: 'content_qty',
                          width: 170,
                          align: 'center' as const,
                          render: (_: any, row: any) => {
                            const v = calcEstimatedOperationTargetQtyForTab(row);
                            if (v == null) return '-';
                            return v;
                          },
                        },
                      ] as any}
                      locale={{ emptyText: '暂无操作对象数据' }}
                      tableLayout="fixed"
                    />
                  </div>
                ),
              },
              {
                key: 'preset-group-list',
                label: (
                  <span style={{ color: activeInfoTab === 'preset-group-list' ? undefined : '#8c8c8c' }}>
                    {hasGroupListBaseline ? '预设分组列表(有)' : '预设分组列表(无)'}
                  </span>
                ),
                children: (
                  <div>
                    {hasGroupListBaseline ? (
                      <>
                        <div
                          style={{
                            marginBottom: 10,
                            padding: '8px 10px',
                            border: '1px solid #ffe58f',
                            borderRadius: 6,
                            background: '#fffbe6',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                            <div style={{ flex: 1, minWidth: 260 }}>
                            <span style={{ color: '#595959', fontWeight: 600, marginRight: 8 }}>分组列表基线文件：</span>
                            {groupListBaselineFiles.length > 0 ? (
                              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, verticalAlign: 'top' }}>
                                {groupListBaselineFiles.map((f: any) => (
                                  <Button
                                    key={String(f?.key)}
                                    size="small"
                                    type="link"
                                    style={{ padding: 0, height: 'auto' }}
                                    onClick={() => { openAttachmentPreview(f, { context: 'group-list-baseline', baselineFile: f }); }}
                                  >
                                    {String(f?.name || '-')}
                                  </Button>
                                ))}
                              </span>
                            ) : (
                              <span style={{ color: '#8c8c8c' }}>暂无</span>
                            )}
                            <span style={{ marginLeft: 10 }}>
                              <Button
                                size="small"
                                type="primary"
                                loading={groupListUploadLoading}
                                disabled={isReadonly}
                                onClick={triggerPickGroupListFile}
                              >
                                替换/上传
                              </Button>
                              <Button
                                size="small"
                                danger
                                style={{ marginLeft: 5 }}
                                loading={groupListDeleteLoading}
                                disabled={isReadonly || groupListBaselineFiles.length === 0}
                                onClick={deleteGroupListBaselineFiles}
                              >
                                删除
                              </Button>
                            </span>
                            </div>
                            <div style={{ minWidth: 360, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <span style={{ color: '#595959', fontWeight: 600 }}>未分组产品如何处理：</span>
                              <span style={{ ...ungroupedAnswerBadgeStyle }}>
                                {hasGroupListBaseline ? (ungroupedHandlingAnswer || '') : ''}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ color: '#595959', fontWeight: 600 }}>分拣标识搜索：</span>
                            <Input
                              size="small"
                              allowClear
                              value={groupListSearchKeyword}
                              onChange={(e) => setGroupListSearchKeyword(e.target.value)}
                              placeholder="输入分拣标识模糊搜索"
                              style={{ width: 220 }}
                            />
                          </div>
                        </div>
                        {baselineLoading ? (
                          <div style={{ marginBottom: 10, color: '#8c8c8c' }}>正在加载基线数据...</div>
                        ) : null}
                        {groupListPreviewError ? (
                          <div style={{ marginBottom: 10, color: '#cf1322' }}>{groupListPreviewError}</div>
                        ) : null}
                        <Table
                          size="small"
                          rowKey={(r: any, i?: number) => String(r?.__idx ?? i ?? 0)}
                          loading={groupListPreviewLoading}
                          pagination={{
                            size: 'small',
                            current: groupListTablePage,
                            pageSize: groupListTablePageSize,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'] as any,
                            onChange: (page: number, pageSize?: number) => {
                              setGroupListTablePage(page);
                              if (pageSize && pageSize !== groupListTablePageSize) {
                                setGroupListTablePageSize(pageSize);
                              }
                            },
                            showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                          }}
                          dataSource={groupedGroupListPreviewRows}
                          scroll={{ x: 'max-content' as any }}
                          columns={(groupListPreviewCols || []).flatMap((col: any) => {
                            const titleText = String(col?.title || '').trim();
                            const dataIndexText = String(col?.dataIndex || '').trim();
                            const isSortMarkCol = titleText.includes('分拣标识') || (groupListSearchDataIndex && dataIndexText === groupListSearchDataIndex);
                            const isGroupNoCol = titleText.includes('分组编号') || (groupListGroupNoDataIndex && dataIndexText === groupListGroupNoDataIndex);
                            const baseCol = {
                              ...col,
                              render: (v: any, row: any) => {
                                if (isGroupNoCol) return String(row?.__preset_group_display_no ?? v ?? '');
                                if (isSortMarkCol) {
                                  return (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
                                      {renderSortMarkBadge(v)}
                                      <Tooltip title="添加到操作区域扫描框">
                                        <Button
                                          size="small"
                                          type="text"
                                          icon={<SwapLeftOutlined style={{ color: '#1677ff' }} />}
                                          disabled={isReadonly}
                                          onClick={() => addSortMarkToInput(v)}
                                          style={{ paddingInline: 4 }}
                                        />
                                      </Tooltip>
                                    </span>
                                  );
                                }
                                return String(v ?? '');
                              },
                              onCell: (row: any) => ({
                                rowSpan: isGroupNoCol ? Number(row?.__preset_group_row_span ?? 1) : 1,
                                style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'center', verticalAlign: 'middle' },
                              }),
                            } as any;
                            if (!isSortMarkCol) return [baseCol];
                            return [
                              baseCol,
                              {
                                title: '已分配仓位',
                                dataIndex: '__assigned_location_placeholder',
                                key: '__assigned_location_placeholder',
                                width: 120,
                                align: 'center' as const,
                                render: (v: any) => renderLocationBadge(v),
                                onCell: () => ({ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'center' } }),
                              },
                            ];
                          })}
                          locale={{ emptyText: '已存在 GROUP_LIST 基线文件，但暂无可展示分组列表数据' }}
                          tableLayout="fixed"
                        />
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            marginBottom: 10,
                            padding: '8px 10px',
                            border: '1px solid #ffe58f',
                            borderRadius: 6,
                            background: '#fffbe6',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                            <div style={{ flex: 1, minWidth: 260 }}>
                            <span style={{ color: '#595959', fontWeight: 600, marginRight: 8 }}>分组列表基线文件：</span>
                            <span style={{ color: '#8c8c8c' }} />
                            <span style={{ marginLeft: 10 }}>
                              <Button
                                size="small"
                                type="primary"
                                loading={groupListUploadLoading}
                                disabled={isReadonly}
                                onClick={triggerPickGroupListFile}
                              >
                                替换/上传
                              </Button>
                                <Button
                                  size="small"
                                  danger
                                  style={{ marginLeft: 5 }}
                                  loading={groupListDeleteLoading}
                                  disabled
                                  onClick={deleteGroupListBaselineFiles}
                                >
                                  删除
                                </Button>
                            </span>
                            </div>
                            <div style={{ minWidth: 360, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <span style={{ color: '#595959', fontWeight: 600 }}>未分组产品如何处理：</span>
                              <span style={{ ...ungroupedAnswerBadgeStyle }} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ color: '#595959', fontWeight: 600 }}>分拣标识搜索：</span>
                            <Input
                              size="small"
                              allowClear
                              value={groupListSearchKeyword}
                              onChange={(e) => setGroupListSearchKeyword(e.target.value)}
                              placeholder="输入分拣标识模糊搜索"
                              style={{ width: 220 }}
                            />
                          </div>
                        </div>
                        <div style={{ color: '#8c8c8c' }}>暂无预设分组列表</div>
                      </>
                    )}
                  </div>
                ),
              },
              {
                key: 'instruction-attachments',
                label: (
                  <span style={{ color: activeInfoTab === 'instruction-attachments' ? undefined : '#8c8c8c' }}>
                    {`指令问题与附件(${attachmentRows.length})`}
                  </span>
                ),
                children: (
                  <div>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>问题与答案</div>
                      <Table
                        size="small"
                        rowKey={(r: any, i?: number) => String(r?.key || i || 0)}
                        pagination={false}
                        dataSource={instructionQuestionAnswers.rows}
                        scroll={{ x: 'max-content' as any, y: 220 as any }}
                        columns={[
                          { title: '序号', dataIndex: '__idx', key: '__idx', width: 70, align: 'center' as any },
                          { title: '问题', dataIndex: 'question', key: 'question', width: 320, render: (v: any) => String(v || '-') },
                          { title: '答案', dataIndex: 'answer', key: 'answer', render: (v: any) => <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{String(v || '-')}</span> },
                        ] as any}
                        locale={{ emptyText: '暂无问题与答案' }}
                        tableLayout="fixed"
                      />
                    </div>
                    <div style={{ marginTop: 8, marginBottom: 8, borderTop: '1px solid #f0f0f0' }} />
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>指令附件</div>
                    <Table
                      size="small"
                      rowKey={(r: any) => String(r?.key || r?.id || r?.file_name)}
                      loading={attachmentsLoading}
                      pagination={{
                        size: 'small',
                        current: attachmentsPage,
                        pageSize: attachmentsPageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'] as any,
                        onChange: (p: number, ps: number) => {
                          setAttachmentsPage(p);
                          setAttachmentsPageSize(ps);
                        },
                        showTotal: (total: number) => `共 ${total} 条`,
                      }}
                      dataSource={attachmentRows}
                      scroll={{ x: 'max-content' as any }}
                      columns={[
                        {
                          title: '序号',
                          key: '__idx',
                          width: 70,
                          align: 'center' as any,
                          render: (_: any, __: any, idx: number) => ((attachmentsPage - 1) * attachmentsPageSize) + idx + 1,
                        },
                        {
                          title: '文件名',
                          dataIndex: 'file_name',
                          key: 'file_name',
                          render: (v: any) => String(v || '-'),
                        },
                        {
                          title: '操作',
                          key: 'actions',
                          width: 180,
                          align: 'center' as any,
                          render: (_: any, r: any) => {
                            const hasUrl = !!String(r?.url || '').trim();
                            return (
                              <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                <Button size="small" type="link" disabled={!hasUrl} onClick={() => { openAttachmentPreview(r); }}>
                                  预览
                                </Button>
                                <Button
                                  size="small"
                                  type="link"
                                  disabled={!hasUrl}
                                  onClick={() => { downloadAttachment(r); }}
                                >
                                  下载
                                </Button>
                              </div>
                            );
                          },
                        },
                      ] as any}
                      locale={{ emptyText: instructionId ? '当前指令暂无已上传附件' : '缺少指令ID，无法查询附件' }}
                    />
                  </div>
                ),
              },
            ]}
          />
        </section>

        <section style={{ ...panelStyle('#f6ffed', '#95de64'), minHeight: 320, overflow: 'hidden' }}>
          <div style={{ fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span>操作记录</span>
            <span style={{ color: '#595959', fontWeight: 600 }}>
              {`已分拣:${summaryStats.totalQty}件`}
            </span>
          </div>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <Table
              size="small"
              rowKey={(r: any, i?: number) => String(r?.id || i || 0)}
              pagination={{
                size: 'small',
                current: operationPage,
                pageSize: operationPageSize,
                total: operationRowsDesc.length,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['5', '10', '20', '50', '100'] as any,
                onChange: (p: number, ps: number) => {
                  setOperationPage(p);
                  setOperationPageSize(ps);
                },
                showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条,共${total}条记录`,
              }}
              dataSource={operationRowsDesc}
              scroll={{ x: 'max-content' as any }}
              columns={[
                { title: '序号', dataIndex: 'seq', key: 'seq', width: 70, align: 'center' as const, render: (v: any) => String(v || '-') },
                { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 240, align: 'center' as const, render: (v: any) => renderSortMarkBadge(v) },
                { title: '数量', dataIndex: 'qty', key: 'qty', width: 80, align: 'center' as const, render: (v: any) => String(v || '-') },
                { title: '仓位', dataIndex: 'location_no', key: 'location_no', width: 90, align: 'center' as const, render: (v: any) => renderLocationBadge(v) },
                { title: '时间', dataIndex: 'time_text', key: 'time_text', width: 140, align: 'center' as const, render: (v: any) => renderDateTimeTwoLines(v) },
                {
                  title: '操作',
                  key: 'actions',
                  width: 170,
                  align: 'center' as const,
                  render: (_: any, row: any) => (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Button size="small" disabled={isReadonly} onClick={() => openEditQtyModal(row)}>修改数量</Button>
                      <Button size="small" danger disabled={isReadonly} onClick={() => removeOperationRow(row)}>删除</Button>
                    </div>
                  ),
                },
              ] as any}
              locale={{ emptyText: '暂无操作记录' }}
              tableLayout="fixed"
            />
          </div>
        </section>

        <section style={{ ...panelStyle('#fff0f6', '#ff85c0'), minHeight: 320, overflow: 'hidden' }}>
          <div style={{ fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span>统计结果</span>
            <span style={{ color: '#595959', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <span>{`仓位:${summaryStats.locationCount}个`}</span>
              <span>{`分拣ID:${summaryStats.sortIdCount}个`}</span>
              <span style={{ color: '#d9d9d9' }}>|</span>
              <span>{`预估分拣目标总数量:${operationTargetEstimatedProductQtyTotal == null ? '-' : `${Math.max(0, Math.floor(operationTargetEstimatedProductQtyTotal))}件`}`}</span>
              <span>{`已分拣:${summaryStats.totalQty}件`}</span>
              <span
                style={{
                  padding: '1px 8px',
                  borderRadius: 4,
                  border: '1px solid #ffbb96',
                  background: '#fff2e8',
                  color: '#d4380d',
                  fontWeight: 800,
                }}
              >
                {`差额:${operationTargetEstimatedProductQtyTotal == null ? '-' : `${Math.floor(operationTargetEstimatedProductQtyTotal - summaryStats.totalQty)}件`}`}
              </span>
              <span style={{ color: '#d9d9d9' }}>|</span>
              <span>{`已生成容器:${generatedContainerTabSummary.containerCount}个`}</span>
            </span>
          </div>
          <Tabs
            size="small"
            items={[
              {
                key: 'stats-by-mark',
                label: `按分拣标识统计(${statsByMarkTabSummary.markCount}标识 ${statsByMarkTabSummary.totalQty}件)`,
                children: (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ color: '#595959', fontWeight: 600 }}>分拣标识筛选：</span>
                      <Input
                        size="small"
                        allowClear
                        value={statsMarkFilter}
                        onChange={(e) => { setStatsMarkFilter(e.target.value); setStatsMarkPage(1); }}
                        placeholder="输入分拣标识模糊匹配"
                        style={{ width: 220 }}
                      />
                    </div>
                    <Table
                      size="small"
                      rowKey={(r: any, i?: number) => String(r?.sorting_mark || i || 0)}
                      pagination={{
                        size: 'small',
                        current: statsMarkPage,
                        pageSize: statsMarkPageSize,
                        total: filteredStatsByMarkRows.length,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['5', '10', '20', '50', '100'] as any,
                        onChange: (p: number, ps: number) => {
                          setStatsMarkPage(p);
                          setStatsMarkPageSize(ps);
                        },
                        showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条,共${total}条记录`,
                      }}
                      dataSource={filteredStatsByMarkRows}
                      scroll={{ x: 'max-content' as any }}
                      columns={[
                        { title: '序号', dataIndex: '__idx', key: '__idx', width: 70, align: 'center' as const },
                        { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', align: 'center' as const, render: (v: any) => renderSortMarkBadge(v) },
                        { title: '分拣ID', dataIndex: 'sort_id', key: 'sort_id', width: 100, align: 'center' as const, render: (v: any) => String(v || '-') },
                        { title: '累计数量', dataIndex: 'total_qty', key: 'total_qty', width: 100, align: 'center' as const },
                        { title: '仓位', dataIndex: 'location_no', key: 'location_no', width: 100, align: 'center' as const, render: (v: any) => renderLocationBadge(v) },
                        { title: '最近时间', dataIndex: 'last_time', key: 'last_time', width: 140, align: 'center' as const, render: (v: any) => renderDateTimeTwoLines(v) },
                      ] as any}
                      locale={{ emptyText: '暂无统计数据' }}
                      tableLayout="fixed"
                    />
                  </div>
                ),
              },
              {
                key: 'stats-by-location',
                label: `仓位当前留存(${statsByLocationTabSummary.locationCount}仓位 ${statsByLocationTabSummary.totalQty}件)`,
                children: (
                  <div>
                    <Table
                      size="small"
                      rowKey={(r: any, i?: number) => String(r?.__rowKey || r?.location_no || i || 0)}
                      pagination={false}
                      dataSource={statsByLocationRows}
                      scroll={{ x: 'max-content' as any }}
                      columns={[
                        {
                          title: '序号',
                          dataIndex: '__idx',
                          key: '__idx',
                          width: 70,
                          align: 'center' as const,
                          onCell: (row: any) => ({
                            rowSpan: row?.__isGroupFirst ? row?.__groupRowSpan : 0,
                            style: { background: row?.__groupColor || undefined },
                          }),
                        },
                        {
                          title: '仓位',
                          dataIndex: 'location_no',
                          key: 'location_no',
                          width: 100,
                          align: 'center' as const,
                          render: (v: any) => renderLocationBadge(v),
                          onCell: (row: any) => ({
                            rowSpan: row?.__isGroupFirst ? row?.__groupRowSpan : 0,
                            style: { background: row?.__groupColor || undefined },
                          }),
                        },
                        {
                          title: '总数量',
                          dataIndex: 'total_qty',
                          key: 'total_qty',
                          width: 100,
                          align: 'center' as const,
                          onCell: (row: any) => ({
                            rowSpan: row?.__isGroupFirst ? row?.__groupRowSpan : 0,
                            style: { background: row?.__groupColor || undefined },
                          }),
                        },
                        {
                          title: '分拣标识与数量',
                          dataIndex: 'sorting_mark',
                          key: 'mark_qty_summary',
                          width: 260,
                          align: 'left' as const,
                          render: (_: any, row: any) => (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {renderSortMarkBadge(row?.sorting_mark)}
                              <span style={{ color: '#262626' }}>{`× ${Math.max(0, Number(row?.mark_qty || 0))}`}</span>
                            </div>
                          ),
                          onCell: (row: any) => ({ style: { background: row?.__groupColor || undefined } }),
                        },
                        {
                          title: '最近时间',
                          dataIndex: 'last_time',
                          key: 'last_time',
                          width: 140,
                          align: 'center' as const,
                          render: (v: any) => renderDateTimeTwoLines(v),
                          onCell: (row: any) => ({
                            rowSpan: row?.__isGroupFirst ? row?.__groupRowSpan : 0,
                            style: { background: row?.__groupColor || undefined },
                          }),
                        },
                        {
                          title: '操作',
                          key: 'actions',
                          width: 120,
                          align: 'center' as const,
                          render: (_: any, row: any) => (
                            <Button size="small" disabled={isReadonly} onClick={() => openTempBoxModal(row?.location_no)}>
                              放入容器
                            </Button>
                          ),
                          onCell: (row: any) => ({
                            rowSpan: row?.__isGroupFirst ? row?.__groupRowSpan : 0,
                            style: { background: row?.__groupColor || undefined },
                          }),
                        },
                      ] as any}
                      locale={{ emptyText: '暂无统计数据' }}
                      tableLayout="fixed"
                    />
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                      <Pagination
                        size="small"
                        current={statsLocationPage}
                        pageSize={statsLocationPageSize}
                        total={statsByLocationGroups.length}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={['5', '10', '20', '50', '100'] as any}
                        onChange={(p: number, ps: number) => {
                          setStatsLocationPage(p);
                          setStatsLocationPageSize(ps);
                        }}
                        showTotal={(total: number, range: [number, number]) => `第${range[0]}-${range[1]}组,共${total}组`}
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: 'stats-generated-containers',
                label: `已装入容器(${generatedContainerTabSummary.containerCount}容器 ${generatedContainerTabSummary.totalQty}件)`,
                children: (
                  <div>
                    <Table
                      size="small"
                      loading={generatedDraftContainersLoading}
                      rowKey={(r: any, i?: number) => String(r?.__rowKey || r?.system_code || i || 0)}
                      pagination={{
                        size: 'small',
                        current: generatedDraftContainersPage,
                        pageSize: generatedDraftContainersPageSize,
                        total: generatedDraftContainerDetailRows.length,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['5', '10', '20', '50', '100'] as any,
                        onChange: (p: number, ps: number) => {
                          setGeneratedDraftContainersPage(p);
                          setGeneratedDraftContainersPageSize(ps);
                        },
                        showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                      }}
                      dataSource={generatedDraftContainerDetailRows}
                      onRow={(row: any) => ({
                        style: {
                          background: Number(row?.__containerGroupIndex || 0) % 2 === 0 ? '#f0f9ff' : '#f6ffed',
                        },
                      })}
                      scroll={{ x: 'max-content' as any }}
                      columns={[
                        {
                          title: '容器系统编码',
                          dataIndex: 'system_code',
                          key: 'system_code',
                          width: 170,
                          align: 'center' as const,
                          onCell: (row: any) => ({ rowSpan: Number(row?.__containerRowSpan || 0) }),
                          render: (v: any) => <span style={{ fontWeight: 700, color: '#1677ff' }}>{String(v || '-')}</span>,
                        },
                        {
                          title: '库存单元',
                          dataIndex: 'unit_code',
                          key: 'unit_code',
                          width: 180,
                          align: 'center' as const,
                          onCell: (row: any) => ({ rowSpan: Number(row?.__unitRowSpan || 0) }),
                          render: (v: any) => String(v || '-'),
                        },
                        {
                          title: '分拣标识',
                          dataIndex: 'sorting_mark',
                          key: 'sorting_mark',
                          width: 220,
                          align: 'center' as const,
                          render: (v: any) => {
                            const txt = String(v || '').trim();
                            return txt ? renderSortMarkBadge(txt) : '-';
                          },
                        },
                        {
                          title: '数量',
                          dataIndex: 'qty',
                          key: 'qty',
                          width: 100,
                          align: 'center' as const,
                          render: (v: any) => (v == null ? '-' : String(Math.max(0, Number(v || 0)))),
                        },
                        {
                          title: '总数量',
                          dataIndex: 'total_qty',
                          key: 'total_qty',
                          width: 100,
                          align: 'center' as const,
                          onCell: (row: any) => ({ rowSpan: Number(row?.__containerRowSpan || 0) }),
                          render: (v: any) => String(Math.max(0, Number(v || 0))),
                        },
                        {
                          title: '备注',
                          dataIndex: 'remarks',
                          key: 'remarks',
                          width: 240,
                          align: 'center' as const,
                          onCell: (row: any) => ({ rowSpan: Number(row?.__containerRowSpan || 0) }),
                          render: (v: any) => {
                            const text = String(v || '').trim();
                            if (!text) return '-';
                            const maxLen = 20;
                            const shortText = text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
                            return (
                              <Tooltip title={text}>
                                <span>{shortText}</span>
                              </Tooltip>
                            );
                          },
                        },
                        {
                          title: '操作',
                          key: 'actions',
                          width: 120,
                          align: 'center' as const,
                          onCell: (row: any) => ({ rowSpan: Number(row?.__containerRowSpan || 0) }),
                          render: (_: any, row: any) => (
                            Number(row?.__containerRowSpan || 0) > 0 ? (
                              <Button size="small" disabled={isReadonly} onClick={() => openGeneratedDraftContainerLabelPreview(row)}>
                                打印标签
                              </Button>
                            ) : null
                          ),
                        },
                      ] as any}
                      locale={{ emptyText: '暂无草稿容器' }}
                      tableLayout="fixed"
                    />
                  </div>
                ),
              },
            ]}
          />
        </section>
        </div>
      </div>
      <input
        ref={groupListFileInputRef}
        type="file"
        accept=".csv,.xls,.xlsx"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const f = e?.target?.files?.[0];
          if (!f) return;
          if (isReadonly) return;
          await uploadGroupListBaselineFile(f);
        }}
      />
      <Modal
        open={tempBoxModalOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 36 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontWeight: 700 }}>放入容器</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#595959' }}>仓位:</span>
                {renderLocationBadge(tempBoxLocationNo || '-')}
              </span>
              <span style={{ color: '#595959', marginLeft: 8 }}>
                已分拣ID:{tempBoxStats.sortedIdCount}个
                {'  '}已分拣:{tempBoxStats.sortedQtyTotal}件
              </span>
              <span style={{ display: 'inline-block', width: 1, height: 14, background: '#d9d9d9', marginLeft: 5, marginRight: 5 }} />
              <span style={{ color: '#595959' }}>
                已选择ID:{tempBoxStats.selectedIdCount}个
                {'  '}已选择:{tempBoxStats.selectedQtyTotal}件.
              </span>
            </span>
          </div>
        )}
        onCancel={() => setTempBoxModalOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Table
          size="small"
          rowKey={(r: any, i?: number) => getTempBoxRowKey(r, i)}
          dataSource={tempBoxLocationRows}
          pagination={{
            current: tempBoxPage,
            pageSize: tempBoxPageSize,
            total: Array.isArray(tempBoxLocationRows) ? tempBoxLocationRows.length : 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
            onChange: (page: number, pageSize?: number) => {
              setTempBoxPage(page);
              if (pageSize && pageSize !== tempBoxPageSize) {
                setTempBoxPageSize(pageSize);
                setTempBoxPage(1);
              }
            },
            onShowSizeChange: (_current: number, size: number) => {
              setTempBoxPageSize(size);
              setTempBoxPage(1);
            },
          }}
          columns={[
            {
              title: (
                <Checkbox
                  checked={tempBoxHeaderCheckState.checked}
                  indeterminate={tempBoxHeaderCheckState.indeterminate}
                  disabled={isReadonly}
                  onChange={(e) => toggleTempBoxSelectAll(!!e?.target?.checked)}
                >
                  选择
                </Checkbox>
              ),
              key: 'selected',
              width: 90,
              align: 'center' as const,
              render: (_: any, row: any, idx: number) => {
                const key = getTempBoxRowKey(row, idx);
                const selected = tempBoxSelectionMap[key];
                const selectedQty = Math.max(0, Number(selected?.qty || 0));
                return (
                  <Checkbox
                    checked={!!selected?.checked && selectedQty > 0}
                    disabled={isReadonly}
                    onChange={(e) => toggleTempBoxRowChecked(row, idx, !!e?.target?.checked)}
                  />
                );
              },
            },
            { title: '序号', dataIndex: '__idx', key: '__idx', width: 70, align: 'center' as const },
            { title: '分拣ID', dataIndex: 'sort_id', key: 'sort_id', width: 100, align: 'center' as const, render: (v: any) => String(v || '-') },
            { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 240, align: 'center' as const, render: (v: any) => renderSortMarkBadge(v) },
            { title: '已分拣数量', dataIndex: 'sorted_qty', key: 'sorted_qty', width: 110, align: 'center' as const, render: (v: any) => String(Math.max(0, Number(v || 0))) },
            {
              title: '已选择数量',
              key: 'selected_qty',
              width: 140,
              align: 'center' as const,
              render: (_: any, row: any, idx: number) => {
                const key = getTempBoxRowKey(row, idx);
                const selected = tempBoxSelectionMap[key];
                const sortedQty = Math.max(0, Number(row?.sorted_qty || 0));
                const val = Math.max(0, Number(selected?.qty ?? sortedQty));
                return (
                  <InputNumber
                    min={0}
                    precision={0}
                    value={val}
                    disabled={isReadonly}
                    onChange={(v) => changeTempBoxRowQty(row, idx, Number(v || 0))}
                    style={{ width: 110 }}
                  />
                );
              },
            },
          ] as any}
          locale={{ emptyText: '当前仓位暂无已分拣产品记录' }}
          scroll={{ x: 'max-content' as any }}
          tableLayout="fixed"
        />
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => setTempBoxModalOpen(false)}>取消</Button>
          <Button
            style={(isReadonly || !Array.isArray(selectedTempBoxRows) || selectedTempBoxRows.length === 0)
              ? { background: '#d9d9d9', borderColor: '#d9d9d9', color: '#8c8c8c' }
              : { background: '#52c41a', borderColor: '#52c41a', color: '#ffffff' }}
            disabled={isReadonly || !Array.isArray(selectedTempBoxRows) || selectedTempBoxRows.length === 0}
            onClick={openAddToExistingContainerModal}
          >
            放入已有容器
          </Button>
          <Button
            type="primary"
            style={(isReadonly || !Array.isArray(selectedTempBoxRows) || selectedTempBoxRows.length === 0)
              ? { background: '#d9d9d9', borderColor: '#d9d9d9', color: '#8c8c8c' }
              : { background: '#1677ff', borderColor: '#1677ff', color: '#ffffff' }}
            disabled={isReadonly || !Array.isArray(selectedTempBoxRows) || selectedTempBoxRows.length === 0}
            onClick={openCreateTempContainerModal}
          >
            创建新容器
          </Button>
        </div>
      </Modal>
      <Modal
        open={tempBoxAddExistingOpen}
        title={(
          <span>
            选择已有容器（来源仓位:{tempBoxLocationNo || '-'}）
          </span>
        )}
        onCancel={() => {
          if (tempBoxAddExistingSubmitting) return;
          setTempBoxAddExistingOpen(false);
          setTempBoxAddExistingSearch('');
        }}
        onOk={submitAddToExistingContainer}
        confirmLoading={tempBoxAddExistingSubmitting}
        okButtonProps={{ disabled: isReadonly }}
        okText="确定放入"
        cancelText="取消"
        width={880}
        destroyOnClose
      >
        <div style={{ marginBottom: 10, color: '#595959' }}>
          仅显示当前指令下且来源仓位与当前仓位一致的草稿容器。
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
          <span style={{ color: '#595959' }}>目标容器</span>
          <Select
            showSearch
            allowClear
            loading={tempBoxAddExistingLoading}
            disabled={isReadonly}
            value={tempBoxAddExistingSelectedContainerId ?? undefined}
            options={tempBoxAddExistingOptions}
            placeholder="点击选择，或直接扫描/输入容器编码"
            optionFilterProp="label"
            filterOption={(input, option) => {
              const kw = String(input || '').trim().toUpperCase();
              if (!kw) return true;
              const label = String((option as any)?.label || '').toUpperCase();
              const code = String((option as any)?.rawCode || '').toUpperCase();
              return label.includes(kw) || code.includes(kw);
            }}
            onSearch={(v) => {
              const kw = String(v || '').trim();
              setTempBoxAddExistingSearch(kw);
              if (!kw) return;
              const hit = (Array.isArray(tempBoxAddExistingOptions) ? tempBoxAddExistingOptions : []).find((x: any) =>
                String(x?.rawCode || '').trim().toUpperCase() === kw.toUpperCase(),
              );
              if (hit) {
                setTempBoxAddExistingSelectedContainerId(Number(hit.value || 0));
              }
            }}
            onChange={(v) => {
              const id = Number(v || 0);
              setTempBoxAddExistingSelectedContainerId(Number.isFinite(id) && id > 0 ? id : null);
            }}
            notFoundContent="未找到可用容器"
            style={{ width: '100%' }}
          />
        </div>
      </Modal>
      <Modal
        open={tempBoxCreateContainerOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%' }}>
            <span style={{ fontWeight: 700 }}>创建新容器</span>
            <span style={{ color: '#595959', fontWeight: 600 }}>{tempBoxCreateSelectionSummary}</span>
          </div>
        )}
        onCancel={() => setTempBoxCreateContainerOpen(false)}
        onOk={submitCreateSortCountContainerDraft}
        confirmLoading={tempBoxCreateSubmitting}
        okButtonProps={{ disabled: isReadonly }}
        okText="确定"
        cancelText="取消"
        width={640}
        destroyOnClose
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ minWidth: 92 }}>容器类型</span>
            <Select
              showSearch
              optionFilterProp="label"
              value={tempBoxContainerType || undefined}
              disabled={isReadonly}
              loading={tempBoxContainerTypeLoading}
              options={tempBoxContainerTypeChoices}
              onChange={(v) => setTempBoxContainerType(String(v || ''))}
              placeholder="请选择容器类型"
              style={{ flex: 1 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ minWidth: 92, lineHeight: '32px' }}>备注</span>
            <Input.TextArea
              value={tempBoxContainerRemarks}
              disabled={isReadonly}
              onChange={(e) => setTempBoxContainerRemarks(String(e?.target?.value || ''))}
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder="可填写补充说明"
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={tempBoxManualMatchOpen}
        title="手动选择匹配库存单元"
        onCancel={() => {
          setTempBoxManualMatchOpen(false);
        }}
        onOk={submitTempBoxManualMatches}
        confirmLoading={tempBoxCreateSubmitting}
        okButtonProps={{ disabled: isReadonly }}
        okText="确认匹配并创建容器"
        cancelText="取消"
        width={1180}
        destroyOnClose
      >
        <div style={{ marginBottom: 10, color: '#595959' }}>
          分拣标识未唯一匹配，请为每条记录手动选择一个库存单元后继续。
        </div>
        <Table
          size="small"
          rowKey={(r: any, i?: number) => String(r?.row_key || i || 0)}
          pagination={false}
          dataSource={tempBoxManualMatchRows}
          scroll={{ x: 'max-content' as any }}
          columns={[
            { title: '分拣ID', dataIndex: 'sort_id', key: 'sort_id', width: 110, align: 'center' as const, render: (v: any) => String(v || '-') },
            { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 220, align: 'center' as const, render: (v: any) => renderSortMarkBadge(v) },
            { title: '数量', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'center' as const, render: (v: any) => String(Math.max(0, Number(v || 0))) },
            {
              title: '失败原因',
              dataIndex: 'reason',
              key: 'reason',
              width: 150,
              align: 'center' as const,
              render: (v: any) => {
                const key = String(v || '');
                if (key === 'duplicate_match') return '匹配到多个库存单元';
                if (key === 'not_found') return '未匹配到库存单元';
                if (key === 'empty_sorting_mark') return '分拣标识为空';
                if (key === 'manual_match_out_of_scope') return '手动匹配超出范围';
                return key || '-';
              },
            },
            {
              title: '手动选择库存单元',
              key: 'manual_inventory_unit',
              width: 460,
              render: (_: any, row: any) => {
                const key = String(row?.row_key || '');
                const options = (Array.isArray(row?.candidate_units) ? row.candidate_units : [])
                  .map((x: any) => {
                    const uid = Number(x?.id || 0);
                    const unitCode = String(x?.system_barcode || '').trim() || '-';
                    const cachedItems = uid > 0 ? (operationTargetUnitContentCache[String(uid)] || []) : [];
                    const contentText = formatInventoryUnitContentItemsText(cachedItems as any);
                    return {
                      value: uid,
                      label: contentText && contentText !== '-'
                        ? `${unitCode} ${contentText}`
                        : unitCode,
                    };
                  })
                  .filter((x: any) => Number(x.value) > 0);
                return (
                  <Select
                    showSearch
                    optionFilterProp="label"
                    value={(tempBoxManualMatchSelection as any)?.[key] ?? undefined}
                    disabled={isReadonly}
                    options={options}
                    onChange={(v) => {
                      setTempBoxManualMatchSelection((prev) => ({
                        ...(prev || {}),
                        [key]: Number(v || 0) || null,
                      }));
                    }}
                    placeholder="请选择库存单元"
                    style={{ width: 430, maxWidth: '100%' }}
                  />
                );
              },
            },
          ] as any}
          locale={{ emptyText: '暂无需要手动匹配的记录' }}
          tableLayout="fixed"
        />
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>装入容器预检测</span>
            <Button
              size="small"
              onClick={() => {
                Modal.info({
                  title: '装入容器预检测说明',
                  width: 720,
                  content: (
                    <div style={{ lineHeight: 1.8 }}>
                      <div>
                        按照分拣标识匹配项目中已存在的LIU,并使用此LIU来作为仓储容器内容物记录。
                      </div>
                      <div style={{ marginTop: 8 }}>分为3种情况:</div>
                      <div>1 已匹配到LIU,且LIU的物理信息已确认,则可以直接执行</div>
                      <div>2 已匹配到LIU,但LIU的物理信息未确认,需先确认物理信息</div>
                      <div>
                        3 未匹配到任何已存在的LIU,需先创建LIU记录(此种情况会进一步按照匹配标识检测项目中是否已存在此产品,未存在此产品则会先创建新产品并关联到此项目,然后使用此产品创建LIU)
                      </div>
                    </div>
                  ),
                });
              }}
              style={{ color: '#1677ff', borderColor: '#1677ff', fontWeight: 400 }}
            >
              说明
            </Button>
          </div>
        )}
        open={need2PrecheckOpen}
        onCancel={() => resetNeed2PrecheckState()}
        width={900}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => resetNeed2PrecheckState()}>
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={submitNeed2PrecheckContinue}
            disabled={isReadonly || need2PrecheckStats.misses > 0 || need2PrecheckStats.unconfirmed > 0}
          >
            确认并放入容器
          </Button>,
        ]}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8, marginBottom: 12 }}>
          <Card size="small">
            <div style={{ color: '#8c8c8c' }}>检测总数</div>
            <div style={{ fontWeight: 700 }}>{need2PrecheckStats.totalCandidates}</div>
          </Card>
          <Card size="small">
            <div style={{ color: '#8c8c8c' }}>
              <Tooltip title="按照分拣标识未匹配到项目已有的LIU库存单元,需要创建库存单元">
                <span style={{ cursor: 'help' }}>未匹配</span>
              </Tooltip>
            </div>
            <div style={{ fontWeight: 700, color: '#cf1322' }}>{need2PrecheckStats.misses}</div>
          </Card>
          <Card size="small">
            <div style={{ color: '#8c8c8c' }}>
              <Tooltip title="按照分拣标识匹配到项目已有的LIU库存单元,该库存单元需确认物理信息">
                <span style={{ cursor: 'help' }}>已匹配-待确认物理信息</span>
              </Tooltip>
            </div>
            <div style={{ fontWeight: 700, color: '#fa8c16' }}>{need2PrecheckStats.unconfirmed}</div>
          </Card>
          <Card size="small">
            <div style={{ color: '#8c8c8c' }}>
              <Tooltip title="按照分拣标识匹配到项目已有的LIU库存单元,且该库存单元已确认物理信息,可直接放入容器">
                <span style={{ cursor: 'help' }}>已匹配-可直接执行</span>
              </Tooltip>
            </div>
            <div style={{ fontWeight: 700, color: '#1677ff' }}>{need2PrecheckStats.ready}</div>
          </Card>
        </div>
        {need2PrecheckStats.misses > 0 || need2PrecheckStats.unconfirmed > 0 ? (
          need2PrecheckDetailText ? (
            <Alert type="warning" showIcon message={need2PrecheckDetailText} style={{ marginBottom: 10 }} />
          ) : null
        ) : (
          <Alert
            type="success"
            showIcon={false}
            message={(
              <span style={{ color: '#389e0d' }}>
                <span style={{ marginRight: 6 }}>√</span>
                所有记录预检测通过,已匹配LIU并确认物理信息,可执行放入容器操作
              </span>
            )}
            style={{ marginBottom: 10, borderColor: '#b7eb8f', background: '#f6ffed' }}
          />
        )}
        {need2PrecheckStats.misses > 0 ? (
          <Card
            size="small"
            title={<span style={{ color: '#cf1322' }}>未匹配到已存在的LIU库存单元-需先创建LIU</span>}
            style={{ marginBottom: 10, borderColor: '#ffccc7' }}
            extra={(
              <Button
                size="small"
                type="primary"
                disabled={isReadonly}
                onClick={openNeed2MissingCreateModal}
              >
                创建库存单元
              </Button>
            )}
          >
            <div>
              <div style={{ marginBottom: 8, color: '#595959' }}>
                检测到 {need2PrecheckStats.misses} 条未匹配记录。可点击右侧按钮逐条创建 LIU。
              </div>
              <div style={{ maxHeight: 140, overflowY: 'auto', color: '#8c8c8c' }}>
                {(need2PrecheckMisses || []).slice(0, 20).map((m: any, idx: number) => (
                  <div key={`pre-miss-${idx}`}>
                    {idx + 1}. 分拣ID:{String(m?.sort_id || '-')} 分拣标识:{String(m?.sorting_mark || '-')} 数量:{Math.max(0, Number(m?.quantity || 0))}
                  </div>
                ))}
                {Array.isArray(need2PrecheckMisses) && need2PrecheckMisses.length > 20 ? <div>...其余 {need2PrecheckMisses.length - 20} 条已省略</div> : null}
              </div>
            </div>
          </Card>
        ) : null}
        {need2PrecheckStats.unconfirmed > 0 ? (
          <Card
            size="small"
            title={<span style={{ color: '#fa8c16' }}>已匹配LIU-待确认物理信息</span>}
            style={{ marginBottom: 10, borderColor: '#ffe58f' }}
            extra={
              <Button
                size="small"
                type="primary"
                disabled={isReadonly}
                onClick={triggerNeed2WeightConfirmFromPrecheck}
              >
                确认物理信息
              </Button>
            }
          >
            <div>
              <div style={{ marginBottom: 8, color: '#595959' }}>
                检测到 {need2PrecheckStats.unconfirmed} 条已匹配的LIU记录需要确认物理信息,点击右侧&quot;确认物理信息&quot;按钮逐条确认
              </div>
              <div style={{ maxHeight: 140, overflowY: 'auto', color: '#8c8c8c' }}>
                {(need2PrecheckUnconfirmedUnits || []).slice(0, 20).map((u: any, idx: number) => {
                  const sortingMarkText = String(u?.sorting_mark_text || '').trim()
                    || (Array.isArray(u?.sorting_marks) ? u.sorting_marks.map((x: any) => String(x || '').trim()).filter((x: string) => !!x).join('|') : '')
                    || '-';
                  const sizeText = `${Math.max(0, Number(u?.length || 0))}×${Math.max(0, Number(u?.width || 0))}×${Math.max(0, Number(u?.height || 0))}cm`;
                  return (
                    <div key={`pre-unconfirmed-${String(u?.id || idx)}`}>
                      {idx + 1}. LIU:{String(u?.system_barcode || '-')} 分拣标识:{sortingMarkText} 声明重量:{Math.max(0, Number(u?.weight || 0))}g 声明尺寸:{sizeText}
                    </div>
                  );
                })}
                {Array.isArray(need2PrecheckUnconfirmedUnits) && need2PrecheckUnconfirmedUnits.length > 20 ? <div>...其余 {need2PrecheckUnconfirmedUnits.length - 20} 条已省略</div> : null}
              </div>
            </div>
          </Card>
        ) : null}
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingRight: 36 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              创建LIU库存单元
              {need2MissingCurrentRow ? (
                <span style={{ marginLeft: 6, color: '#595959', fontWeight: 400, display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span>分拣标识:</span>
                  <span style={{ ...sortMarkBadgeStyle, fontWeight: 600, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {String((need2MissingCurrentRow as any)?.sorting_mark || '').trim() || '-'}
                  </span>
                </span>
              ) : null}
            </span>
            <span
              style={{
                color: Math.min((need2MissingCreateIdx || 0) + 1, (need2MissingCreateRows || []).length || 1) >= ((need2MissingCreateRows || []).length || 1)
                  ? '#52c41a'
                  : '#ff4d4f',
                marginRight: 10,
                flexShrink: 0,
              }}
            >
              {`进度:${Math.min((need2MissingCreateIdx || 0) + 1, (need2MissingCreateRows || []).length || 1)}/${(need2MissingCreateRows || []).length || 1}`}
            </span>
          </div>
        )}
        open={need2MissingCreateOpen}
        onCancel={() => {
          if (need2MissingCreateSubmitting) return;
          closeNeed2MissingCreateModal();
        }}
        width={680}
        destroyOnClose
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              if (need2MissingCreateSubmitting) return;
              closeNeed2MissingCreateModal();
            }}
            disabled={need2MissingCreateSubmitting}
          >
            取消
          </Button>,
          <Button
            key="create"
            type="primary"
            loading={need2MissingCreateSubmitting}
            onClick={submitNeed2MissingCreate}
            disabled={isReadonly || need2MissingCreateLoading || need2MissingCreateSubmitting || !need2MissingCurrentRow}
          >
            创建并继续
          </Button>,
        ]}
      >
        {need2MissingCurrentRow ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Card size="small" title="待处理分拣记录">
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 6 }}>
                <div style={{ color: '#8c8c8c' }}>分拣ID</div>
                <div>{String((need2MissingCurrentRow as any)?.sort_id || '-')}</div>
                <div style={{ color: '#8c8c8c' }}>分拣标识</div>
                <div><Tag color="blue">{String((need2MissingCurrentRow as any)?.sorting_mark || '-')}</Tag></div>
                <div style={{ color: '#8c8c8c' }}>数量</div>
                <div>{Math.max(0, Number((need2MissingCurrentRow as any)?.quantity || 0))}</div>
              </div>
            </Card>
            <Card size="small" title="产品匹配结果" loading={need2MissingCreateLoading}>
              {need2MissingCreateContext?.matched_product ? (
                <div style={{ color: '#1677ff' }}>
                  {(() => {
                    const p = need2MissingCreateContext?.matched_product || {};
                    const name = String(p?.name || '-');
                    const customCode = String(p?.custom_code || '-');
                    const codes = Array.isArray(p?.sorting_codes)
                      ? p.sorting_codes.map((x: any) => String(x || '').trim()).filter((x: string) => !!x)
                      : [];
                    const codeText = codes.length > 0 ? codes.join('|') : '-';
                    return `已匹配项目产品：${name} (${customCode} ${codeText})`;
                  })()}
                </div>
              ) : (
                <div style={{ color: '#cf1322' }}>
                  未匹配到项目产品：将创建“分拣操作未知产品”（默认价值 1 USD）并绑定项目，再创建 LIU。
                </div>
              )}
            </Card>
            <Card size="small" title="LIU物理信息（可修改）">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>重量</span>
                  <InputNumber
                    min={1}
                    addonAfter="克"
                    value={need2MissingCreateForm.weight_g}
                    disabled={isReadonly}
                    onChange={(v) => setNeed2MissingCreateForm((prev) => ({ ...prev, weight_g: Number(v || 0) }))}
                    style={{ width: 170 }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>长度</span>
                  <InputNumber
                    min={1}
                    addonAfter="厘米"
                    value={need2MissingCreateForm.length_cm}
                    disabled={isReadonly}
                    onChange={(v) => setNeed2MissingCreateForm((prev) => ({ ...prev, length_cm: Number(v || 0) }))}
                    style={{ width: 170 }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>宽度</span>
                  <InputNumber
                    min={1}
                    addonAfter="厘米"
                    value={need2MissingCreateForm.width_cm}
                    disabled={isReadonly}
                    onChange={(v) => setNeed2MissingCreateForm((prev) => ({ ...prev, width_cm: Number(v || 0) }))}
                    style={{ width: 170 }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>高度</span>
                  <InputNumber
                    min={1}
                    addonAfter="厘米"
                    value={need2MissingCreateForm.height_cm}
                    disabled={isReadonly}
                    onChange={(v) => setNeed2MissingCreateForm((prev) => ({ ...prev, height_cm: Number(v || 0) }))}
                    style={{ width: 170 }}
                  />
                </div>
                <div style={{ gridColumn: '1 / span 2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>自带物流包装</span>
                  <Switch
                    checked={!!need2MissingCreateForm.logistics_package}
                    disabled={isReadonly}
                    onChange={(checked) => setNeed2MissingCreateForm((prev) => ({ ...prev, logistics_package: !!checked }))}
                  />
                </div>
              </div>
            </Card>
          </div>
        ) : (
          <Alert type="success" showIcon message="未匹配记录已处理完成" />
        )}
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingRight: 40 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              重量尺寸确认
              {need2CurrentUnit ? (
                <span style={{ marginLeft: 6, color: '#595959', fontWeight: 400, display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span>分拣标识:</span>
                  <span style={{ ...sortMarkBadgeStyle, fontWeight: 600, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {String((need2CurrentUnit as any)?.sorting_mark_text || '').trim()
                      || (Array.isArray((need2CurrentUnit as any)?.sorting_marks) ? (need2CurrentUnit as any).sorting_marks.join('|') : '')
                      || '-'}
                  </span>
                </span>
              ) : null}
            </span>
            {need2ProgressMeta.total > 0 ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: need2ProgressMeta.done ? '#52c41a' : '#ff4d4f', marginRight: 10, flexShrink: 0 }}>
                <span>{`进度:${need2ProgressMeta.current}/${need2ProgressMeta.total}`}</span>
              </span>
            ) : null}
          </div>
        )}
        open={need2WeightConfirmOpen}
        onCancel={() => {
          if (need2WeightConfirmSubmitting) return;
          resetNeed2WeightConfirmState();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              if (need2WeightConfirmSubmitting) return;
              resetNeed2WeightConfirmState();
            }}
            disabled={need2WeightConfirmSubmitting}
          >
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={need2WeightConfirmSubmitting}
            onClick={submitNeed2WeightConfirm}
            disabled={isReadonly || need2WeightConfirmSubmitting}
          >
            {need2WeightConfirmSubmitting ? '确认中...' : '确认并继续'}
          </Button>,
        ]}
        width={600}
        destroyOnClose
      >
        {need2CurrentUnit ? (
          <div style={{ padding: '20px 0' }}>
            <Card size="small" title="库存单元信息" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>当前进度：</span>
                <span>{`${Math.min((need2CurrentUnitIdx || 0) + 1, (need2UnconfirmedUnits || []).length)} / ${(need2UnconfirmedUnits || []).length}`}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>库存单元编码：</span>
                <span>{String(need2CurrentUnit?.system_barcode || '-')}</span>
              </div>
            </Card>
            <Card size="small" title="客户声明重量尺寸" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>重量：</span>
                <span>{`${Math.max(0, Number(need2CurrentUnit?.weight || 0))} 克`}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>长度：</span>
                <span>{`${Math.max(0, Number(need2CurrentUnit?.length || 0))} 厘米`}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>宽度：</span>
                <span>{`${Math.max(0, Number(need2CurrentUnit?.width || 0))} 厘米`}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>高度：</span>
                <span>{`${Math.max(0, Number(need2CurrentUnit?.height || 0))} 厘米`}</span>
              </div>
            </Card>
            <Card size="small" title="实际重量尺寸（可修改）">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>重量：</span>
                  <InputNumber
                    min={1}
                    addonAfter="克"
                    value={need2WeightForm.actual_weight}
                    disabled={isReadonly}
                    onChange={(v) => setNeed2WeightForm((prev) => ({ ...prev, actual_weight: Number(v || 0) }))}
                    style={{ width: 150 }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>长度：</span>
                  <InputNumber
                    min={1}
                    addonAfter="厘米"
                    value={need2WeightForm.actual_length}
                    disabled={isReadonly}
                    onChange={(v) => setNeed2WeightForm((prev) => ({ ...prev, actual_length: Number(v || 0) }))}
                    style={{ width: 150 }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>宽度：</span>
                  <InputNumber
                    min={1}
                    addonAfter="厘米"
                    value={need2WeightForm.actual_width}
                    disabled={isReadonly}
                    onChange={(v) => setNeed2WeightForm((prev) => ({ ...prev, actual_width: Number(v || 0) }))}
                    style={{ width: 150 }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>高度：</span>
                  <InputNumber
                    min={1}
                    addonAfter="厘米"
                    value={need2WeightForm.actual_height}
                    disabled={isReadonly}
                    onChange={(v) => setNeed2WeightForm((prev) => ({ ...prev, actual_height: Number(v || 0) }))}
                    style={{ width: 150 }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>自带物流包装：</span>
                  <Switch
                    checked={!!need2WeightForm.logistics_package}
                    disabled={isReadonly}
                    onChange={(checked) => setNeed2WeightForm((prev) => ({ ...prev, logistics_package: !!checked }))}
                  />
                </div>
              </div>
            </Card>
          </div>
        ) : null}
      </Modal>
      <StorageLabelPreview
        visible={labelPreviewVisible}
        data={labelPreviewData}
        onClose={() => {
          setLabelPreviewVisible(false);
          setLabelPreviewData(undefined);
        }}
        onPrint={async (data) => {
          if (isReadonly) return;
          try {
            const printResult = await storageLabelService.printLabel(data);
            if ((printResult as any)?.success) {
              message.success(`标签打印成功！任务ID：${(printResult as any).printJobId || (printResult as any).print_job_id || ''}`);
            } else {
              message.error(`标签打印失败：${(printResult as any)?.message || ''}`);
            }
          } catch (error) {
            const errorInfo: any = handleLabelError ? handleLabelError(error, '打印') : { details: (error as any)?.message || '未知错误' };
            message.error(`标签打印失败：${errorInfo.details}`);
          }
        }}
        onDownload={async (data) => {
          if (isReadonly) return;
          try {
            const blob = await storageLabelService.downloadLabel(data);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `仓储容器标签_${(data as any)?.containerInfo?.systemCode || 'label'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('标签下载成功！');
          } catch (error) {
            const errorInfo: any = handleLabelError ? handleLabelError(error, '下载') : { details: (error as any)?.message || '未知错误' };
            message.error(`标签下载失败：${errorInfo.details}`);
          }
        }}
      />
      <Modal
        title="分拣清点阶段流程说明"
        open={flowGuideOpen}
        onCancel={() => setFlowGuideOpen(false)}
        footer={<Button type="primary" onClick={() => setFlowGuideOpen(false)}>关闭</Button>}
        width={980}
        destroyOnClose
      >
        <div style={{ lineHeight: 1.8, color: '#262626', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              borderRadius: 8,
              padding: '10px 12px',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>1 整体逻辑</div>
            <div><span style={{ fontWeight: 600 }}>装箱阶段：</span>只生成草稿（容器/内容物 + 需要的话新产品、新IU、新分拣组、分拣组移除计划等草稿意图）。</div>
            <div><span style={{ fontWeight: 600 }}>阶段提交：</span>仅提交分拣结果快照与阶段状态，不做正式库存账务与主数据发布。</div>
            <div><span style={{ fontWeight: 600 }}>指令终审：</span>统一原子执行发布与结算（分拣组调整、库存占用处理/增减、状态推进、库龄继承落账），并将草稿转正式。</div>

            <div style={{ fontWeight: 600, margin: '10px 0 4px' }}>库龄继承</div>
            <div>1) 当前默认采用“加权平均库龄”给产出 IU 赋初始库龄，计算公式：</div>
            <div style={{ marginLeft: 12 }}>加权平均库龄 = Σ(来源库龄天数 × 对应数量) / Σ数量</div>
            <div>2) 同一次拆解中产出的所有新 IU（不论种类和数量），先统一赋值为该加权平均库龄。</div>
            <div>3) 计算示例A（BIU 拆 LIU）：30天×2件、60天×3件，则平均库龄 = (30×2 + 60×3) / 5 = 48天。</div>
            <div>4) 计算示例B（LIU 细分）：30天×3件、60天×7件，则平均库龄 = (30×3 + 60×7) / 10 = 51天。</div>
          </div>

          <div
            style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 8,
              padding: '10px 12px',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>2 支持的三种分拣需求说明</div>

            <div style={{ marginBottom: 10 }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 8px',
                  borderRadius: 4,
                  border: '1px solid #1677ff',
                  color: '#1677ff',
                  background: '#f0f5ff',
                  fontWeight: 600,
                }}
              >
                需求1
              </span>
              <div style={{ marginTop: 6 }}>将多款入库时混合在一起的散货库存单元(LIU)或者箱库存单元(BIU)分拣开,将按照指令选定的分拣策略(标准模式/分组模式)重新为每种库存单元设定入库分拣组,以后这些库存单元再有入库时将按照新的入库分拣组设定执行入库;</div>
              <div>此场景仅支持条码分拣;</div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 8px',
                  borderRadius: 4,
                  border: '1px solid #1677ff',
                  color: '#1677ff',
                  background: '#f0f5ff',
                  fontWeight: 600,
                }}
              >
                需求2
              </span>
              <div style={{ marginTop: 6 }}>将某款箱库存单元(BIU)或者托盘库存单元(PIU)拆开后执行分拣,分拣出的不同产品将转换为散货库存单元(LIU),将按照指令选定的分拣策略(标准模式/分组模式)为每种散货库存单元设定入库分拣组;</div>
              <div>此场景仅支持条码分拣;</div>
              <div>此场景限每个指令只能处理一款箱库存单元或者托盘库存单元,处理数量不限;</div>
            </div>

            <div>
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 8px',
                  borderRadius: 4,
                  border: '1px solid #1677ff',
                  color: '#1677ff',
                  background: '#f0f5ff',
                  fontWeight: 600,
                }}
              >
                需求3
              </span>
              <div style={{ marginTop: 6 }}>将某款散货库存单元(LIU)再按照特定规则分拣开,如:同一款产品按照不同的分拣条码分开(每个分拣条码可能适用于不同的平台店铺),或者按照产品不同特征分开等(如包装方式/产品颜色/型号等);</div>
              <div>将生成全新的变体产品和变体散货库存单元,并将按照指令选定的分拣策略(标准模式/分组模式)为每种变体散货库存单元设定入库分拣组;</div>
              <div>此场景支持条码分拣/目视分拣/其他分拣方式;</div>
              <div>此场景限每个指令只能处理一款散货库存单元,处理数量不限;</div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        title="请选择源BIU"
        open={need1BiuMatchPickerOpen}
        onCancel={() => {
          setNeed1BiuMatchPickerOpen(false);
          setNeed1BiuMatchPickerCandidates([]);
          setNeed1BiuMatchPickerSelectedCode('');
        }}
        onOk={() => {
          const pickedCode = String(need1BiuMatchPickerSelectedCode || '').trim();
          if (!pickedCode) {
            message.warning('请选择源BIU');
            return;
          }
          setNeed1BiuMatchPickerOpen(false);
          setNeed1BiuMatchPickerCandidates([]);
          setNeed1BiuMatchPickerSelectedCode('');
          setSortInputValue(pickedCode);
          setSortInputQty(Math.max(1, Number(need1BiuMatchPendingQty || 1)));
          setTimeout(() => {
            try { sortInputRef.current?.focus?.(); } catch {}
            handleSortInputSubmit();
          }, 0);
        }}
        okText="确认"
        cancelText="取消"
        width={1100}
        destroyOnClose
      >
        <div style={{ color: '#595959', marginBottom: 8 }}>
          {`扫描值 ${String(need1BiuMatchPendingScanText || '-')} 命中多个操作目标BIU，请选择本次来源BIU。`}
        </div>
        <Table
          size="small"
          rowKey={(r: any) => String(r?.code || '-')}
          pagination={false}
          dataSource={Array.isArray(need1BiuMatchPickerCandidates) ? need1BiuMatchPickerCandidates : []}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: need1BiuMatchPickerSelectedCode ? [need1BiuMatchPickerSelectedCode] : [],
            onChange: (_keys: React.Key[], rows: any[]) => {
              const picked = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
              setNeed1BiuMatchPickerSelectedCode(String(picked?.code || '').trim());
            },
            getCheckboxProps: (record: any) => ({ disabled: !String(record?.code || '').trim() }),
          }}
          columns={[
            {
              title: '库存单元系统编码',
              dataIndex: 'code',
              key: 'code',
              width: 220,
              render: (v: any) => String(v || '-'),
            },
            {
              title: '库存单元内容项',
              dataIndex: 'content_items_lines',
              key: 'content_items_lines',
              render: (v: any) => {
                const lines = Array.isArray(v) ? v : [];
                if (lines.length <= 0) return '-';
                return (
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {lines.map((line: any, idx: number) => (
                      <div key={`biu-item-line-${idx}`}>{String(line || '-')}</div>
                    ))}
                  </div>
                );
              },
            },
          ] as any}
        />
      </Modal>
      <Modal
        open={locationLabelPreviewOpen}
        title={locationLabelPreviewTitle}
        onCancel={() => {
          setLocationLabelPreviewOpen(false);
          setLocationLabelPayload(null);
          revokeLocationLabelPdfUrl();
          setLocationLabelPreviewPdfUrl('');
        }}
        width={900}
        destroyOnClose
        footer={[
          <Button
            key="close"
            onClick={() => {
              setLocationLabelPreviewOpen(false);
              setLocationLabelPayload(null);
              revokeLocationLabelPdfUrl();
              setLocationLabelPreviewPdfUrl('');
            }}
          >
            关闭
          </Button>,
          <Button key="download" onClick={downloadLocationLabel} disabled={isReadonly || !locationLabelPayload}>下载</Button>,
          <Button key="print" type="primary" onClick={printLocationLabel} disabled={isReadonly || !locationLabelPreviewPdfUrl}>打印</Button>,
        ]}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {locationLabelPreviewLoading ? (
            <div style={{ color: '#8c8c8c' }}>正在生成标签...</div>
          ) : locationLabelPreviewPdfUrl ? (
            <iframe
              ref={locationLabelIframeRef}
              title="仓位标签预览"
              src={locationLabelPreviewPdfUrl}
              style={{ width: '100%', height: 680, border: '1px solid #d9d9d9', background: '#fff' }}
            />
          ) : (
            <div style={{ color: '#8c8c8c' }}>暂无预览内容</div>
          )}
        </div>
      </Modal>
      <Modal
        title="修改数量"
        open={editQtyModalOpen}
        onOk={submitEditQty}
        okButtonProps={{ disabled: isReadonly }}
        onCancel={() => {
          setEditQtyModalOpen(false);
          setEditQtyRowId('');
        }}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ color: '#595959' }}>分拣标识：</span>
          {renderSortMarkBadge(currentEditingOperationRow?.sorting_mark)}
          <span style={{ color: '#595959', marginLeft: 12 }}>仓位：</span>
          {renderLocationBadge(currentEditingOperationRow?.location_no)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>数量：</span>
          <InputNumber
            min={1}
            precision={0}
            value={editQtyValue}
            disabled={isReadonly}
            onChange={(v) => setEditQtyValue(Math.max(1, Number(v || 1)))}
            style={{ width: 180 }}
          />
        </div>
      </Modal>
      <Modal
        open={previewOpen}
        title={previewTitle}
        onCancel={closePreviewModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {previewLoading ? (
          <div style={{ color: '#8c8c8c' }}>加载中...</div>
        ) : previewError ? (
          <div style={{ color: '#cf1322' }}>{previewError}</div>
        ) : previewType === 'pdf' ? (
          <iframe
            title={previewTitle}
            src={previewUrl}
            style={{ width: '100%', height: '70vh', border: 0 }}
          />
        ) : previewType === 'image' ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={previewUrl}
              alt={previewTitle}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </div>
        ) : previewType === 'video' ? (
          <video
            controls
            src={previewUrl}
            style={{ width: '100%', maxHeight: '70vh', background: '#000' }}
          />
        ) : previewType === 'text' ? (
          <pre
            style={{
              margin: 0,
              maxHeight: '70vh',
              overflow: 'auto',
              padding: 12,
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              background: '#fafafa',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {previewText || '（空内容）'}
          </pre>
        ) : previewType === 'csv' || previewType === 'xlsx' ? (
          <div>
            {previewContext === 'group-list-baseline' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Button
                  size="small"
                  type="primary"
                  disabled={isReadonly || !previewDirty}
                  loading={groupListSaveLoading}
                  onClick={saveEditedGroupListBaseline}
                >
                  保存修改
                </Button>
                <span style={{ color: '#8c8c8c' }}>{isReadonly ? '当前阶段已完成或已跳过，基线文件只读' : '点击单元格可直接编辑'}</span>
                <span style={{ color: previewDirty ? '#d48806' : '#8c8c8c' }}>
                  {previewDirty ? '存在未保存修改' : '未修改'}
                </span>
              </div>
            ) : null}
            {previewType === 'xlsx' && previewSheetNames.length > 1 && previewContext !== 'group-list-baseline' ? (
              <Tabs
                size="small"
                activeKey={previewActiveSheet || previewSheetNames[0]}
                onChange={(sheetName) => {
                  const name = String(sheetName || '').trim();
                  setPreviewActiveSheet(name);
                  const cached = previewXlsxCacheRef.current?.[name];
                  if (cached) {
                    setPreviewCols(cached.cols || []);
                    setPreviewData(cached.data || []);
                    return;
                  }
                  try {
                    const wb = previewXlsxWbRef.current;
                    const XLSX = previewXlsxLibRef.current;
                    const sh = wb?.Sheets?.[name];
                    const aoa: any[] = sh ? XLSX.utils.sheet_to_json(sh, { header: 1, defval: '', blankrows: false }) : [];
                    const arr = Array.isArray(aoa) ? aoa.filter((r: any) => Array.isArray(r) && r.length > 0) : [];
                    if (arr.length === 0) {
                      setPreviewCols([]);
                      setPreviewData([]);
                      return;
                    }
                    const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
                    const dataRows = arr.slice(1);
                    const cols = headers.map((h: any, i: number) => ({
                      title: String(h || `列${i + 1}`),
                      dataIndex: `c${i}`,
                      align: 'center',
                    }));
                    const data = dataRows.map((r: any[], i: number) => {
                      const obj: any = { __idx: i + 2, __rowIndex: i };
                      cols.forEach((c: any, ci: number) => { obj[c.dataIndex] = String(r?.[ci] ?? ''); });
                      return obj;
                    });
                    previewXlsxCacheRef.current[name] = { cols, data };
                    setPreviewCols(cols);
                    setPreviewData(data);
                  } catch {
                    setPreviewCols([]);
                    setPreviewData([]);
                  }
                }}
                items={previewSheetNames.map((name) => ({
                  key: name,
                  label: <span style={{ fontFamily: 'monospace' }}>{name}</span>,
                }))}
              />
            ) : null}
            <Table
              size="small"
              rowKey={(r: any, i?: number) => String(r?.__idx ?? i ?? 0)}
              dataSource={previewData}
              pagination={{
                pageSize: previewPageSize,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100] as any,
                onShowSizeChange: (_: number, ps: number) => setPreviewPageSize(ps),
                onChange: (_p: number, ps: number) => { if (ps !== previewPageSize) setPreviewPageSize(ps); },
              }}
              columns={(() => {
                const base = (previewCols || []).filter(Boolean);
                const cols = [{ title: '行号', dataIndex: '__idx', align: 'center', width: 80 }, ...base];
                return cols.map((col: any) => ({
                  ...col,
                  render: (value: any, row: any) => {
                    const isEditableCell = (
                      previewContext === 'group-list-baseline'
                      && String(col?.dataIndex || '') !== '__idx'
                      && !isReadonly
                    );
                    if (!isEditableCell) return String(value ?? '');
                    const dataIndex = String(col?.dataIndex || '');
                    const rowIdx = Math.max(Number(row?.__rowIndex ?? 0), 0);
                    const isEditing = (
                      previewEditingCell
                      && previewEditingCell.rowIdx === rowIdx
                      && previewEditingCell.dataIndex === dataIndex
                    );
                    if (!isEditing) {
                      return (
                        <div
                          style={{ minHeight: 24, cursor: 'text' }}
                          onClick={() => setPreviewEditingCell({ rowIdx, dataIndex })}
                        >
                          {String(value ?? '')}
                        </div>
                      );
                    }
                    return (
                      <Input
                        size="small"
                        autoFocus
                        value={String(value ?? '')}
                        onChange={(e) => {
                          const next = String(e?.target?.value ?? '');
                          setPreviewData((prev) => {
                            const arr = Array.isArray(prev) ? [...prev] : [];
                            const hit = { ...(arr[rowIdx] || {}) };
                            hit[dataIndex] = next;
                            arr[rowIdx] = hit;
                            return arr;
                          });
                          if (previewType === 'xlsx') {
                            const active = String(previewActiveSheet || previewSheetNames?.[0] || '').trim();
                            if (active) {
                              const cache = previewXlsxCacheRef.current?.[active];
                              if (cache && Array.isArray(cache.data)) {
                                const nextRows = [...cache.data];
                                const hitRow = { ...(nextRows[rowIdx] || {}) };
                                hitRow[dataIndex] = next;
                                nextRows[rowIdx] = hitRow;
                                previewXlsxCacheRef.current[active] = { ...(cache || {}), data: nextRows };
                              }
                            }
                          }
                          setPreviewDirty(true);
                        }}
                        onPressEnter={() => setPreviewEditingCell(null)}
                        onBlur={() => setPreviewEditingCell(null)}
                      />
                    );
                  },
                  onCell: () => ({ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'center' } }),
                }));
              })()}
              scroll={{ y: 600 }}
              tableLayout="fixed"
            />
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 8, color: '#666' }}>该格式暂不支持直接解析预览，可下载后查看。</div>
            <Button type="link" onClick={() => { if (previewUrl) window.open(previewUrl, '_blank', 'noopener,noreferrer'); }}>
              在新窗口打开
            </Button>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default SortAndCountStepRenderer;

